/**
 * @fileoverview Dexie-to-MongoDB replication factory for local-first system, tenant, and mobile databases.
 * @module local-db/replication-factory
 *
 * Responsibilities:
 * - Define collection maps for local Dexie tables and server MongoDB collections.
 * - Coordinate pull and push cycles for local-first sync.
 * - Guard server-authoritative or provisional records from invalid client pushes.
 *
 * Dependencies:
 * - Dexie database instances supplied by app-specific local database factories.
 * - HTTP sync routes and WebSocket push/pull helpers.
 * - Local checkpoint and wake-notification storage.
 *
 * Architecture:
 * - Dexie remains the UI source of truth.
 * - MongoDB remains the server source of truth.
 * - Sync reconciles local changes while respecting collection authority boundaries.
 *
 * Notes:
 * - `_deleted` is the only supported soft-delete tombstone.
 * - Dirty-ledger cleanup is best-effort and never changes push selection or checkpoint formats.
 * - Divergence must not wipe an in-progress full-pull cursor; Occupancy
 *   (Dexie buildings) stays short until inspectableEntities finishes paging.
 * - Pulled documents failing inbound validation are quarantined (skipped and
 *   reported), never thrown, so one bad server document cannot stall a pull.
 */

// =============================================================================
// replicationFactory.ts — Dexie ↔ MongoDB bidirectional sync engine
//
// THIS FILE IS THE SYNC CONTRACT. DO NOT CHANGE THE PROTOCOL WITHOUT UPDATING
// ALL THREE SERVER-SIDE COUNTERPARTS: tenant-api, system-api, and mobile-api
// sync routes. Changing one side without the other WILL break sync silently.
//
// ─── ARCHITECTURE ────────────────────────────────────────────────────────────
//
// This is a local-first system. Dexie is always the source of truth for the UI.
// MongoDB is the source of truth for the server. Sync reconciles them.
//
//   ┌─────────────┐   WebSocket (instant)   ┌─────────────┐
//   │  MongoDB    │ ──────────────────────▶ │   Dexie     │
//   │  (server)   │ ◀────────────────────── │   (local)   │
//   └─────────────┘   Push (on write)     └─────────────┘
//
// ─── THREE OPERATIONS ────────────────────────────────────────────────────────
//
// 1. PULL  POST /api/sync/pull
//    Client sends: { collection, checkpoint: { updatedAt, _id }, departmentId }
//    Server returns: { documents[], hasMore, nextCheckpoint }
//    - On EMPTY checkpoint: full reconciliation pull. Server returns ALL docs.
//      Client replaces entire table and removes anything not in the server set.
//      This is the ONLY correct way to handle a fresh install or slug change.
//    - On EXISTING checkpoint: delta pull. Only docs newer than checkpoint.
//    - Limit: 500 docs per page. Client loops until hasMore = false.
//    - Checkpoints stored in localStorage per (collection, departmentId).
//
// 2. PUSH  POST /api/sync/push
//    Client sends: { collection, changes: [{ id, doc, isDeleted }] }
//    Server writes to MongoDB and broadcasts a change event to all WebSocket clients.
//    Push runs AFTER pull so local state is reconciled before sending outbound.
//    Only collections in PUSH_ALLOWED (server-side) can be pushed.
//    DO NOT add push for read-only reference collections (templates, lookups,
//    violation codes, roles, etc.) — those are server-authoritative.
//
// 3. WebSocket   /ws or /m/api/ws
//    WebSocket connection for real-time sync events. One persistent connection per active client.
//    Server sends: { type: 'upsert'|'delete', collection, documentId, updatedAt }
//    On UPSERT event: immediately wake the pull loop for that collection.
//    On DELETE event: immediately remove the doc from Dexie — no pull needed.
//    On WebSocket disconnect: §POLL-1 fallback (30 s) wakes all collection loops.
//    While WebSocket is connected: §POLL-2 safety-net (90 s) wakes all loops
//      as defence-in-depth for change-stream silent failures (e.g. Atlas M0).
//    Change source: MongoDB Change Stream catches ALL writes to the DB,
//    not just writes made through the API — this includes bulk imports, workers,
//    Atlas console changes, etc.
//
// ─── COLLECTION MAP ──────────────────────────────────────────────────────────
//
// TENANT_COLLECTION_MAP: Dexie table name → server collection name (tenant-web)
// MOBILE_COLLECTION_MAP: defined in mobile-pwa/src/components/DBProvider.tsx
//
// The server-side ALLOWED_COLLECTIONS map in each API's syncRoutes.ts MUST
// contain every collection listed here. If a collection is missing from the
// server map, the pull will return 400. Add it to both sides simultaneously.
//
// Collection name translation:
//   Dexie key (local)        → server key (in request body) → MongoDB collection
//   entities                 → inspectableEntities          → inspectableEntities
//   cases                    → inspectionCases              → cases
//   lookup_values (tenant)   → lookups                      → lookups
//   (see full map below)
//
// ─── CONFLICT RESOLUTION ─────────────────────────────────────────────────────
//
// A conflict occurs when pull returns a doc that differs from the local version
// AND the local version has been modified since the last push checkpoint.
// Resolution strategies (per collection, configured in conflictResolver.ts):
//   server-wins     — server state overwrites local (read-only collections)
//   client-wins     — local state is kept (cases: field-level merge attempted first)
//   field-level-merge — auto-merge non-conflicting fields, surface the rest
//   manual          — surfaced to UI via onConflict callback
//
// DO NOT add per-collection field exclusion lists or projection stripping here.
// The server controls what fields are returned in the pull response via its
// own projection. If a field creates spurious conflicts, fix the server-side
// projection in syncRoutes.ts — do not add ignore lists in this file.
//
// ─── WHAT NOT TO CHANGE ──────────────────────────────────────────────────────
//
// If a collection is server-authoritative (read-only from the client perspective),
// pass its server collection name in ReplicationConfig.pushAllowedCollections.
// Collections absent from that set will skip push entirely, preventing 400 retry
// loops for reference data (tiers, roles, lookups, etc.) that the server never
// accepts writes for. This mirrors the server-side PUSH_ALLOWED allowlist.
//
// DO NOT change the checkpoint format ({ updatedAt, _id } compound cursor).
// The server uses this exact shape to build its MongoDB query. Any change here
// requires a matching change in EVERY API's syncRoutes.ts pull handler.
//
// DO NOT change the push payload format ({ collection, changes: [...] }).
// The mobile outbound queue and tenant-web push both depend on this shape.
//
// DO NOT add polling as an alternative to WebSocket for normal operation. The 30-second
// interval is a FALLBACK for when WebSocket is disconnected — it is not a primary path.
//
// DO NOT add any mock data, localStorage stubs, or dev-mode fallbacks here.
// This file runs in production. If a backend route is missing, fix the backend.
//
// ─── SOFT-DELETE CONVENTION (DOC-1) ──────────────────────────────────────────
//
// The ONLY accepted soft-delete tombstone field is:  _deleted: true
//
// Every service that deletes a synced document MUST write:
//   { _deleted: true, deletedAt: ISO string, updatedAt: ISO string }
//
// Why this specific field:
//   - The push loop reads:  isDeleted: doc._deleted === true
//   - The server pull filter is:  { _deleted: { $ne: true } }
//
// Using any other field (isDeleted, active: false, isActive: false) causes:
//   - Push sends the doc as an upsert (not a tombstone) → deletion never syncs
//   - Server stores the record forever → reappears on every fresh install
//   - Any other device that clears its IndexedDB restores the deleted record
//
// Hard deletes are fine for non-synced data and server-only operations.
// For all client-writable synced collections, ALWAYS use _deleted: true.
//
// ─── CHECKPOINT LIFECYCLE (DOC-2) ────────────────────────────────────────────
//
// Four checkpoint types manage where each collection's sync cursor lives:
//
//   Key pattern                    Storage       Purpose
//   ─────────────────────────────  ──────────    ──────────────────────────────
//   sync_pull_<col>                localStorage  Delta pull cursor (updatedAt)
//   sync_fullpull_cursor_<col>     localStorage  Full-pull page resume cursor
//   sync_push_<col>                IndexedDB     Push checkpoint (last pushed)
//                                  (_sync_meta)
//   sync_db_nonce_<dbName>         localStorage  DB identity tie-breaker
//                                  + _sync_meta
//
// Pull checkpoints (localStorage): Worst case of loss = redundant full pull.
//   Acceptable trade-off — no data loss, just a brief performance hit.
//
// Push checkpoints (IndexedDB _sync_meta): MUST survive app kill mid-push.
//   If the device dies after a successful push write to MongoDB but before the
//   checkpoint is saved, the next push will re-send already-written docs. This
//   is safe because executeSyncPush is idempotent (upsert-by-_id). But if the
//   checkpoint lives in localStorage and localStorage is cleared, the entire
//   push history is replayed — this MUST be durable with the data itself.
//   IndexedDB uses the same storage partition as Dexie → atomic with data.
//
// _sync_meta table key space:
//   _id = 'sync_nonce'          → DB identity nonce (string value)
//   _id = 'sync_push_<col>'     → push checkpoint timestamp per collection
//
// Cleared by:
//   validateSyncNonce()    — on Dexie wipe (nonce mismatch): clears ALL types
//   forceResync()          — on manual trigger: clears ALL types
//   slug change in Provider — on department switch: clears ALL types
//   useAuthGatedDb         — on auth / session start: clears pull + fullpull only
//
// ─── TRIPLE-LOCK COLLECTION MAP RULE (DOC-3) ─────────────────────────────────
//
// Adding a new synced collection requires updating ALL THREE maps simultaneously.
// Missing any ONE of the three causes a specific silent failure:
//
//   1. COLLECTION_MAP in the app's DBProvider (or TENANT_COLLECTION_MAP here)
//      Missing → pull returns 400 (Unknown collection). Sync silently skips.
//
//   2. ALLOWED_COLLECTIONS in the API's syncRoutes.ts
//      Missing → server returns 400. Client retries forever, never pulls data.
//
//   3. MONGO_TO_CLIENT_KEY in the API's change watcher (*ChangeWatcher.ts)
//      Missing → WebSocket events for this collection are silently dropped.
//      The collection will only sync on the 30-second polling fallback.
//
// The rule: update all three in the SAME commit. Never add one without the others.
//
// ─── STEP-BY-STEP DATA FLOW (DOC-4) ──────────────────────────────────────────
//
// FULL PULL (first sync, forceResync, or checkpoint cleared):
//   1. Auth headers constructed; POST /api/sync/pull { collection, full: true }
//   2. Server returns { documents[], hasMore, checkpoint, allIds? }
//   3. bulkPut() all documents into Dexie table
//   4. Advance FULLPULL_CURSOR_KEY (compound cursor) after each page
//   5. On final page (hasMore=false): write PULL_KEY; set PUSH_KEY to now
//      (setting push checkpoint to now means first push skips already-synced docs)
//   6. Reconcile: for collections with allIds[], delete any Dexie docs whose
//      _id is NOT in allIds (removes server-deleted docs missed by delta pull)
//
// DELTA PULL (normal operation after full pull):
//   1. Read PULL_KEY from localStorage as 'since' timestamp
//   2. POST /api/sync/pull { collection, since, sinceId }
//   3. Server returns only docs where updatedAt > since (compound cursor stable sort)
//   4. For each doc: if _deleted=true → delete from Dexie; else → upsert
//   5. Advance PULL_KEY to the highest updatedAt in the response
//
// PUSH (triggered by wakeLocalWrite() after any local Dexie write):
//   1. Read PUSH_KEY from IndexedDB _sync_meta as last push timestamp
//   2. Query Dexie: all docs in table where updatedAt > lastPushTimestamp
//   3. Deduplicate by _id (keep latest by updatedAt)
//   4. Map each doc: { id: doc._id, doc, isDeleted: doc._deleted === true }
//      NOTE: ONLY doc._deleted === true marks a deletion. isDeleted/isActive are
//      UI fields and are silently ignored by the push engine for tombstoning.
//   5. POST /api/sync/push { collection, changes }
//   6. Server applies upserts/deletes, returns { conflicts }
//   7. Handle conflicts per resolution strategy (server-wins, auto-merge, manual)
//   8. Write new PUSH_KEY to _sync_meta (durable, atomic with IndexedDB data)
//
// WebSocket (persistent connection, primary real-time channel):
//   1. WebSocket connection to /ws or /m/api/ws — long-lived WebSocket connection
//   2. On 'connected': log handshake, reset stale-connection timer
//   3. On 'upsert' event: immediately wake pull loop for that collection
//      (pulls from last checkpoint, not full pull — fast delta only)
//   4. On 'delete' event: immediately remove doc from Dexie — no pull needed
//   5. On 'ping': reset 90-second stale-connection timer
//   6. On disconnect: §POLL-1 starts (30 s interval); reconnect with exponential backoff
//   7. On reconnect: §POLL-1 stops, §POLL-2 starts (90 s interval); runDivergenceCheckAndWake
//   8. §POLL-2 safety-net fires every 90 s even while connected (change-stream guard)
//   Change source: MongoDB Change Stream catches ALL writes to the DB —
//   API, mobile-api, workers, bulk imports, Atlas console, migrations.
// =============================================================================

import { detectFieldDiffs, applyAutoMergeRules, resolveClientNewerConflictDoc, isoAfterBoth, canAutoMerge, buildMergedDocument, requiresManualConflictResolution } from './conflictResolver';
import { stampReceiptCoverageFlag } from '../databases/tenantDb';
import {
  setConnected,
  setSyncing,
  setSyncSuccess,
  setPulling,
  setSyncError,
  updateLastPing,
  setCollectionPushState,
  clearCollectionPushState,
  getSyncStatus,
} from '../stores/syncStatusStore';
import { startWebSocketSyncClient, pullViaWebSocket, pushViaWebSocket, getActiveWebSocketSyncClient } from './websocketSyncClient';
import { timedFetch } from '../services/connectionQuality';
import { syncLog } from '../stores/syncActivityLog';
import { debugLog, debugWarn } from '../utils/debugLog';
import {
  TENANT_COLLECTION_MAP,
  TENANT_SYSTEM_DERIVED_COLLECTION_MAP,
  SYSTEM_COLLECTION_MAP,
} from '@firesquirrel/sync-config';
import { validateComplexInspectionSyncDocument } from '@firesquirrel/import-core/complex-inspections';
import {
  clearAcknowledgedDirtyDocs,
  reconcileDirtyLedger,
} from '../services/localDirtyLedger';

// ---------------------------------------------------------------------------
// PushSyncError — typed error so the retry loop can identify push failures
// without resorting to substring matching on the message. Both the HTTP and
// WebSocket push paths wrap their underlying errors in this class so any
// transport-level error surfaces consistently in the failed-push UI.
// ---------------------------------------------------------------------------
class PushSyncError extends Error {
  /** Tag readable across module boundaries even if `instanceof` fails (e.g. duplicated bundles). */
  readonly isPushSyncError = true;
  readonly collection: string;
  constructor(collection: string, message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'PushSyncError';
    this.collection = collection;
  }
}

function isPushSyncError(err: unknown): err is PushSyncError {
  return !!err && typeof err === 'object' && (err as any).isPushSyncError === true;
}

// ---------------------------------------------------------------------------
// LocalWriteError — surfaced (not thrown) when one or more documents could
// not be persisted to Dexie during a full pull. This replaces the silent
// `catch { /* skip this doc */ }` in the per-doc fallback so dropped
// documents are no longer invisible.
//
// Policy (chosen by product owner):
//   "Advance + surface error only" — the full-pull cursor still advances
//   so a single bad doc cannot block the entire collection forever, but
//   the failure is loudly logged, recorded on the error$ observable, and
//   reflected in setSyncError so the sync widget shows a degraded state.
//
// This error is intentionally NOT thrown out of syncCollection so the
// outer retry loop does not treat it as a transport failure — that would
// incorrectly inflate the per-collection failed-push counter and trigger
// exponential backoff for a Dexie-write issue that has nothing to do
// with the network. See @errorHandling on syncCollection.
//
// Carries the collection name and a bounded sample of failing _ids so an
// operator can grep server logs for the responsible documents.
// ---------------------------------------------------------------------------
/**
 * @contract LocalWriteError
 *
 * Purpose:
 * - Capture per-document local persistence failures during a pull — Dexie
 *   write rejects on full pulls and inbound-validation quarantines on full
 *   and delta pulls — so they surface to the sync status UI instead of
 *   being silently skipped.
 *
 * Fields:
 * - collection: Dexie collection name where the writes failed.
 * - failedCount: total number of documents that could not be persisted.
 * - sampleFailedIds: up to LOCAL_WRITE_ERROR_SAMPLE_SIZE failing `_id`s for
 *   diagnostic logging (never the whole list — could be thousands).
 * - sampleReason: representative error message from the first failure.
 *
 * Rules:
 * - This class is emitted to `error$` and `setSyncError`, never thrown out
 *   of syncCollection. Throwing it would conflate Dexie-write failures
 *   with transport failures in the retry loop.
 *
 * Sync:
 * - Surfaced from the local-first pull path; never sent to the server.
 */
const LOCAL_WRITE_ERROR_SAMPLE_SIZE = 10;
class LocalWriteError extends Error {
  /** Tag readable across module boundaries even if `instanceof` fails (e.g. duplicated bundles). */
  readonly isLocalWriteError = true;
  readonly collection: string;
  readonly failedCount: number;
  readonly sampleFailedIds: string[];
  readonly sampleReason: string;
  constructor(
    collection: string,
    failedCount: number,
    sampleFailedIds: string[],
    sampleReason: string,
  ) {
    super(
      `Local write failed for ${failedCount} doc(s) in ${collection}: ` +
      `[${sampleFailedIds.slice(0, LOCAL_WRITE_ERROR_SAMPLE_SIZE).join(', ')}` +
      `${sampleFailedIds.length > LOCAL_WRITE_ERROR_SAMPLE_SIZE ? ', …' : ''}] ` +
      `(${sampleReason})`,
    );
    this.name = 'LocalWriteError';
    this.collection = collection;
    this.failedCount = failedCount;
    this.sampleFailedIds = sampleFailedIds.slice(0, LOCAL_WRITE_ERROR_SAMPLE_SIZE);
    this.sampleReason = sampleReason;
  }
}

export function isLocalWriteError(err: unknown): err is LocalWriteError {
  return !!err && typeof err === 'object' && (err as any).isLocalWriteError === true;
}

/**
 * @errorHandling reportLocalWriteFailures
 * Strategy: Surface per-document pull failures (Dexie write rejects and
 *   inbound-validation quarantines) as one LocalWriteError per call; never
 *   throws, so the caller still advances its cursor/checkpoint.
 * User Impact: The sync widget shows the failure via setSyncError and the
 *   Activity Log; the affected documents are absent or stale locally.
 * Logging: console.error + syncLog.error with the collection, a bounded
 *   sample of `_id`s and the first reason — never document payloads.
 */
function reportLocalWriteFailures(
  dexieCollection: string,
  failures: Array<{ id: string; reason: string }>,
): void {
  if (failures.length === 0) return;
  const failedIds = failures.map((f) => f.id);
  const firstReason = failures[0].reason || 'unknown Dexie error';
  const localErr = new LocalWriteError(dexieCollection, failures.length, failedIds, firstReason);
  // Loud, durable log — the sync widget's lastError is fleeting but
  // console.error survives across UI states for postmortem grepping.
  console.error(
    `[sync] LOCAL WRITE FAILURE for ${dexieCollection}: ` +
    `${failures.length} doc(s) could not be saved to Dexie. ` +
    `Sample _ids: [${failedIds.slice(0, LOCAL_WRITE_ERROR_SAMPLE_SIZE).join(', ')}` +
    `${failedIds.length > LOCAL_WRITE_ERROR_SAMPLE_SIZE ? ', …' : ''}]. ` +
    `First reason: ${firstReason}`,
  );
  setSyncError(localErr.message, 'unknown');
  syncLog.error(`${failures.length} doc(s) failed to save locally for ${dexieCollection}`, { collection: dexieCollection, detail: firstReason });
}

// ---------------------------------------------------------------------------
// PullSemaphore — fair-share HTTP pull concurrency limit.
//
// PROBLEM
// -------
// On first sync after a cache clear, every collection's loop starts within
// a few seconds of every other collection (200 ms stagger × ~17 collections
// on mobile, ~22 on tenant-web). Each loop's full pull then races for the
// browser's ~6 concurrent HTTP-connections-per-origin limit at the same
// time. The browser does serve requests in FIFO order at the connection
// level, but a 100k-row `violationIndex` pull can hold a slot for several
// minutes — small fast collections (lookups, accounts, roles) sit behind
// it because they happened to start a few hundred ms later.
//
// FIX
// ---
// A single in-flight-pull semaphore per `setupReplication` instance gives
// us *page-level* fairness rather than *connection-level* fairness. After
// each page of a large collection completes, it releases the slot,
// allowing a different collection's next pull to interleave before the
// large collection grabs the slot back for its next page.
//
// SCOPE
// -----
// - Gates the HTTP `fetch` calls in `syncCollection` (both full pull and
//   delta pull HTTP fallback). All HTTP traffic to `/sync/pull` goes
//   through here.
// - Does NOT gate `pullViaWebSocket` — that multiplexes over a single
//   shared WS connection and is already serialized by the WS client.
// - Does NOT gate push (`/sync/push`). Push is much less common and
//   already throttled by user activity.
//
// DEFAULTS
// --------
// 4 in-flight pulls is the default. Below the browser's 6-slot HTTP/1.1
// limit so non-sync traffic (style tiles, photos, etc.) still has room.
// Configurable via `ReplicationConfig.maxConcurrentPulls` (range 1–8).
//
// EARLIER FIX, RELATED
// --------------------
// The system-api side has its own server-side semaphore
// (`SYSTEM_API_MAX_CONCURRENT_PULLS`, default 3) for Atlas connection
// fairness. This client semaphore is its complement: client decides
// which N of its M collections fetch *first*, the server decides which
// N of all clients' requests it serves at once.
// ---------------------------------------------------------------------------

/**
 * @contract PullSemaphore
 *
 * Purpose:
 * - Bound the number of in-flight HTTP `/sync/pull` requests per
 *   replication instance so a single large collection cannot monopolise
 *   the browser's HTTP-connection budget.
 *
 * Fields:
 * - maxConcurrent: cap on simultaneous acquired slots. Range 1–8.
 *
 * Rules:
 * - `acquire()` resolves immediately when a slot is free, otherwise
 *   queues FIFO behind earlier waiters.
 * - `release()` MUST be called for every successful `acquire()` (the
 *   `withSlot()` helper guarantees this with try/finally).
 * - Failures inside `withSlot()` still release the slot.
 *
 * Sync:
 * - Pure in-process scheduler. Not persisted, not synced.
 */
const DEFAULT_MAX_CONCURRENT_PULLS = 4;
const MIN_MAX_CONCURRENT_PULLS = 1;
const MAX_MAX_CONCURRENT_PULLS = 8;

export class PullSemaphore {
  readonly maxConcurrent: number;
  private activeCount = 0;
  private readonly waiters: Array<() => void> = [];

  constructor(maxConcurrent: number = DEFAULT_MAX_CONCURRENT_PULLS) {
    if (!Number.isFinite(maxConcurrent)) {
      this.maxConcurrent = DEFAULT_MAX_CONCURRENT_PULLS;
    } else {
      this.maxConcurrent = Math.max(
        MIN_MAX_CONCURRENT_PULLS,
        Math.min(MAX_MAX_CONCURRENT_PULLS, Math.floor(maxConcurrent)),
      );
    }
  }

  /**
   * @process acquire
   *
   * Steps:
   * 1. When fewer than `maxConcurrent` slots are active, take one
   *    immediately.
   * 2. Otherwise queue FIFO and resolve once another caller releases.
   *
   * Inputs:
   * - None.
   *
   * Outputs:
   * - A Promise that resolves when the caller holds a slot.
   *
   * Failure Handling:
   * - Never rejects on its own. Errors caught by `withSlot()` still
   *   release the held slot.
   *
   * Idempotency:
   * - NOT REQUIRED. Each call is a distinct slot acquisition.
   */
  acquire(): Promise<void> {
    if (this.activeCount < this.maxConcurrent) {
      this.activeCount++;
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => this.waiters.push(resolve));
  }

  release(): void {
    const next = this.waiters.shift();
    if (next) {
      // Hand the slot directly to the next waiter without decrementing
      // — keeps active-count accurate while never letting it drop below
      // the high-water mark while waiters exist.
      next();
    } else {
      this.activeCount = Math.max(0, this.activeCount - 1);
    }
  }

  /**
   * Convenience wrapper: acquire, run the callback, release in a
   * try/finally so a thrown error inside the callback never leaks a
   * slot. This is the only API `syncCollection` should use.
   */
  async withSlot<T>(fn: () => Promise<T>): Promise<T> {
    await this.acquire();
    try {
      return await fn();
    } finally {
      this.release();
    }
  }

  // Test-only: introspection of internal state for assertions. Hidden
  // behind underscore-prefixed names per the project's pattern for test
  // helpers (cf. `_resetParcelBboxIndexForTests`).
  _activeCountForTests(): number { return this.activeCount; }
  _waiterCountForTests(): number { return this.waiters.length; }
}

/**
 * Describe an HTTP sync failure without logging tokens, SAS, or full payloads.
 * Hop headers (`x-azure-ref`, `x-ms-request-id`, `x-envoy-upstream-service-time`)
 * distinguish Front Door / ACA 503 from a tenant-api 500 body.
 *
 * @errorHandling
 * Strategy: Clone the response, take a 200-char body snippet, never log Authorization.
 * User Impact: Console/telemetry can name the hop that returned 503/504.
 * Logging: REQUIRED — callers attach the Error.message to `[sync]` warnings.
 */
async function httpSyncFailure(res: Response, prefix: string): Promise<Error> {
  let bodySnippet = '';
  try {
    bodySnippet = (await res.clone().text()).replace(/\s+/g, ' ').slice(0, 200);
  } catch { /* ignore */ }
  const getHeader = (name: string): string =>
    typeof res.headers?.get === 'function' ? res.headers.get(name) ?? '' : '';
  const azureRef = getHeader('x-azure-ref') || getHeader('x-ms-request-id');
  const upstreamMs = getHeader('x-envoy-upstream-service-time');
  const parts = [`${prefix}HTTP ${res.status}`];
  if (azureRef) parts.push(`azureRef=${azureRef.slice(0, 80)}`);
  if (upstreamMs) parts.push(`upstreamMs=${upstreamMs}`);
  if (bodySnippet) parts.push(`body=${bodySnippet}`);
  return new Error(parts.join(' '));
}

// ---------------------------------------------------------------------------
// Types
//
// TODO (audit Priority 2 — sync architecture audit, 2026-05-12):
// - Add @contract JSDoc blocks for both ReplicationConfig and SyncConflict
//   below. The fields are documented inline but the project code-documentation
//   rule requires explicit @contract blocks naming Purpose, Fields, Rules, and
//   Sync behaviour so the public API surface is discoverable from JSDoc alone.
// - Add @errorHandling and @logging JSDoc blocks on syncCollection() (around
//   line 1012) and setupReplication() (around line 2016). Both functions have
//   comprehensive error handling and logging — what is missing is the
//   structured tag documenting the strategy, user impact, and event taxonomy.
// ---------------------------------------------------------------------------

export interface ReplicationConfig {
  apiBaseUrl: string;
  departmentId: string;
  getAuthHeaders: () => Promise<Record<string, string>>;
  onConflict?: (conflict: SyncConflict) => void;
  /** Called when the server reports this department as not found (tenant-not-found). */
  onTenantNotFound?: (departmentId: string) => void;
  /** Dexie table name → server collection name sent in sync requests */
  collectionMap: Record<string, string>;
  /**
   * Server collection names that the client is allowed to push changes to.
   * Collections absent from this set skip the push step entirely, preventing
   * 400 retry loops for server-authoritative reference data.
   * If omitted, ALL collections attempt push (backwards-compatible default).
   */
  pushAllowedCollections?: ReadonlySet<string>;
  /**
   * Optional: Inspection account IDs to filter sync data (mobile-api only).
   * When provided, only cases/entities/contacts for these accounts are synced.
   * Changes to this array trigger checkpoint clearing and full re-sync.
   */
  inspectionAccountIds?: string[];
  syncRoutePrefix?: string;
  checkpointPrefix?: string;
    /**
   * Enable WebSocket bidirectional sync (replaces SSE + HTTP pull/push).
   * When true, uses WebSocket for all sync operations.
   * When false, uses legacy SSE + HTTP fetch (default for backward compatibility).
   */
  useWebSocket?: boolean;
  /**
   * Called when sync transport rejects the bearer token. The host application
   * must invalidate the session and present recovery UI.
   */
  onAuthFailure?: (reason: string) => void;
  /**
   * Dexie collection keys that should be checked against `/sync/status` when
   * WebSocket sync connects or reconnects. Intended for server-authoritative
   * collections where missed hard-delete events cannot be recovered by delta
   * pull alone.
   */
  divergenceCheckCollections?: string[];
  /**
   * Cap on concurrent in-flight HTTP `/sync/pull` requests per replication
   * instance. Provides page-level fairness so a single large collection
   * (e.g. 100k-row violationIndex) cannot monopolise the browser's HTTP
   * slot budget across the ~17–22 parallel collection loops.
   *
   * Defaults to 4. Clamps to the 1–8 range. Does not affect WebSocket
   * pulls (those are serialized by the shared WS client) or pushes.
   * See PullSemaphore @contract for the full rationale.
   */
  maxConcurrentPulls?: number;
  /**
   * Forward to WebSocketClient — when true the underlying WS client listens
   * for `visibilitychange` events and proactively closes the socket when the
   * page is hidden, then forces a reconnect when it becomes visible again.
   * Required for iOS/iPadOS PWAs where WKWebView is frozen in background.
   * Defaults to false; only mobile-pwa should enable this.
   */
  enableVisibilityReconnect?: boolean;
  /**
   * Phased initial sync — Dexie collection keys whose INITIAL full pull is
   * deferred until the light "readiness" set has completed a clean pull over
   * the live socket. Lets the heavy, geometry-fat collections (e.g.
   * `entities`, `violation_index`) hold off slamming a freshly-(re)connected,
   * possibly-cold or just-scaled-out replica until it has proven it is serving
   * sync. The gate re-arms on every disconnect so a reconnect (or a new
   * replica grabbed during scale-out) gets the same handshake-first treatment;
   * an absolute timeout (see `readinessGateTimeoutMs`) guarantees it can never
   * deadlock initial sync. Empty/omitted ⇒ phasing disabled (no behavior
   * change). Only takes effect with `useWebSocket: true`. Mobile-only today.
   */
  deferredFullSyncCollections?: string[];
  /**
   * Dexie collection keys that are account-scoped (they pull nothing until the
   * user selects inspection accounts, e.g. `cases`). Excluded from the
   * readiness "light set" so the gate never waits on a collection that
   * legitimately cannot complete before account selection. Only meaningful
   * alongside `deferredFullSyncCollections`.
   */
  accountScopedCollections?: string[];
  /**
   * Absolute fallback (ms) after which deferred collections are released even
   * if the light set never reports completion — the deadlock backstop for the
   * readiness gate. Defaults to 45000. Only used when
   * `deferredFullSyncCollections` is non-empty.
   */
  readinessGateTimeoutMs?: number;
  /**
   * Stuck-sync watchdog threshold (ms). When sync work is genuinely
   * outstanding (a resumable full-pull cursor is still saved) but no page
   * progress is observed for this long while online, the watchdog forces a
   * clean WebSocket reconnect to break a silent stall — the direct self-heal
   * for "looked like it was syncing but never finished" field reports.
   * 0/omitted ⇒ disabled. Only takes effect with `useWebSocket: true`.
   * Mobile-only today.
   */
  stuckSyncTimeoutMs?: number;
  /**
   * Dexie collection keys that push local writes but never pull the server
   * copy. Used so mobile-pwa can push `audit_logs` without downloading the
   * department trail, while tenant-web (omitting this set) two-way syncs
   * the same table so inspector entries appear on Audit Log tabs.
   */
  writeOnlyCollections?: ReadonlySet<string>;
}

export interface CheckpointType {
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Conflict types — surfaced to the UI for manual resolution
// Phase 2: Enhanced with field-level diffs and auto-merge support
// ---------------------------------------------------------------------------
// NOTE: this is a structural duplicate of conflictResolver.ts's `FieldDiff`
// (pre-existing — not introduced here). Keep `autoMergeRule`'s literal union
// in sync with conflictResolver.ts's `FieldDiff.autoMergeRule`; a mismatch
// breaks the assignments below where a conflictResolver.ts `FieldDiff[]` is
// passed into this module's `FieldDiff[]`-typed parameters.
export interface FieldDiff {
  field: string;
  localValue: unknown;
  serverValue: unknown;
  autoMergeRule?: 'union' | 'max' | 'latest' | 'server-wins' | 'client-wins' | 'nested-merge';
  merged?: boolean;
  mergedValue?: unknown;
}

export type ConflictResolution = 
  | 'keep-local' 
  | 'accept-server' 
  | 'field-level-merge' 
  | 'auto-merged';

export interface SyncConflict {
  id: string;
  collection: string;
  documentId: string;
  localState: Record<string, unknown>;
  serverState: Record<string, unknown>;
  detectedAt: string;
  resolved: boolean;
  resolution?: ConflictResolution;
  resolvedBy?: string; // userId
  resolvedAt?: string;
  autoMerged?: boolean;
  fieldDiffs: FieldDiff[];
}

// ---------------------------------------------------------------------------
// Tenant-web collection maps — re-exported from @firesquirrel/sync-config.
//
// The canonical definitions live in api/packages/sync-config/src/tenant-collections.ts.
// This re-export pattern keeps sync-config the single source of truth across
// every app and every API. Consumers continue to import { TENANT_COLLECTION_MAP }
// from '@firesquirrel/local-db' unchanged.
//
// THREE-FILE SYNC RULE (still applies):
//   1. TENANT_COLLECTION_MAP in @firesquirrel/sync-config (the canonical)
//   2. ALLOWED_COLLECTIONS in api/tenant-api/src/routes/syncRoutes.contract.ts
//   3. MONGO_TO_CLIENT_KEY in api/tenant-api/src/sync/tenantChangeWatcher.ts
//   Drift in any of the three breaks sync. Parity tests in tenant-api guard
//   the lockstep.
//
// Intentional local-only (NOT synced — by design, not by accident):
//   - email_configurations   — no server sync route yet
//   - (widget_library was removed in Section 2.4; no longer a table)
//   - scheduled_reports      — no server sync route yet (distinct from
//                              `report_schedules` which IS synced)
//   - report_executions      — server-written; no push needed; pulled via
//                              `generated_documents` instead.
// ---------------------------------------------------------------------------
export {
  TENANT_COLLECTION_MAP,
  TENANT_PUSH_ALLOWED_COLLECTIONS,
  TENANT_SYSTEM_DERIVED_COLLECTION_MAP,
  TENANT_SYSTEM_DERIVED_PUSH_ALLOWED_COLLECTIONS,
} from '@firesquirrel/sync-config';

import { TENANT_COLLECTION_DISPLAY_NAMES as SHARED_TENANT_COLLECTION_DISPLAY_NAMES } from '@firesquirrel/sync-config';

// ---------------------------------------------------------------------------
// Human-readable display names for the sync UI
// ---------------------------------------------------------------------------
export const COLLECTION_DISPLAY_NAMES: Record<string, string> = {
  ...SHARED_TENANT_COLLECTION_DISPLAY_NAMES,
  // Local label overrides — tenant sync UI wording that intentionally differs
  // from the shared sync-config defaults.
  generated_documents: 'Generated Documents',
  inspection_sms_deliveries: 'Inspection SMS Deliveries',
  system_inspection_templates: 'System Inspection Templates',
};

// ---------------------------------------------------------------------------
// Per-collection override for the timestamp field used in push checkpoint queries.
// Most collections use 'updatedAt'; audit_logs are append-only and use 'createdAt'.
// ---------------------------------------------------------------------------
const PUSH_TIMESTAMP_FIELD: Record<string, string> = {
  audit_logs: 'createdAt',
};

/**
 * True when this Dexie collection is configured as write-only (push, never pull).
 * Tenant-web omits `writeOnlyCollections` so `audit_logs` two-way syncs.
 * Mobile-pwa sets `{ 'audit_logs' }` so field devices do not download the
 * department audit trail.
 */
function isWriteOnlyCollection(config: ReplicationConfig, dexieCollection: string): boolean {
  return config.writeOnlyCollections?.has(dexieCollection) === true;
}

/** Test helper — write-only is config-driven, not hardcoded to audit_logs. */
export function _isWriteOnlyCollectionForTests(config: Pick<ReplicationConfig, 'writeOnlyCollections'>, dexieCollection: string): boolean {
  return isWriteOnlyCollection(config as ReplicationConfig, dexieCollection);
}

// ---------------------------------------------------------------------------
// Client-only fields that must NEVER be pushed to the server. These are
// local-first markers (optimistic-write tracking, local cache timestamps)
// that have no MongoDB counterpart. Pushing them pollutes server documents
// and breaks server-side guards that rely on their absence (e.g. the
// optimistic-row tombstone guard would protect a row forever if the server
// retained __optimistic: true after the worker's completion write).
// ---------------------------------------------------------------------------
const CLIENT_ONLY_PUSH_FIELDS: ReadonlySet<string> = new Set([
  '__optimistic',
  'fetchedAt',
]);

/**
 * Return a shallow copy of `doc` with all client-only fields removed.
 * Used exclusively in the push payload builder so the server never sees
 * local-only markers. The original Dexie doc is untouched.
 */
function stripClientOnlyFields(doc: any): any {
  if (!doc || typeof doc !== 'object') return doc;
  const stripped: Record<string, any> = {};
  for (const key in doc) {
    if (CLIENT_ONLY_PUSH_FIELDS.has(key)) continue;
    stripped[key] = doc[key];
  }
  return stripped;
}

// ---------------------------------------------------------------------------
// Offline detection helpers
// isOnline()      — returns false only when the browser has definitively signalled
//                   no connectivity. navigator.onLine can be truthy even on a
//                   captive-portal or extremely lossy connection, but treating
//                   those as online is correct — the sync loop's exponential
//                   backoff handles partial/lossy networks gracefully.
// waitForOnline() — resolves when the browser fires the 'online' event.
//                   Falls back to a 30-second timeout so the loop is not
//                   stuck indefinitely if the event never fires (e.g. SSR,
//                   non-browser environments, or very old WebViews).
// ---------------------------------------------------------------------------
function _rfIsOnline(): boolean {
  if (typeof navigator === 'undefined') return true; // non-browser (SSR/test) — assume online
  return navigator.onLine !== false; // undefined also treated as online
}

function _rfWaitForOnline(): Promise<void> {
  return new Promise<void>((resolve) => {
    if (_rfIsOnline()) { resolve(); return; }
    const onOnline = () => {
      if (typeof window !== 'undefined') window.removeEventListener('online', onOnline);
      resolve();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onOnline, { once: true });
      // Safety-net timeout: resolve after 30s even if the event never fires
      setTimeout(() => { window.removeEventListener('online', onOnline); resolve(); }, 30_000);
    } else {
      // Non-browser fallback — resolve after 5s
      setTimeout(resolve, 5_000);
    }
  });
}

// ---------------------------------------------------------------------------
// System-web collection map — re-exported from @firesquirrel/sync-config.
//
// Canonical definition: api/packages/sync-config/src/system-collections.ts.
// Keys match systemDb.ts table names AND system-api syncRoutes.ts keys.
//
// Intentional local-only (NOT synced — by design):
//   - fire_boundaries — boundary geometry is large (2-8KB/doc); fetched
//                       on-demand instead of replicated.
// ---------------------------------------------------------------------------
export { SYSTEM_COLLECTION_MAP, SYSTEM_PUSH_ALLOWED_COLLECTIONS, MOBILE_COLLECTION_MAP, MOBILE_PUSH_ALLOWED_COLLECTIONS, MOBILE_WRITE_ONLY_COLLECTIONS } from '@firesquirrel/sync-config';

// ---------------------------------------------------------------------------
// ReplicationStateStub — per-collection observable sync state
// ---------------------------------------------------------------------------
export interface ReplicationStateStub {
  error$: { subscribe: (fn: (e: any) => void) => (() => void) };
  active$: { subscribe: (fn: (v: boolean) => void) => (() => void) };
  canceled$: { subscribe: (fn: (v: boolean) => void) => (() => void) };
  cancel: () => Promise<void>;
  /**
   * Resolves once this collection's first pull+push cycle has settled
   * (success or error) — i.e. once any locally-dirty docs it has have been
   * discovered AND reported to syncStatusStore (or found to have none).
   * Used by `waitForInitialSyncAttempt` to replace fixed-delay heuristics
   * with an actual per-collection completion signal. Optional because
   * hand-built stubs in tests do not need to provide it.
   */
  firstCycleSettled?: Promise<void>;
  /**
   * Returns true if a graceful WebSocket disconnect terminated this
   * collection's loop before it ever completed a single real (pull+push)
   * attempt. When true, this collection's push-state entry has been
   * cleared (nothing to report) even though it was never actually checked
   * for locally-dirty docs — callers needing an honest "confirmed clean"
   * signal (not just "no entry left in the store") should treat this as
   * unconfirmed rather than drained. See `hasUnconfirmedFirstCycleDisconnect`.
   * Optional because hand-built stubs in tests do not need to provide it;
   * treated as `false` (no known risk) when absent.
   */
  hadUnconfirmedFirstCycleDisconnect?: () => boolean;
  /**
   * Returns a NEW promise each call, resolving once this collection's
   * current-or-next loop iteration settles from the moment this is called
   * (not just the first ever — see `firstCycleSettled` for that). Used by
   * `waitForNextSyncAttempt` to wait for a wake-triggered re-check (e.g.
   * `triggerFullSync()`) to actually run before trusting a drain check —
   * without this, a synchronous `isDrainedFor()` check made immediately
   * after waking a sleeping loop can observe stale (pre-wake) state and
   * miss a write that landed just before the wake. Optional because
   * hand-built stubs in tests do not need to provide it.
   */
  registerNextCycleWaiter?: () => Promise<void>;
}

export type NamedReplicationStates = Map<string, ReplicationStateStub>;

interface ReplicationTeardown {
  stopSse?: () => void;
  removeOnlineListener?: () => void;
  /** Stop the §POLL-1/§POLL-2 safety-net polling timers. Called by cancelAllReplication. */
  stopPolling?: () => void;
  /** Stop the stuck-sync watchdog interval. Called by cancelAllReplication. */
  stopWatchdog?: () => void;
  /** Force the readiness gate open so any parked deferred loop unblocks on teardown. */
  releaseReadinessGate?: () => void;
  /** Release the multi-tab leader lock so another tab can acquire it. */
  releaseLeaderLock?: () => void;
  wakeRegistryKeys?: string[];
  nestedStates?: NamedReplicationStates[];
}

const replicationTeardowns = new WeakMap<NamedReplicationStates, ReplicationTeardown>();

// ---------------------------------------------------------------------------
// Checkpoint helpers
// PULL checkpoints: localStorage (acceptable — worst case is redundant full pull)
// PUSH checkpoints: IndexedDB _sync_meta (critical — must survive app kill mid-push)
// ---------------------------------------------------------------------------
const checkpointNamespacePrefix = (prefix?: string): string => (prefix ? `${prefix}_` : '');
const PULL_KEY = (col: string, prefix?: string): string => `sync_${checkpointNamespacePrefix(prefix)}pull_${col}`;
const PULL_ID_KEY = (col: string, prefix?: string): string => `sync_${checkpointNamespacePrefix(prefix)}pull_id_${col}`;
const PULL_ID_TYPE_KEY = (col: string, prefix?: string): string => `sync_${checkpointNamespacePrefix(prefix)}pull_id_type_${col}`;
const PUSH_KEY = (col: string, prefix?: string): string => `sync_${checkpointNamespacePrefix(prefix)}push_${col}`;
const FULLPULL_CURSOR_KEY = (col: string, prefix?: string): string => `sync_${checkpointNamespacePrefix(prefix)}fullpull_cursor_${col}`;
const PUSH_ACK_KEY = (col: string, docId: string, prefix?: string): string => `sync_${checkpointNamespacePrefix(prefix)}push_ack_${col}_${encodeURIComponent(docId)}`;
const PUSH_UNACKED_KEY = (col: string, docId: string, prefix?: string): string => `sync_${checkpointNamespacePrefix(prefix)}push_unacked_${col}_${encodeURIComponent(docId)}`;
const KNOWN_SERVER_IDS_KEY = (col: string, prefix?: string): string => `sync_${checkpointNamespacePrefix(prefix)}server_ids_${col}`;
const KNOWN_SERVER_VERSIONS_KEY = (col: string, prefix?: string): string => `sync_${checkpointNamespacePrefix(prefix)}server_versions_${col}`;
const LAST_SYNCED_WATERMARK_KEY = (col: string, prefix?: string): string => `sync_${checkpointNamespacePrefix(prefix)}last_synced_watermark_${col}`;

function parseCursorIdType(
  idType: unknown,
  cursorId?: string,
): 'string' | 'objectId' | undefined {
  if (idType === 'objectId' || idType === 'string') return idType;
  if (typeof cursorId === 'string') {
    if (cursorId.startsWith('oid:')) return 'objectId';
    if (cursorId.startsWith('str:')) return 'string';
  }
  return undefined;
}

function getCheckpoint(key: string): string {
  try { return localStorage.getItem(key) || ''; } catch { return ''; }
}
function setCheckpoint(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}

function hasAnySyncCheckpoints(collectionMap: Record<string, string>, checkpointPrefix?: string): boolean {
  try {
    return Object.keys(collectionMap).some((col) => (
      !!localStorage.getItem(PULL_KEY(col, checkpointPrefix)) ||
      !!localStorage.getItem(PULL_ID_KEY(col, checkpointPrefix)) ||
      !!localStorage.getItem(PULL_ID_TYPE_KEY(col, checkpointPrefix)) ||
      !!localStorage.getItem(FULLPULL_CURSOR_KEY(col, checkpointPrefix))
    ));
  } catch {
    return false;
  }
}

// Push checkpoint helpers — stored in IndexedDB for atomic durability with the data
let _dexieInstance: any = null;
const _pendingMetaWrites = new Set<Promise<unknown>>();

function trackMetaWrite(p: Promise<unknown> | undefined): void {
  if (!p || typeof p.finally !== 'function') return;
  _pendingMetaWrites.add(p);
  p.finally(() => _pendingMetaWrites.delete(p));
}

async function flushPendingMetaWrites(): Promise<void> {
  while (_pendingMetaWrites.size > 0) {
    await Promise.allSettled(Array.from(_pendingMetaWrites));
  }
}

// ---------------------------------------------------------------------------
// Durable pull checkpoint helpers — stored in IndexedDB _sync_meta (same
// durability class as the data itself) with one-time migration from legacy
// localStorage checkpoints. localStorage pull checkpoints were acceptable
// when their loss only caused a redundant full pull, but quota pressure on
// mobile Safari could evict them silently, causing endless full-pull loops.
// _sync_meta survives as long as IndexedDB survives (same storage partition
// as Dexie), so the checkpoint is atomic with the data.
//
// Read path: _sync_meta first, then localStorage fallback (legacy / pre-
// migration). Write path: _sync_meta only; localStorage entry is removed
// after a successful durable write so the legacy value does not recur.
// ---------------------------------------------------------------------------

/**
 * Read a pull checkpoint value by its full _sync_meta key.
 * Checks _sync_meta first (durable), then falls back to localStorage (legacy).
 * Returns the checkpoint string or '' when absent.
 *
 * Exported for test assertions (tests verify the checkpoint persisted).
 */
export function readPullCheckpoint(key: string): string {
  // Durable store first — _sync_meta may have the value even after
  // localStorage was cleared by quota pressure.
  if (_dexieInstance) {
    try {
      const metaTable = _dexieInstance.table('_sync_meta');
      if (metaTable) {
        // Synchronous-style read is not possible with Dexie, but tests call
        // this AFTER settle() so the fire-and-forget put has resolved. We do
        // a best-effort sync read from the mock's in-memory store; real Dexie
        // callers use the async getPullCheckpointDurable below.
        const store = (metaTable as any)._store;
        if (store instanceof Map) {
          const record = store.get(key);
          if (record?.value) return String(record.value);
        }
      }
    } catch { /* fall through to localStorage */ }
  }
  // Legacy fallback
  return getCheckpoint(key);
}

/**
 * Async durable read of a pull checkpoint from _sync_meta.
 * Falls back to localStorage when _sync_meta is unavailable.
 */
async function getPullCheckpointDurable(key: string): Promise<string> {
  if (_dexieInstance) {
    try {
      const metaTable = _dexieInstance.table('_sync_meta');
      if (metaTable) {
        const record = await metaTable.get(key);
        if (record?.value) return String(record.value);
      }
    } catch { /* fall through to localStorage */ }
  }
  return getCheckpoint(key);
}

/**
 * Whether a collection has completed a pull checkpoint, read from the durable
 * store (_sync_meta, with legacy localStorage fallback). Consumers that gate UI
 * on "has this collection synced" MUST use this, not localStorage directly:
 * durable writes remove the localStorage copy, so a raw localStorage read never
 * sees a checkpoint once the sync engine has written one.
 */
export async function hasPullCheckpoint(collection: string, checkpointPrefix?: string): Promise<boolean> {
  return !!(await getPullCheckpointDurable(PULL_KEY(collection, checkpointPrefix)));
}

/** Count how many of the given collections have a durable pull checkpoint. */
export async function countPullCheckpoints(collections: readonly string[], checkpointPrefix?: string): Promise<number> {
  const values = await Promise.all(
    collections.map((collection) => getPullCheckpointDurable(PULL_KEY(collection, checkpointPrefix))),
  );
  return values.filter(Boolean).length;
}

/**
 * Durable write of a pull checkpoint to _sync_meta.
 * Also removes the legacy localStorage entry so quota pressure cannot
 * resurrect a stale value. Fire-and-forget on the put (same pattern as
 * setPushCheckpoint) — the value is best-effort durable.
 */
function setPullCheckpointDurable(key: string, value: string): void {
  // When the durable store (_sync_meta) is available, write there exclusively
  // and remove the legacy localStorage entry so quota pressure cannot
  // silently evict the checkpoint. When _sync_meta is unavailable (older
  // browsers or test mocks without it), fall back to localStorage.
  if (!_dexieInstance) {
    setCheckpoint(key, value);
    return;
  }
  try { localStorage.removeItem(key); } catch { /* ignore */ }
  try {
    const metaTable = _dexieInstance.table('_sync_meta');
    if (!metaTable) { setCheckpoint(key, value); return; }
    const writePromise = metaTable.put({ _id: key, value }).catch((e: any) => {
      debugWarn(`[sync] Failed to persist durable pull checkpoint ${key}:`, e);
    });
    trackMetaWrite(writePromise);
  } catch (e) {
    console.warn(`[sync] setPullCheckpointDurable error for ${key}:`, e);
    setCheckpoint(key, value);
  }
}

/**
 * One-time migration of a legacy localStorage pull checkpoint into _sync_meta.
 * If the localStorage key exists AND _sync_meta does not yet have it, writes
 * the value to _sync_meta and removes the localStorage entry.
 * Returns the migrated value (or '' when nothing to migrate).
 */
async function migrateLegacyPullCheckpoint(key: string): Promise<string> {
  // 1. Check the durable store (_sync_meta) first — this is the authoritative
  //    location for pull checkpoints. If it has the value, return it and
  //    clean up any stale localStorage entry.
  if (_dexieInstance) {
    try {
      const metaTable = _dexieInstance.table('_sync_meta');
      if (metaTable) {
        const existing = await metaTable.get(key);
        if (existing?.value) {
          try { localStorage.removeItem(key); } catch { /* ignore */ }
          return String(existing.value);
        }
      }
    } catch { /* fall through to localStorage */ }
  }
  // 2. No durable value — check localStorage for a legacy checkpoint.
  const legacyValue = getCheckpoint(key);
  if (!legacyValue) return '';
  // 3. Migrate: write the legacy value to _sync_meta and remove from
  //    localStorage so quota pressure cannot recur.
  if (_dexieInstance) {
    try {
      const metaTable = _dexieInstance.table('_sync_meta');
      if (metaTable) {
        await metaTable.put({ _id: key, value: legacyValue });
        try { localStorage.removeItem(key); } catch { /* ignore */ }
      }
    } catch { /* keep localStorage value if _sync_meta write fails */ }
  }
  return legacyValue;
}

/**
 * @process ParseRetryAfterMs
 * Steps:   Parse an HTTP Retry-After header value into milliseconds.
 * Inputs:  headerValue — the raw header string (delta-seconds or HTTP-date), or null.
 * Outputs: Milliseconds to wait, or undefined when the value is absent/unparseable.
 *          Past HTTP-dates clamp to 0 (never negative).
 * Failure Handling: returns undefined for unparseable values.
 * Idempotency: NOT REQUIRED (pure parse).
 */
export function parseRetryAfterMs(headerValue: string | null | undefined): number | undefined {
  if (headerValue == null || headerValue === '') return undefined;
  const trimmed = headerValue.trim();
  if (trimmed === '') return undefined;
  // Delta-seconds: a non-negative integer
  if (/^\d+$/.test(trimmed)) {
    const seconds = parseInt(trimmed, 10);
    return seconds * 1000;
  }
  // HTTP-date: parse and compute remaining ms
  const parsed = Date.parse(trimmed);
  if (Number.isNaN(parsed)) return undefined;
  const diff = parsed - Date.now();
  // Clamp past dates to 0 — never return a negative delay.
  return Math.max(0, diff);
}

function setPushCheckpoint(dexieCollection: string, timestamp: string, checkpointPrefix?: string): void {
  if (!_dexieInstance) return;
  try {
    const metaTable = _dexieInstance.table('_sync_meta');
    if (!metaTable) return;
    const key = PUSH_KEY(dexieCollection, checkpointPrefix);
    debugLog(`[sync] 📌 Updating push checkpoint for ${dexieCollection} to ${timestamp}`);
    const writePromise = metaTable.put({ _id: key, value: timestamp }).catch((e: any) => {
      debugWarn(`[sync] Failed to update push checkpoint for ${dexieCollection}:`, e);
    });
    trackMetaWrite(writePromise);
  } catch (e) {
    console.warn(`[sync] setPushCheckpoint error for ${dexieCollection}:`, e);
  }
}

async function getPushCheckpoint(dexieCollection: string, checkpointPrefix?: string): Promise<string> {
  if (!_dexieInstance) return '';
  try {
    const metaTable = _dexieInstance.table('_sync_meta');
    if (!metaTable) return '';
    const record = await metaTable.get(PUSH_KEY(dexieCollection, checkpointPrefix));
    return record?.value || '';
  } catch {
    return '';
  }
}

async function advancePushCheckpoint(dexieCollection: string, timestamp: string, checkpointPrefix?: string): Promise<void> {
  if (!timestamp) return;
  const current = await getPushCheckpoint(dexieCollection, checkpointPrefix);
  if (!current || timestamp > current) {
    setPushCheckpoint(dexieCollection, timestamp, checkpointPrefix);
  }
}

async function getPushAckTimestamps(
  dexieCollection: string,
  docIds: string[],
  checkpointPrefix?: string,
): Promise<Map<string, string>> {
  const ackMap = new Map<string, string>();
  if (!_dexieInstance || docIds.length === 0) return ackMap;
  try {
    const metaTable = _dexieInstance.table('_sync_meta');
    if (!metaTable) return ackMap;
    const keys = docIds.map((docId) => PUSH_ACK_KEY(dexieCollection, docId, checkpointPrefix));
    const records = await metaTable.bulkGet(keys);
    // Dexie guarantees bulkGet returns results positionally aligned with keys, so
    // `index` indexes both `records` and `docIds`. Guard the invariant explicitly:
    // a mismatch would only drop trailing docs (treated as un-acked → conservatively
    // re-pushed), but we surface it because it would signal a Dexie contract change.
    if (records.length !== keys.length) {
      console.warn(
        `[sync] getPushAckTimestamps bulkGet length mismatch for ${dexieCollection}: expected ${keys.length}, got ${records.length}`,
      );
    }
    records.forEach((record: any, index: number) => {
      const value = record?.value;
      if (typeof value === 'string' && value) {
        ackMap.set(docIds[index], value);
      }
    });
  } catch {
    return ackMap;
  }
  return ackMap;
}

async function getUnackedPushIds(dexieCollection: string, checkpointPrefix?: string): Promise<Set<string>> {
  const ids = new Set<string>();
  if (!_dexieInstance) return ids;
  try {
    const metaTable = _dexieInstance.table('_sync_meta');
    const keyPrefix = `sync_${checkpointNamespacePrefix(checkpointPrefix)}push_unacked_${dexieCollection}_`;
    const keys = await metaTable.where('_id').startsWith(keyPrefix).primaryKeys();
    for (const key of keys) {
      try {
        ids.add(decodeURIComponent(String(key).slice(keyPrefix.length)));
      } catch {
        continue;
      }
    }
  } catch {
    return ids;
  }
  return ids;
}

function recordUnackedPushDocs(
  dexieCollection: string,
  docs: Array<{ _id: string }>,
  checkpointPrefix?: string,
): void {
  if (!_dexieInstance || docs.length === 0) return;
  try {
    const metaTable = _dexieInstance.table('_sync_meta');
    const records = docs.map((doc) => ({
      _id: PUSH_UNACKED_KEY(dexieCollection, String(doc._id), checkpointPrefix),
      value: true,
    }));
    trackMetaWrite(metaTable.bulkPut(records).catch((error: unknown) => {
      console.warn(`[sync] Failed to record unacked push documents for ${dexieCollection}:`, error);
    }));
  } catch (error) {
    console.warn(`[sync] recordUnackedPushDocs error for ${dexieCollection}:`, error);
  }
}

function clearUnackedPushDocs(
  dexieCollection: string,
  docs: Array<{ _id: string }>,
  checkpointPrefix?: string,
): void {
  clearUnackedPushIds(dexieCollection, docs.map((doc) => String(doc._id)), checkpointPrefix);
}

function clearUnackedPushIds(
  dexieCollection: string,
  docIds: string[],
  checkpointPrefix?: string,
): void {
  if (!_dexieInstance || docIds.length === 0) return;
  try {
    const metaTable = _dexieInstance.table('_sync_meta');
    const keys = docIds.map((docId) => PUSH_UNACKED_KEY(dexieCollection, docId, checkpointPrefix));
    trackMetaWrite(metaTable.bulkDelete(keys).catch((error: unknown) => {
      console.warn(`[sync] Failed to clear unacked push documents for ${dexieCollection}:`, error);
    }));
  } catch (error) {
    console.warn(`[sync] clearUnackedPushDocs error for ${dexieCollection}:`, error);
  }
}

/**
 * @process getPushAckTimestampForDocument
 * Steps: Read the existing per-document push acknowledgement key from the supplied raw Dexie database.
 * Inputs: Raw Dexie table accessor, sync collection, document ID, and optional checkpoint prefix.
 * Outputs: Last acknowledged document updatedAt, or null when the document has not been acknowledged.
 * Failure Handling: Missing DB/table/key and IDB read failures return null so callers fail closed.
 * Idempotency: NOT REQUIRED — read-only local sync metadata lookup.
 *
 * @errorHandling getPushAckTimestampForDocument
 * Strategy: Treat every read failure as unacknowledged; never infer server acceptance from a missing record.
 * User Impact: An offline-created contact origin remains deferred until explicit sync acknowledgement exists.
 * Logging: No IDs or payloads are logged.
 * @localFirst
 * Source of Truth: `_sync_meta` push-ack record written only after server acceptance.
 * Write Flow: No writes.
 * Sync Strategy: Uses the shared PUSH_ACK_KEY format; no normal sync collection is added.
 * Conflict Resolution: A missing or older ack never qualifies the current document as synchronized.
 */
export async function getPushAckTimestampForDocument(
  rawDexie: { table: (name: string) => any } | null | undefined,
  dexieCollection: string,
  docId: string,
  checkpointPrefix?: string,
): Promise<string | null> {
  if (!rawDexie || !docId) return null;
  try {
    const record = await rawDexie.table('_sync_meta').get(PUSH_ACK_KEY(dexieCollection, docId, checkpointPrefix));
    return typeof record?.value === 'string' && record.value ? record.value : null;
  } catch {
    return null;
  }
}

function setPushAckTimestamps(
  dexieCollection: string,
  docs: Array<{ _id: string; updatedAt?: string }>,
  checkpointPrefix?: string,
): void {
  if (!_dexieInstance || docs.length === 0) return;
  try {
    const metaTable = _dexieInstance.table('_sync_meta');
    if (!metaTable) return;
    const records = docs
      .map((doc) => ({
        _id: PUSH_ACK_KEY(dexieCollection, String(doc._id), checkpointPrefix),
        value: typeof doc.updatedAt === 'string' ? doc.updatedAt : '',
      }))
      .filter((record) => record.value);
    if (records.length === 0) return;
    // Best-effort by design: push-ack tracking is an optimization that suppresses
    // redundant re-pushes. A failed write leaves the doc un-acked, so the next sync
    // cycle conservatively re-pushes it (idempotent on the server) rather than
    // skipping a genuinely dirty doc. We therefore log and swallow instead of
    // propagating — a throw here would abort an otherwise-successful push.
    const writePromise = metaTable.bulkPut(records).catch((e: any) => {
      console.warn(`[sync] Failed to update push ack timestamps for ${dexieCollection}:`, e);
    });
    void clearAcknowledgedDirtyDocs(_dexieInstance, dexieCollection, docs).catch((e: any) => {
      console.warn(`[sync] Failed to clear dirty ledger acknowledgements for ${dexieCollection}:`, e);
    });
    trackMetaWrite(writePromise);
  } catch (e) {
    console.warn(`[sync] setPushAckTimestamps error for ${dexieCollection}:`, e);
  }
}

async function getKnownServerVersions(
  dexieCollection: string,
  checkpointPrefix?: string,
  explicitMetaTable?: any,
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  try {
    const metaTable = explicitMetaTable ?? (_dexieInstance ? _dexieInstance.table('_sync_meta') : null);
    if (!metaTable) return result;
    const record = await metaTable.get(KNOWN_SERVER_VERSIONS_KEY(dexieCollection, checkpointPrefix));
    if (typeof record?.value === 'string' && record.value) {
      const parsed = JSON.parse(record.value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        for (const [id, ts] of Object.entries(parsed)) {
          if (id && typeof ts === 'string') result.set(id, ts);
        }
      }
    }
    const legacyRecord = await metaTable.get(KNOWN_SERVER_IDS_KEY(dexieCollection, checkpointPrefix));
    if (typeof legacyRecord?.value === 'string' && legacyRecord.value) {
      const parsed = JSON.parse(legacyRecord.value);
      if (Array.isArray(parsed)) {
        for (const id of parsed) {
          if (id != null && String(id) && !result.has(String(id))) {
            result.set(String(id), '');
          }
        }
      }
    }
  } catch { /* ignore */ }
  return result;
}

const _knownServerVersionQueues = new Map<string, Promise<void>>();

function recordKnownServerVersions(
  dexieCollection: string,
  entries: Iterable<{ _id: string; updatedAt: string }>,
  checkpointPrefix?: string,
  replace = false,
  explicitMetaTable?: any,
): void {
  try {
    const metaTable = explicitMetaTable ?? (_dexieInstance ? _dexieInstance.table('_sync_meta') : null);
    if (!metaTable) return;
    const key = KNOWN_SERVER_VERSIONS_KEY(dexieCollection, checkpointPrefix);
    const incoming: Array<{ _id: string; updatedAt: string }> = [];
    for (const entry of entries) {
      if (entry?._id != null && String(entry._id)) {
        incoming.push({ _id: String(entry._id), updatedAt: String(entry.updatedAt ?? '') });
      }
    }
    if (!replace && incoming.length === 0) return;
    const prevQueue = _knownServerVersionQueues.get(key) ?? Promise.resolve();
    const writePromise = prevQueue
      .catch(() => undefined)
      .then(async () => {
        const nextMap = replace
          ? new Map<string, string>()
          : await getKnownServerVersions(dexieCollection, checkpointPrefix, metaTable);
        for (const { _id, updatedAt } of incoming) {
          const prev = nextMap.get(_id);
          if (!prev || (updatedAt && updatedAt >= prev)) {
            nextMap.set(_id, updatedAt);
          }
        }
        await metaTable.put({ _id: key, value: JSON.stringify(Object.fromEntries(nextMap)) });
      })
      .catch((e: any) => {
        debugWarn(`[sync] Failed to update known server versions for ${dexieCollection}:`, e);
      });
    _knownServerVersionQueues.set(key, writePromise);
    trackMetaWrite(writePromise);
  } catch { /* ignore */ }
}

async function getLastSyncedWatermark(
  dexieCollection: string,
  checkpointPrefix?: string,
): Promise<string> {
  if (!_dexieInstance) return '';
  try {
    const metaTable = _dexieInstance.table('_sync_meta');
    if (!metaTable) return '';
    const record = await metaTable.get(LAST_SYNCED_WATERMARK_KEY(dexieCollection, checkpointPrefix));
    return typeof record?.value === 'string' ? record.value : '';
  } catch {
    return '';
  }
}

/**
 * Distinguish a local row that was already synchronized with (or originated
 * from) the server from a genuinely unpushed offline creation/edit.
 */
function isPreviouslySyncedServerRow(
  row: any,
  pushField: string,
  initialPrePullPushCp: string,
  knownServerVersions: ReadonlyMap<string, string>,
  ackMap: Map<string, string>,
  unackedPushIds: ReadonlySet<string>,
  preservedPendingDeleteIds: ReadonlySet<string>,
  preservedNewerLocalIds: ReadonlySet<string>,
): boolean {
  if (!row || typeof row !== 'object') return false;
  const docId = String(row._id ?? '');
  if (!docId) return false;
  if (row._deleted === true || unackedPushIds.has(docId) || preservedPendingDeleteIds.has(docId) || preservedNewerLocalIds.has(docId)) {
    return false;
  }
  const ts = typeof row[pushField] === 'string' ? row[pushField] : '';
  if (!isUnackedLocalDoc(row, pushField, ackMap)) {
    return true;
  }
  if (knownServerVersions.has(docId)) {
    const knownTs = knownServerVersions.get(docId) ?? '';
    if (!knownTs) {
      return !initialPrePullPushCp || !ts || ts <= initialPrePullPushCp;
    }
    return !ts || ts <= knownTs || pushAckTimestampsMatch(ts, knownTs);
  }
  if (initialPrePullPushCp && ts) {
    return ts <= initialPrePullPushCp;
  }
  const migratedAt = typeof row.legacySource?.migratedAt === 'string'
    ? row.legacySource.migratedAt
    : row.legacySource?.migratedAt instanceof Date
      ? row.legacySource.migratedAt.toISOString()
      : '';
  const responseMigratedAt = typeof row.legacyMigrationAppliedAt === 'string'
    ? row.legacyMigrationAppliedAt
    : row.legacyMigrationAppliedAt instanceof Date
      ? row.legacyMigrationAppliedAt.toISOString()
      : '';
  const latestMigrationTs = migratedAt && responseMigratedAt
    ? (migratedAt >= responseMigratedAt ? migratedAt : responseMigratedAt)
    : (migratedAt || responseMigratedAt);
  if (latestMigrationTs && ts && ts > latestMigrationTs && row.updatedBy !== 'legacy-migration') {
    return false;
  }
  if (
    row.legacySource != null ||
    row.createdBy === 'legacy-migration' ||
    row.updatedBy === 'legacy-migration'
  ) {
    return true;
  }
  return false;
}

/**
 * @process PruneSupersededPushAcks
 * Steps:   Delete push-ack records whose timestamp is at or below the new
 *          push checkpoint. Once the checkpoint advances past an ack, the
 *          ack is unreachable by future `above(checkpoint)` scans and only
 *          serves to grow _sync_meta unbounded.
 * Inputs:  dexieCollection, newCheckpoint (ISO ts), checkpointPrefix.
 * Outputs: None (deletes rows from _sync_meta).
 * Failure Handling: best-effort — logs and swallows.
 * Idempotency: REQUIRED.
 */
async function pruneSupersededPushAcks(
  dexieCollection: string,
  newCheckpoint: string,
  checkpointPrefix?: string,
): Promise<void> {
  if (!_dexieInstance || !newCheckpoint) return;
  try {
    const metaTable = _dexieInstance.table('_sync_meta');
    if (!metaTable) return;
    const ackPrefix = `sync_${checkpointNamespacePrefix(checkpointPrefix)}push_ack_${dexieCollection}_`;
    const ackKeys = await metaTable.where('_id').startsWith(ackPrefix).primaryKeys();
    if (ackKeys.length === 0) return;
    // Fetch all ack records to compare their timestamps against the checkpoint.
    const ackRecords = await metaTable.bulkGet(ackKeys);
    const toDelete: string[] = [];
    for (let i = 0; i < ackKeys.length; i++) {
      const record = ackRecords[i];
      const ackTs = record?.value;
      if (typeof ackTs === 'string' && ackTs && ackTs <= newCheckpoint) {
        toDelete.push(ackKeys[i] as string);
      }
    }
    if (toDelete.length > 0) {
      await metaTable.bulkDelete(toDelete);
      debugLog(`[sync] Pruned ${toDelete.length} superseded push-ack(s) for ${dexieCollection}`);
    }
  } catch (e) {
    console.warn(`[sync] pruneSupersededPushAcks error for ${dexieCollection}:`, e);
  }
}

/**
 * @process pushAckTimestampsMatch
 * Steps:   Compare a doc's local push-field timestamp against its last push-ack timestamp.
 * Inputs:  localTs, ackTs — ISO-8601 strings (or '').
 * Outputs: true when both denote the same instant, ignoring serialized precision
 *          differences (e.g. `2024-01-01T12:00:00Z` vs `2024-01-01T12:00:00.000Z`).
 * Failure Handling: when either value is non-empty but unparseable, falls back to exact
 *          string equality. This is intentionally conservative — a false "differs" only
 *          re-pushes a doc (idempotent on the server); it never skips a genuinely dirty doc.
 * Idempotency: NOT REQUIRED (pure comparison).
 */
export function pushAckTimestampsMatch(localTs: string, ackTs: string): boolean {
  if (localTs === ackTs) return true;
  if (!localTs || !ackTs) return false;
  const localMs = Date.parse(localTs);
  const ackMs = Date.parse(ackTs);
  if (Number.isNaN(localMs) || Number.isNaN(ackMs)) return false;
  return localMs === ackMs;
}

/**
 * True when a local doc still needs pushing: it has never been acked, has no
 * local push timestamp, or its timestamp differs from the ack. Shared by the
 * push leg and the post-full-pull pending-edit scan.
 */
function isUnackedLocalDoc(doc: any, pushField: string, ackMap: Map<string, string>): boolean {
  const ackTs = ackMap.get(String(doc?._id));
  const localTs = typeof doc?.[pushField] === 'string' ? String(doc[pushField]) : '';
  return !ackTs || !localTs || !pushAckTimestampsMatch(localTs, ackTs);
}

/** ISO instant 1 ms before `ts` (so `above()` includes `ts`), or '' when unparseable. */
function isoJustBefore(ts: string): string {
  const ms = Date.parse(ts);
  return Number.isNaN(ms) ? '' : new Date(ms - 1).toISOString();
}

/**
 * Deterministic seam for empty-delta divergence recovery.
 * Returns true only when a delta pull returned zero docs and the local table is empty.
 */
export function shouldRecoverFromEmptyDelta(lastDeltaDocumentsLength: number, localCount: number): boolean {
  return lastDeltaDocumentsLength === 0 && localCount === 0;
}

/**
 * Deterministic seam for the stale-doc post-pull reconciliation decision.
 * Returns true only for a fresh full pull that wrote docs and left Dexie with
 * materially more rows than the server sent, which indicates stale re-imported rows.
 */
const RECEIPT_DEXIE_TABLE = 'legacy_migration_receipts';

/**
 * Receipt coverage indexes a client-only `deletedFlag`. Server documents omit
 * it, and IndexedDB skips a compound key when any part is missing, so every
 * inbound receipt must be stamped before bulkPut/put.
 */
function stampPulledReceipts(dexieCollection: string, docs: any[]): any[] {
  if (dexieCollection !== RECEIPT_DEXIE_TABLE) return docs;
  return docs.map((doc) => stampReceiptCoverageFlag(doc));
}

export function shouldReconcileStaleFreshPull(
  wasFreshPull: boolean,
  totalUpserted: number,
  dexieCount: number,
): boolean {
  return wasFreshPull && totalUpserted > 0 && dexieCount > Math.ceil(totalUpserted * 1.1);
}

/**
 * @reusable isProtectedOptimisticRow
 * Usage: The single predicate for "this local row is an in-flight optimistic
 *        write and must not be replaced or deleted by server state yet". Shared
 *        by every guard that protects optimistic rows — the read-only upsert
 *        guard, the delta-pull tombstone guard, and applyRemoteDelete — so the
 *        four call sites cannot drift apart.
 * Rules: Presentation-free, pure. A row is protected ONLY while it is both
 *        `__optimistic` AND non-terminal. A `failed` optimistic row is terminal:
 *        the browser upload never reached the server, so it never received
 *        `__uploadJobId` and would otherwise block every future server update
 *        and tombstone for that county forever (see
 *        markOptimisticCacheFileFailed).
 */
export function isProtectedOptimisticRow(doc: any): boolean {
  return doc?.__optimistic === true && doc.status !== 'failed';
}

/**
 * @process shouldAcceptReadOnlyServerUpsert
 * Steps: Accept normal read-only updates; preserve an optimistic cache upload
 *        until the server echoes the unique job id returned for that attempt.
 *        A failed optimistic row is terminal — the server record is authoritative
 *        and must be allowed to replace it (see isProtectedOptimisticRow).
 * Inputs: server collection name, local document, incoming server document.
 * Outputs: true when the incoming document may replace local state.
 * Failure Handling: Missing job ids preserve optimistic state instead of risking
 *                   replacement by a previous upload attempt, UNLESS the local
 *                   row is already in a terminal 'failed' status.
 * Idempotency: REQUIRED.
 */
export function shouldAcceptReadOnlyServerUpsert(
  serverCollection: string,
  localDoc: any,
  serverDoc: any,
): boolean {
  if (serverCollection !== 'geoCacheFiles' || !isProtectedOptimisticRow(localDoc)) return true;
  const acceptedJobId = localDoc.__uploadJobId as string | undefined;
  const serverJobId = serverDoc?.uploadJobId as string | undefined;
  return Boolean(acceptedJobId && serverJobId === acceptedJobId);
}

/**
 * Deterministic seam for queued wake delivery.
 * Increments the pending wake count if the listener is not ready, otherwise invokes the wake fn.
 */
export function queueWakeRequest(
  pendingWakeMap: Map<string, number>,
  dexieKey: string,
  wakeFn?: () => void,
  instanceId = 'tenant',
): void {
  if (wakeFn) {
    wakeFn();
    return;
  }
  const prev = pendingWakeMap.get(dexieKey) ?? 0;
  pendingWakeMap.set(dexieKey, prev + 1);
  // Only log once per collection to avoid flooding the console during initial
  // sync. This is expected startup ordering (WS connects before Dexie
  // collection listeners register), NOT an error — the wake is queued and
  // flushed once the listener is ready. Use console.debug so it doesn't
  // surface as a warning in production consoles.
  if (prev === 0) {
    console.debug(`[sync:${instanceId}] Wake requested before listener registration; queued for: ${dexieKey}`);
  }
}

/**
 * Deterministic seam for flushing queued wake requests once the listener exists.
 */
export function flushQueuedWakeRequests(
  pendingWakeMap: Map<string, number>,
  dexieCollection: string,
  wakeFn: () => void,
): number {
  const queuedWakeCount = pendingWakeMap.get(dexieCollection) ?? 0;
  if (queuedWakeCount > 0) {
    pendingWakeMap.delete(dexieCollection);
    // Wake only ONCE regardless of how many events queued — multiple wakes
    // for the same collection are redundant since a single pull fetches all
    // changes since the last checkpoint. This prevents N redundant pull
    // attempts during initial sync when the WS floods hundreds of upserts.
    wakeFn();
  }
  return queuedWakeCount;
}

/**
 * Deterministic seam for surfacing a manual conflict to the UI.
 */
export function emitManualConflict(
  dexieCollection: string,
  conflict: any,
  localDoc: any,
  mergedDiffs: FieldDiff[],
  onConflict?: (conflict: SyncConflict) => void,
): SyncConflict | undefined {
  if (!onConflict) return undefined;
  const payload: SyncConflict = {
    id: `${dexieCollection}-${String(conflict._id)}-${Date.now()}`,
    collection: dexieCollection,
    documentId: String(conflict._id),
    localState: localDoc,
    serverState: conflict,
    detectedAt: new Date().toISOString(),
    resolved: false,
    fieldDiffs: mergedDiffs,
  };
  onConflict(payload);
  return payload;
}

// ---------------------------------------------------------------------------
// DB Identity Nonce — permanent fix for checkpoint/data desync
//
// The nonce lives IN Dexie (_sync_meta table). Pull checkpoints live in
// localStorage. Push checkpoints live in _sync_meta (same durability as data).
// If Dexie is wiped but localStorage survives, the nonce disappears.
// On next replication startup, the mismatch is detected and ALL checkpoints
// are cleared, forcing a full pull for every collection.
//
// Call validateSyncNonce() BEFORE setupReplication() in every DB provider.
// ---------------------------------------------------------------------------
const SYNC_NONCE_KEY = (dbName: string, checkpointPrefix?: string) => `sync_${checkpointNamespacePrefix(checkpointPrefix)}db_nonce_${dbName}`;

/**
 * Validate that localStorage sync checkpoints belong to the current Dexie instance.
 * Call this BEFORE setupReplication() to prevent stale checkpoints from causing
 * empty grids. Pass the raw Dexie instance (not the wrapper) and a human-readable
 * DB name (e.g. 'system', 'tenant').
 *
 * @param dexieInstance - The raw Dexie instance with a `_sync_meta` table
 * @param dbName - Identifier for this DB ('system' or 'tenant-<slug>')
 * @param collectionMap - The collection map that will be passed to setupReplication
 */
export async function validateSyncNonce(
  dexieInstance: any,
  dbName: string,
  collectionMap: Record<string, string>,
  checkpointPrefix?: string,
): Promise<void> {
  try {
    // Store the Dexie instance for push checkpoint writes
    _dexieInstance = dexieInstance;

    const metaTable = dexieInstance.table('_sync_meta');
    if (!metaTable) return; // table not yet available (pre-v9/v15 schema)

    const existing = await metaTable.get('sync_nonce');
    const lsKey = SYNC_NONCE_KEY(dbName, checkpointPrefix);
    const storedNonce = (() => { try { return localStorage.getItem(lsKey) || ''; } catch { return ''; } })();

    if (!existing) {
      // Dexie has no nonce — this is a fresh DB (first open, or IndexedDB was cleared).
      // Generate a new nonce and store it in both Dexie and localStorage.
      const nonce = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      await metaTable.put({ _id: 'sync_nonce', value: nonce });
      try { localStorage.setItem(lsKey, nonce); } catch { /* ignore */ }

      // If there WERE old checkpoints in localStorage, clear them — they're stale.
      if (storedNonce || hasAnySyncCheckpoints(collectionMap, checkpointPrefix)) {
        console.warn(`[sync] DB identity nonce missing in Dexie (${dbName}) but found in localStorage — clearing stale checkpoints`);
        await clearAllSyncCheckpoints(collectionMap, metaTable, checkpointPrefix);
      } else {
        console.log(`[sync] Fresh DB (${dbName}) — initialized sync nonce`);
      }
      return;
    }

    // Dexie has a nonce — compare to localStorage
    if (existing.value !== storedNonce) {
      if (storedNonce || hasAnySyncCheckpoints(collectionMap, checkpointPrefix)) {
        console.warn(`[sync] DB identity nonce mismatch for ${dbName} (Dexie: ${existing.value}, localStorage: ${storedNonce}) — clearing all checkpoints`);
        await clearAllSyncCheckpoints(collectionMap, metaTable, checkpointPrefix);
      }
      try { localStorage.setItem(lsKey, existing.value); } catch { /* ignore */ }
    }
    // else: nonces match — checkpoints are valid, no action needed
  } catch (err: any) {
    // _sync_meta table may not exist yet (older schema version) — skip silently
    console.warn(`[sync] Nonce validation skipped for ${dbName}:`, err.message);
  }
}

/**
 * Clear ALL sync checkpoint keys from localStorage AND _sync_meta for every collection.
 * Forces a full pull on the next sync cycle for every collection.
 */
export async function clearAllSyncCheckpoints(
  collectionMap: Record<string, string>,
  metaTable?: any,
  checkpointPrefix?: string,
): Promise<void> {
  const collections = Object.keys(collectionMap);
  let cleared = 0;
  for (const col of collections) {
    const pullKey = PULL_KEY(col, checkpointPrefix);
    const pullIdKey = PULL_ID_KEY(col, checkpointPrefix);
    const pullIdTypeKey = PULL_ID_TYPE_KEY(col, checkpointPrefix);
    const cursorKey = FULLPULL_CURSOR_KEY(col, checkpointPrefix);
    try {
      if (localStorage.getItem(pullKey)) { localStorage.removeItem(pullKey); cleared++; }
      if (localStorage.getItem(pullIdKey)) { localStorage.removeItem(pullIdKey); cleared++; }
      if (localStorage.getItem(pullIdTypeKey)) { localStorage.removeItem(pullIdTypeKey); cleared++; }
      if (localStorage.getItem(cursorKey)) { localStorage.removeItem(cursorKey); cleared++; }
    } catch { /* ignore */ }
  }

  // Wait for any fire-and-forget checkpoint writes from just-cancelled loops
  // to settle before deleting durable keys so a late put cannot resurrect a
  // cleared checkpoint after forceResync.
  if (_pendingMetaWrites.size > 0) {
    await flushPendingMetaWrites();
  }

  for (const col of collections) {
    const pullKey = PULL_KEY(col, checkpointPrefix);
    const pullIdKey = PULL_ID_KEY(col, checkpointPrefix);
    const pullIdTypeKey = PULL_ID_TYPE_KEY(col, checkpointPrefix);
    const pushKey = PUSH_KEY(col, checkpointPrefix);
    const cursorKey = FULLPULL_CURSOR_KEY(col, checkpointPrefix);
    try {
      if (localStorage.getItem(pullKey)) { localStorage.removeItem(pullKey); cleared++; }
      if (localStorage.getItem(pullIdKey)) { localStorage.removeItem(pullIdKey); cleared++; }
      if (localStorage.getItem(pullIdTypeKey)) { localStorage.removeItem(pullIdTypeKey); cleared++; }
      if (localStorage.getItem(cursorKey)) { localStorage.removeItem(cursorKey); cleared++; }
    } catch { /* ignore */ }

    // Clear durable pull/cursor/push checkpoints from _sync_meta. Pull
    // checkpoints are authoritative there (migrateLegacyPullCheckpoint reads
    // _sync_meta first), so clearing localStorage alone leaves a delta pull.
    if (metaTable) {
      try {
        const durableKeys = (await metaTable.bulkGet([pullKey, pullIdKey, pullIdTypeKey, cursorKey, pushKey])).filter(Boolean);
        if (durableKeys.length > 0) {
          // Only a confirmed PUSH checkpoint certifies that local edits up to
          // that timestamp reached the server; never substitute `pullKey`.
          const priorPushRecord = durableKeys.find((r: any) => r?._id === pushKey);
          const pushWatermark = String(priorPushRecord?.value || '');
          if (pushWatermark) {
            await metaTable.put({
              _id: LAST_SYNCED_WATERMARK_KEY(col, checkpointPrefix),
              value: pushWatermark,
            });
          }
          await metaTable.bulkDelete([pullKey, pullIdKey, pullIdTypeKey, cursorKey, pushKey]);
          cleared += durableKeys.length;
        }
        const ackPrefix = `sync_${checkpointNamespacePrefix(checkpointPrefix)}push_ack_${col}_`;
        const ackKeys = await metaTable.where('_id').startsWith(ackPrefix).primaryKeys();
        if (ackKeys.length > 0) {
          // Fold per-document push acknowledgements into `knownServerVersions`
          // before deleting the ack keys so per-row server provenance survives
          // checkpoint resets even when no global push checkpoint existed.
          const ackRecords = (await metaTable.bulkGet(ackKeys)).filter(Boolean);
          const ackEntries: Array<{ _id: string; updatedAt: string }> = [];
          for (const rec of ackRecords as any[]) {
            const rawKey = String(rec?._id ?? '');
            const encodedDocId = rawKey.slice(ackPrefix.length);
            const docId = encodedDocId ? decodeURIComponent(encodedDocId) : '';
            const ackTs = typeof rec?.value === 'string' ? rec.value : '';
            if (docId && ackTs) {
              ackEntries.push({ _id: docId, updatedAt: ackTs });
            }
          }
          if (ackEntries.length > 0) {
            recordKnownServerVersions(col, ackEntries, checkpointPrefix, false, metaTable);
            await flushPendingMetaWrites();
          }
          await metaTable.bulkDelete(ackKeys);
          cleared += ackKeys.length;
        }
      } catch { /* ignore */ }
    }
  }
  if (cleared > 0) {
    console.log(`[sync] Cleared ${cleared} stale checkpoint keys`);
  }
}

// ---------------------------------------------------------------------------
// _id coercion — ensures every doc has a valid string primary key for IndexedDB.
// MongoDB _id may arrive as an ObjectId-like object ({$oid:...}, {toHexString:...}),
// null, or undefined after JSON round-trip edge cases. Coerce to string so Dexie
// never hits DataError: "key path yielded a value that is not a valid key".
// ---------------------------------------------------------------------------
/**
 * @security partitionInboundPulledDocuments
 * Validation: Runs every pulled server document through
 *   `validateComplexInspectionSyncDocument` against its Mongo authority
 *   collection name BEFORE `_id` coercion or any Dexie write. The helper's
 *   alias table resolves client-side names (`entities`, `inspectionCases`,
 *   `generatedDocuments`, `mobile_tenant_runtime`, …) to authority names, and
 *   the virtual pull-only `mobileTenantRuntime` collection is always
 *   validated against `mobileTenantRuntimeConfigSchema`.
 * Trust Boundary: Server pull payloads are untrusted input. Legacy documents
 *   (no schemaVersion / no complex fields) pass through unchanged; a
 *   malformed schemaVersion-1 / complex target document is QUARANTINED —
 *   returned in `rejected` and never persisted to Dexie. It is NOT thrown:
 *   the push-side gate is fail-closed, but on pull one non-conforming server
 *   document (backfill, newer API version, unvalidated server path) must not
 *   block every later document in the collection. Callers write `accepted`,
 *   advance the cursor/checkpoint as usual, and surface `rejected` through
 *   `reportLocalWriteFailures`; the document is re-evaluated when the server
 *   sends a newer version of it.
 *
 * @errorHandling partitionInboundPulledDocuments
 * Strategy: Per-document catch; never throws.
 * User Impact: Quarantined documents are absent (or stay at their previous
 *   local version) until the server sends a conforming version.
 * Logging: None here — callers report `_id`s + the validation message.
 */
function partitionInboundPulledDocuments(
  serverCollection: string,
  documents: any[],
): { accepted: any[]; rejected: Array<{ id: string; reason: string }> } {
  const accepted: any[] = [];
  const rejected: Array<{ id: string; reason: string }> = [];
  for (const doc of documents) {
    try {
      accepted.push({ ...doc, ...validateComplexInspectionSyncDocument(serverCollection, doc) });
    } catch (err: any) {
      rejected.push({
        id: String(doc?._id ?? '<unknown>'),
        reason: String(err?.message ?? err ?? 'inbound validation failed'),
      });
    }
  }
  return { accepted, rejected };
}

function coerceDocIds(docs: any[]): any[] {
  for (const doc of docs) {
    if (doc._id == null) {
      // No _id at all — skip this doc (will fail put anyway)
      continue;
    }
    if (typeof doc._id === 'string') continue; // already valid
    // ObjectId-like object with toHexString
    if (typeof doc._id.toHexString === 'function') {
      doc._id = doc._id.toHexString();
    } else if (doc._id.$oid) {
      // Extended JSON format {$oid: "..."}
      doc._id = String(doc._id.$oid);
    } else {
      // Fallback: force to string
      doc._id = String(doc._id);
    }
  }
  return docs;
}

// ---------------------------------------------------------------------------
// Simple observable helper
// ---------------------------------------------------------------------------
function makeObservable<T>() {
  const subs: Array<(v: T) => void> = [];
  return {
    subscribe: (fn: (v: T) => void): (() => void) => {
      subs.push(fn);
      return () => { const i = subs.indexOf(fn); if (i !== -1) subs.splice(i, 1); };
    },
    emit: (v: T) => { subs.forEach(fn => { try { fn(v); } catch { /* ignore */ } }); },
  };
}

/**
 * @process CheckSyncDivergence
 *
 * Steps:
 * 1. Compute local counts and newest `updatedAt` values for selected Dexie collections.
 * 2. Ask the sync status endpoint whether server count or timestamp differs.
 * 3. Clear pull checkpoints for diverged collections and wake their sync loops.
 *
 * Inputs:
 * - Dexie database instance and replication config.
 *
 * Outputs:
 * - Dexie collection keys that require full reconciliation.
 *
 * Failure Handling:
 * - Logs and returns an empty list when status checks fail.
 *
 * Idempotency:
 * - REQUIRED
 */
function bindDexieMetaInstance(db: unknown): void {
  try {
    const metaCapableDb = db as { table?: (name: string) => any; _sync_meta?: { table?: any } };
    if (typeof metaCapableDb?.table === 'function') {
      const metaTable = metaCapableDb.table('_sync_meta');
      if (metaTable) {
        _dexieInstance = metaCapableDb;
      }
    } else if (metaCapableDb?._sync_meta?.table) {
      const syncMetaTable = metaCapableDb._sync_meta.table;
      _dexieInstance = {
        table: (name: string) => (name === '_sync_meta' ? syncMetaTable : undefined),
      };
    }
  } catch { /* ignore */ }
}

export async function checkSyncDivergence(
  db: any,
  config: ReplicationConfig,
): Promise<string[]> {
  const selectedDexieKeys = config.divergenceCheckCollections ?? [];
  if (selectedDexieKeys.length === 0) return [];

  const localCounts: Record<string, number> = {};
  const localMaxUpdatedAt: Record<string, string> = {};
  const serverToDexie = new Map<string, string>();

  for (const dexieKey of selectedDexieKeys) {
    const serverCollection = config.collectionMap[dexieKey];
    if (!serverCollection) continue;

    // Skip collections that have not yet completed their initial full pull
    // (no pull checkpoint in _sync_meta or localStorage). On a fresh DB (or
    // right after a checkpoint reset) those collections are already queued for
    // a full pull; comparing their pre-pull local=0 count against serverCounts
    // falsely flags divergence and wipes their in-progress full-pull cursor
    // mid-flight.
    const pullCheckpointKey = PULL_KEY(dexieKey, config.checkpointPrefix);
    const existingPullCheckpoint =
      (await getPullCheckpointDurable(pullCheckpointKey)) ||
      (await db?._sync_meta?.table?.get?.(pullCheckpointKey))?.value ||
      '';
    if (!existingPullCheckpoint) continue;

    serverToDexie.set(serverCollection, dexieKey);

    const raw = db[dexieKey];
    const table = raw?.table ?? (typeof raw?.bulkPut === 'function' ? raw : undefined);
    if (!table) continue;

    try {
      localCounts[serverCollection] = await table.count();
      // Use the updatedAt index to find the most-recent document in O(log n)
      // rather than loading every row into memory with toArray().  For large
      // collections such as violation_index (100 000+ docs) a toArray() call
      // can load hundreds of MB of objects — enough to OOM a mobile browser tab.
      const lastRow = await table.orderBy('updatedAt').last();
      const rawUpdatedAt = lastRow?.updatedAt;
      localMaxUpdatedAt[serverCollection] = typeof rawUpdatedAt === 'string'
        ? rawUpdatedAt
        : (rawUpdatedAt instanceof Date ? rawUpdatedAt.toISOString() : '');
    } catch (error: any) {
      console.warn(`[sync] Divergence local status failed for ${dexieKey}:`, error?.message ?? error);
    }
  }

  if (Object.keys(localCounts).length === 0) return [];

  // Include the client's pending/failed push counts so the server can
  // record them to the tenant_sync_health ledger for the dashboard. This
  // gives the dashboard an honest "at least N docs are pending across
  // connected clients" signal without the server needing to track
  // client-side queue depth itself.
  const syncStatus = getSyncStatus();

  try {
    const routePrefix = config.syncRoutePrefix ?? '/sync';
    const authHeaders = await config.getAuthHeaders();
    const response = await timedFetch(`${config.apiBaseUrl}${routePrefix}/status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': config.departmentId,
        ...authHeaders,
      },
      body: JSON.stringify({
        localCounts,
        localMaxUpdatedAt,
        pendingPushCount: syncStatus.pendingPushCount,
        failedPushCount: syncStatus.failedPushCount,
      }),
    });

    if (!response.ok) {
      throw await httpSyncFailure(response, '');
    }

    const result = await response.json() as {
      diverged?: string[];
      serverCounts?: Record<string, number>;
    };
    const divergedDexieKeys = (result.diverged ?? [])
      .map((serverCollection) => serverToDexie.get(serverCollection))
      .filter((dexieKey): dexieKey is string => Boolean(dexieKey));

    for (const dexieKey of divergedDexieKeys) {
      const inProgressCursor = getCheckpoint(FULLPULL_CURSOR_KEY(dexieKey, config.checkpointPrefix));
      setPullCheckpointDurable(PULL_KEY(dexieKey, config.checkpointPrefix), '');
      setPullCheckpointDurable(PULL_ID_KEY(dexieKey, config.checkpointPrefix), '');
      setPullCheckpointDurable(PULL_ID_TYPE_KEY(dexieKey, config.checkpointPrefix), '');
      // Occupancy lives in `entities` and pages in 500-doc chunks. Wiping an
      // in-progress inspectableEntities cursor on every /sync/status divergence
      // restart from page 1 and leaves Occupancy stuck at a partial Dexie set
      // (e.g. 414 of 1859). Keep the resume cursor for that collection only.
      if (dexieKey === 'entities' && inProgressCursor) {
        console.warn(
          `[sync] Divergence detected for ${dexieKey} during in-progress full pull — keeping resume cursor so Occupancy/entities paging is not restarted from page 1`,
        );
      } else {
        setCheckpoint(FULLPULL_CURSOR_KEY(dexieKey, config.checkpointPrefix), '');
      }
      const serverCollection = config.collectionMap[dexieKey];
      const local = localCounts[serverCollection];
      const server = result.serverCounts?.[serverCollection];
      console.warn(
        `[sync] Divergence detected for ${dexieKey}; local=${local ?? '?'} server=${server ?? '?'} — full reconciliation scheduled (expected after checkpoint clear, account-scope change, or environment switch)`,
      );
    }

    const entityServer = result.serverCounts?.inspectableEntities;
    if (entityServer != null) {
      console.info(
        `[occupancy-census] sync status entities local=${localCounts.inspectableEntities ?? '?'} server=${entityServer}`,
      );
    }

    return divergedDexieKeys;
  } catch (error: any) {
    console.warn('[sync] Divergence status check failed:', error?.message ?? error);
    return [];
  }
}

// ---------------------------------------------------------------------------
// Sync timing
// MongoDB Change Streams → WebSocket is the primary real-time driver. Each
// collection loop sleeps indefinitely (24 h) after its sync cycle and wakes on:
//   • WebSocket upsert event        → immediate delta pull for that collection
//   • WebSocket delete event        → applyRemoteDelete() directly (no pull needed)
//   • §POLL-2 safety-net (90 s)    → delta pull for all collections, fires even
//                                     while WebSocket is connected; catches events
//                                     the change stream silently missed
//   • §POLL-1 disconnect poll (30 s) → delta pull for all collections, fires
//                                     when WebSocket is reconnecting / offline
//   • window.online                 → wakes all loops after network reconnect
//   • Error backoff                 → exponential retry, capped at MAX_BACKOFF_MS
//
// §POLL-1 and §POLL-2 are not a replacement for the change stream — they are
// a defence-in-depth layer for environments where the change stream is
// unreliable (Atlas M0/M2/M5 tiers, oplog gaps, transient stream errors).
// ---------------------------------------------------------------------------
const AUTH_BACKOFF_MS = 5_000;      // 5 seconds on 401/403 — token refresh is fast
const MAX_BACKOFF_MS = 2 * 60_000;  // 2 minutes cap for exponential backoff
const INDEFINITE_SLEEP_MS = 24 * 60 * 60_000; // 24h — effectively "sleep until woken"
const PUSH_BATCH_SIZE = 50;         // push docs in batches to avoid 413 Payload Too Large

// ---------------------------------------------------------------------------
// §POLL-1 / §POLL-2 — Safety-net polling interval constants
//
// The MongoDB change stream is the primary real-time driver. Two polling
// timers provide defence-in-depth for environments where the change stream
// is unreliable (Atlas M0/M2/M5 tiers) or where a transient stream error
// silently stops event delivery:
//
//   §POLL-1 (30 s — disconnected): fired when the WebSocket is reconnecting
//     or has dropped. Keeps the device roughly in sync on lossy networks and
//     ensures no more than one polling interval of staleness after reconnect.
//
//   §POLL-2 (90 s — connected): fired even while the WebSocket is connected.
//     Catches change-stream events that were silently missed without flooding
//     the server. Intentionally longer than §POLL-1 so the change stream
//     remains the primary path during normal operation.
//
// Both timers call wakeAll(), which triggers a cheap delta pull per collection
// (server returns only documents where updatedAt > last checkpoint). The
// overhead on a quiet tenant is negligible — each pull returns an empty page.
// ---------------------------------------------------------------------------
const POLLING_FALLBACK_DISCONNECTED_MS = 30_000;  // §POLL-1: 30 s when WebSocket is reconnecting
const POLLING_FALLBACK_CONNECTED_MS    = 90_000;  // §POLL-2: 90 s even when WebSocket is live

// ---------------------------------------------------------------------------
// Empty-delta recovery guard (module scope)
//
// Keys include departmentId, not just checkpointPrefix: tenant-web reuses the
// constant 'tenant' prefix across department switches (impersonation) in one
// session, and department A's recovery state must not block B's.
function pullStateKey(departmentId: string, checkpointPrefix: string | undefined, dexieCollection: string): string {
  return `${departmentId}:${checkpointPrefix ?? ''}:${dexieCollection}`;
}

// emptyDeltaRecovered — collections that have already run the
//   empty-delta divergence recovery once this session. Without this guard a
//   legitimately-empty server collection loops forever: full pull → empty
//   delta → checkpoint cleared → full pull. The key is deleted when a full
//   pull persists docs so a later real IndexedDB wipe can recover again.
// ---------------------------------------------------------------------------
const emptyDeltaRecovered = new Set<string>();

// cleanCycleCompleted — dexie collections that have finished at least one
//   clean pull+push cycle in the ACTIVE replication session. Used by
//   waitForCollectionFirstSync so write-side materializers (e.g. inspection
//   delivery records) do not create rows from a pre-pull snapshot and
//   duplicate records that already exist server-side. Cleared per-collection
//   on teardown so a department switch / restart requires a fresh pull.
// cleanCycleWaiters — pending waitForCollectionFirstSync resolvers.
const cleanCycleCompleted = new Set<string>();
const cleanCycleWaiters = new Map<string, Array<() => void>>();

/** Test helper — clears module-level recovery/first-sync state between tests. */
export function _resetPullRecoveryStateForTests(): void {
  emptyDeltaRecovered.clear();
  cleanCycleCompleted.clear();
  cleanCycleWaiters.clear();
}

/**
 * @process waitForCollectionFirstSync
 *
 * Steps:
 * 1. If `dexieCollection` already completed a clean sync cycle in the active
 *    replication session, resolve true immediately.
 * 2. Otherwise register a waiter resolved by the per-collection loop when
 *    that collection's first clean cycle lands; race it against `timeoutMs`.
 * 3. On either outcome, remove the waiter and clear the timer.
 *
 * Inputs:
 * - dexieCollection: Dexie table name (the collectionMap key), e.g.
 *   'inspection_delivery_records'.
 * - timeoutMs: safety ceiling — default 60000ms.
 *
 * Outputs:
 * - Promise<boolean>: true once a clean cycle completed; false on timeout.
 *   Never rejects.
 *
 * Failure Handling:
 * - Never throws. Timeout returns false so callers can skip a write whose
 *   correctness depends on the server snapshot being pulled first.
 *
 * Idempotency:
 * - REQUIRED — safe to call repeatedly; each call registers an independent
 *   waiter. Cleared on cancelAllReplication so a new session re-arms it.
 */
export function waitForCollectionFirstSync(dexieCollection: string, timeoutMs = 60_000): Promise<boolean> {
  if (cleanCycleCompleted.has(dexieCollection)) return Promise.resolve(true);
  return new Promise<boolean>((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const waiter = () => {
      if (timer !== undefined) clearTimeout(timer);
      resolve(true);
    };
    const list = cleanCycleWaiters.get(dexieCollection) ?? [];
    list.push(waiter);
    cleanCycleWaiters.set(dexieCollection, list);
    timer = setTimeout(() => {
      const current = cleanCycleWaiters.get(dexieCollection);
      if (current) {
        const idx = current.indexOf(waiter);
        if (idx >= 0) current.splice(idx, 1);
        if (current.length === 0) cleanCycleWaiters.delete(dexieCollection);
      }
      resolve(false);
    }, timeoutMs);
  });
}

// ---------------------------------------------------------------------------
// Core sync cycle — pull then push for a single collection
// On empty lastPull checkpoint: full reconciliation pull (allIds diff removes stale Dexie docs)
// On non-empty checkpoint: delta pull including _deleted tombstones
// Push is skipped on the very first cycle (push checkpoint set to NOW after full pull
// to prevent pushing entire Dexie state that was just received from server)
// ---------------------------------------------------------------------------
async function syncCollection(
  dexieCollection: string,
  serverCollection: string,
  db: any,
  config: ReplicationConfig,
  pullSemaphore: PullSemaphore,
  onPageProgress?: () => void,
  isCancelled: () => boolean = () => false,
): Promise<void> {
  const writePullCheckpointDurable = (key: string, value: string): void => {
    if (isCancelled()) return;
    setPullCheckpointDurable(key, value);
  };
  const writeLocalCheckpoint = (key: string, value: string): void => {
    if (isCancelled()) return;
    setCheckpoint(key, value);
  };
  const writePushCheckpoint = (col: string, ts: string, prefix?: string): void => {
    if (isCancelled()) return;
    setPushCheckpoint(col, ts, prefix);
  };
  const writeAdvancePushCheckpoint = async (col: string, ts: string, prefix?: string): Promise<void> => {
    if (isCancelled()) return;
    await advancePushCheckpoint(col, ts, prefix);
  };
  const writePushAckTimestamps = (col: string, docs: Array<{ _id: string; updatedAt?: string }>, prefix?: string): void => {
    if (isCancelled()) return;
    setPushAckTimestamps(col, docs, prefix);
  };
  const base = config.apiBaseUrl;
  const routePrefix = config.syncRoutePrefix ?? '/sync';
  const checkpointPrefix = config.checkpointPrefix;
  const getWsClient = () => config.useWebSocket
    ? getActiveWebSocketSyncClient(config.apiBaseUrl, config.departmentId, routePrefix)
    : null;

  // Build headers with fresh auth token
  async function buildHeaders(): Promise<Record<string, string>> {
    const authHeaders = await config.getAuthHeaders();
    return {
      'Content-Type': 'application/json',
      'x-tenant-slug': config.departmentId,
      ...authHeaders,
    };
  }

  // ── PULL ───────────────────────────────────────────────────────
  // Phase 4: Mark pulling as active
  setPulling(true, serverCollection);
  syncLog.info(`Pull started for ${dexieCollection}`, { collection: dexieCollection });
  
  // Durable pull checkpoint: read from _sync_meta first, migrating any
  // legacy localStorage value one-time. This prevents endless full-pull
  // loops when localStorage is evicted by quota pressure but IndexedDB
  // (and its _sync_meta table) survives.
  const pullKey = PULL_KEY(dexieCollection, checkpointPrefix);
  let lastPull = await migrateLegacyPullCheckpoint(pullKey);
  const isFirstSync = !lastPull;

  // Track whether pull succeeded so we know whether to re-throw after push.
  // For push-allowed collections, a pull failure must NOT block push — offline
  // edits need to reach the server even when the pull leg is intermittently
  // failing (timeout, transient 5xx, etc.). Non-pushable (read-only)
  // collections still throw immediately since there is nothing to push.
  let pullError: Error | null = null;
  const isPushableCollection = !!(config.pushAllowedCollections && config.pushAllowedCollections.has(serverCollection));

  // Resolve the raw Dexie.Table handle.
  // tenant-web wraps tables in DexieCollection which exposes a `.table` getter.
  // mobile-pwa passes raw Dexie.Table objects directly (no wrapper).
  // Detect by checking for `.table` first; fall back to the object itself
  // if it quacks like a Dexie.Table (has .bulkPut).
  const _raw = db[dexieCollection];
  const table = _raw?.table ?? (typeof _raw?.bulkPut === 'function' ? _raw : undefined);

  try { // ── pull try-block ──────────────────────────────────────────────

  const isWriteOnlyPush = isWriteOnlyCollection(config, dexieCollection);
  if (isWriteOnlyPush && !lastPull) {
    // Persist a pull checkpoint so we stay in write-only mode and never
    // download the department trail (mobile audit_logs). Push still runs below.
    const now = new Date().toISOString();
    writePullCheckpointDurable(pullKey, now);
    lastPull = now;
  }

  if (isFirstSync && !isWriteOnlyPush) {
    // ── FULL PULL (paginated, resumable) ────────────────────────────────
    // Large collections (e.g. 193k parcels) are pulled in pages of 500.
    // Compound cursor (updatedAt, _id) avoids infinite loops when many
    // docs share the same timestamp.
    //
    // RESUMABLE: if the full pull was interrupted (browser crash, network
    // error, memory pressure), the saved cursor lets us pick up where we
    // left off instead of restarting from page 1 every time.
    let cursorTs: string | undefined = undefined;
    let cursorId: string | undefined = undefined;
    let cursorIdType: 'string' | 'objectId' | undefined = undefined;
    let totalUpserted = 0;
    let totalServerDocs = 0; // total docs received from server (may be 0 for empty collections)
    let firstPageAllIds: string[] | undefined;
    let pageCount = 0;
    let fullPullInterruptedError: Error | null = null;
    // Per-doc Dexie write failures captured across all pages of this full pull.
    // Populated by the individual-put fallback below when bulkPut fails AND
    // an individual table.put also throws. Surfaced as a single LocalWriteError
    // at the end of the pull (one error per pull, not per page) so the user-
    // visible message reflects the total impact instead of spamming N errors
    // for one schema-rejection. See LocalWriteError @contract above.
    const localWriteFailures: Array<{ id: string; reason: string }> = [];
    const fullPullStartedAt = new Date().toISOString();
    const fullPullPushField = PUSH_TIMESTAMP_FIELD[dexieCollection] ?? 'updatedAt';
    const isPushableFullPull = !!config.pushAllowedCollections?.has(serverCollection);
    // Oldest push timestamp among newer local docs this pull preserved while
    // no push checkpoint existed — un-pushed offline edits that may predate
    // fullPullStartedAt and must still reach the push leg.
    let minPreservedLocalPushTs = '';
    // _ids of every server doc this pull returned, so the post-pull
    // pending-edit scan can tell a pre-existing local-only doc from a pulled
    // one (pulled docs are only push-acked when their timestamp is newer than
    // the pull start; docs rejected by inbound validation or whose write
    // failed are server docs too — echoing them back would be a phantom push).
    const pulledDocIds = new Set<string>();
    // _ids of locally-deleted docs preserved during the pull — pending deletes
    // must reach the push leg even though the server returned the doc.
    const preservedPendingDeleteIds = new Set<string>();
    const preservedNewerLocalIds = new Set<string>();
    // Server-time anchor for the pull checkpoint. When the server includes
    // `serverTime` in the response, the checkpoint is anchored to the SERVER's
    // clock instead of the client's — preventing clock-skew issues where a
    // client clock ahead of the server would cause delta pulls to silently
    // skip writes other clients make during the skew window.
    let serverTimeAnchor: string | undefined = undefined;
    const MAX_PAGES = 10_000; // hard safety cap — 10k pages × 500 = 5M docs max

    // Resume from saved cursor if available (interrupted full pull)
    const savedCursor = getCheckpoint(FULLPULL_CURSOR_KEY(dexieCollection, checkpointPrefix));
    // Track whether this started as a completely fresh pull (no resume cursor).
    // Used after the pull completes to detect stale docs from hard-deleted re-imports.
    const wasFreshPull = !savedCursor;
    if (savedCursor) {
      try {
        const parsed = JSON.parse(savedCursor);
        cursorTs = parsed.updatedAt || undefined;
        cursorId = parsed.sinceId || undefined;
        cursorIdType = parsed.sinceIdType || undefined;
        // Full pull pagination is now _id-only on the server. A cursor saved before
        // this change may have only cursorTs and no cursorId. In that case the server
        // ignores `since` entirely and restarts from page 1, so there is no benefit
        // in sending a ts-only cursor — clear it and start fresh instead.
        if (!cursorId) {
          cursorTs = undefined;
          writeLocalCheckpoint(FULLPULL_CURSOR_KEY(dexieCollection, checkpointPrefix), '');
          console.debug(`[sync] Discarded ts-only full-pull cursor for ${dexieCollection} — restarting (server uses _id pagination)`);
        } else {
          console.debug(`[sync] Resuming full pull for ${dexieCollection} from cursor: ts=${cursorTs}, id=${cursorId}`);
        }
      } catch {
        // Invalid cursor — start fresh
        writeLocalCheckpoint(FULLPULL_CURSOR_KEY(dexieCollection, checkpointPrefix), '');
      }
    }

    const initialPrePullPushCp = isPushableFullPull
      ? (await getPushCheckpoint(dexieCollection, checkpointPrefix)) ||
        (await getLastSyncedWatermark(dexieCollection, checkpointPrefix))
      : '';
    const knownServerIdsBeforePull = isPushableFullPull
      ? await getKnownServerVersions(dexieCollection, checkpointPrefix)
      : new Map<string, string>();
    const unackedPushIdsBeforePull = isPushableFullPull
      ? await getUnackedPushIds(dexieCollection, checkpointPrefix)
      : new Set<string>();
    if (table && unackedPushIdsBeforePull.size > 0) {
      const unackedIds = Array.from(unackedPushIdsBeforePull);
      try {
        const localDocs = await table.bulkGet(unackedIds);
        const deletedIds = unackedIds.filter((_id, index) => !localDocs[index]);
        clearUnackedPushIds(dexieCollection, deletedIds, checkpointPrefix);
        deletedIds.forEach((id) => unackedPushIdsBeforePull.delete(id));
      } catch {
        // Retain markers when local presence cannot be checked safely.
      }
    }
    const pulledServerVersionEntries: Array<{ _id: string; updatedAt: string }> = [];

    while (pageCount < MAX_PAGES) {
      pageCount++;
      const body: Record<string, any> = { collection: serverCollection, full: true };
      if (cursorTs) body.since = cursorTs;
      if (cursorId) body.sinceId = cursorId;
      if (cursorIdType) body.sinceIdType = cursorIdType;
      // Account-scoped filtering (mobile-api only)
      if (config.inspectionAccountIds && config.inspectionAccountIds.length > 0) {
        body.inspectionAccountIds = config.inspectionAccountIds;
      }

      let documents: any[];
      let checkpoint: { updatedAt: string; id?: string; idType?: 'string' | 'objectId' };
      let allIds: string[] | undefined;
      let hasMore: boolean;

      try {
        // Gate the full-pull page fetch behind the per-instance pull
        // semaphore so a long-running 100k-row collection releases its
        // slot between pages and smaller collections can interleave.
        // See PullSemaphore @contract for the rationale.
        const pageResult = await pullSemaphore.withSlot(async () => {
          let headers = await buildHeaders();
          let pullRes = await timedFetch(`${base}${routePrefix}/pull`, { method: 'POST', headers, body: JSON.stringify(body) });

          if (pullRes.status === 401 || pullRes.status === 403) {
            await new Promise(r => setTimeout(r, 1_000));
            headers = await buildHeaders();
            pullRes = await timedFetch(`${base}${routePrefix}/pull`, { method: 'POST', headers, body: JSON.stringify(body) });
          }
          if (!pullRes.ok) throw await httpSyncFailure(pullRes, '');

          return await pullRes.json();
        });
        documents = pageResult.documents;
        checkpoint = pageResult.checkpoint;
        allIds = pageResult.allIds;
        hasMore = !!pageResult.hasMore;
        // Capture server time for clock-skew-anchored checkpoint (see above).
        if (pageResult.serverTime && !serverTimeAnchor) {
          serverTimeAnchor = pageResult.serverTime;
        }
      } catch (fetchErr: any) {
        if (isCancelled()) return;
        console.warn(`[sync] Full pull fetch failed on page ${pageCount} for ${dexieCollection}: ${fetchErr.message} — will resume next cycle`);
        syncLog.error(`Full pull failed for ${dexieCollection} on page ${pageCount}: ${fetchErr.message}`, { collection: dexieCollection, detail: fetchErr.message });
        // Already logged to the activity log — mark the error so the outer
        // pull catch does not emit a second syncLog.error for the same failure.
        try { Object.defineProperty(fetchErr, '__syncLogged', { value: true, enumerable: false, configurable: true }); } catch { /* ignore */ }
        fullPullInterruptedError = fetchErr instanceof Error
          ? fetchErr
          : new Error(String(fetchErr?.message || fetchErr || 'Full pull fetch failed'));
        break; // cursor already saved — will resume next cycle
      }

      if (isCancelled()) return;

      // Only capture allIds on a completely fresh pull (no cursor at all).
      // If resuming mid-pull (cursorId is set), allIds covers ALL server docs but
      // Dexie only has partial docs so far — running reconciliation would bulkDelete
      // everything not yet pulled. Guard on both cursorTs AND cursorId being absent.
      if (pageCount === 1 && !cursorTs && !cursorId && allIds) {
        firstPageAllIds = allIds;
      }

      // Advance cursor FIRST — even if bulkPut fails, we move forward.
      // Re-fetching the same page forever is worse than skipping a few docs.
      //
      // IMPORTANT: Do NOT write PULL_KEY here. Writing it mid-loop causes
      // isFirstSync=false on the next cycle, which abandons the full pull and
      // falls into delta mode — leaving Dexie permanently under-populated.
      // FULLPULL_CURSOR_KEY handles resumption on its own.
      // PULL_KEY is only written when the full pull completes (below the loop).
      //
      // NOTE: Full pull checkpoints carry id only (updatedAt is always '' because
      // the server sorts by _id not updatedAt for full pulls). Gate on id, not updatedAt.
      for (const d of documents) {
        if (d?._id != null) pulledDocIds.add(String(d._id));
      }
      if (checkpoint?.id) {
        cursorId = checkpoint.id;
        cursorIdType = checkpoint.idType;
        cursorTs = checkpoint.updatedAt || cursorTs; // keep existing ts for delta transition
        writeLocalCheckpoint(FULLPULL_CURSOR_KEY(dexieCollection, checkpointPrefix), JSON.stringify({
          updatedAt: cursorTs,
          sinceId: cursorId,
          sinceIdType: cursorIdType,
        }));
      }

      totalServerDocs += documents.length;
      if (documents.length > 0 && table) {
        const inbound = partitionInboundPulledDocuments(serverCollection, documents);
        localWriteFailures.push(...inbound.rejected);
        const acceptedDocs = coerceDocIds(inbound.accepted);

        // Preserve locally-deleted docs that haven't been pushed yet.
        // Full pull brings server state which doesn't know about local deletes.
        // bulkPut would overwrite them, losing the pending deletion.
        let docsToWrite = acceptedDocs;
        if (isPushableFullPull) {
          try {
            const docIds = acceptedDocs.map((d: any) => d._id).filter(Boolean);
            const localDocs = await table.bulkGet(docIds);
            const pendingDeleteIds = new Set<string>();
            const newerLocalIds = new Set<string>();
            const pushCpVal = await getPushCheckpoint(dexieCollection, checkpointPrefix);
            for (const ld of localDocs) {
              if (ld && (ld as any)._deleted === true) {
                pendingDeleteIds.add(String((ld as any)._id));
                continue;
              }
              if (ld) {
                const localUpdatedAt = String((ld as any).updatedAt ?? '');
                const localId = String((ld as any)._id ?? '');
                const matchingServer = acceptedDocs.find((d: any) => String(d?._id ?? '') === localId);
                const serverUpdatedAt = String(matchingServer?.updatedAt ?? '');
                if (localUpdatedAt && serverUpdatedAt && localUpdatedAt > serverUpdatedAt) {
                  // Bug 5 fix: Only protect the local version if it has pending
                  // changes (updatedAt > push checkpoint). If already pushed, the
                  // server version from another client should be authoritative.
                  if (!pushCpVal || localUpdatedAt > pushCpVal) {
                    newerLocalIds.add(localId);
                    const localPushTs = String((ld as any)[fullPullPushField] ?? '');
                    if (!pushCpVal && localPushTs && (!minPreservedLocalPushTs || localPushTs < minPreservedLocalPushTs)) {
                      minPreservedLocalPushTs = localPushTs;
                    }
                  }
                }
              }
            }
            for (const id of pendingDeleteIds) preservedPendingDeleteIds.add(id);
            for (const id of newerLocalIds) preservedNewerLocalIds.add(id);
            if (pendingDeleteIds.size > 0 || newerLocalIds.size > 0) {
              docsToWrite = acceptedDocs.filter((d: any) => {
                const docId = String(d._id);
                return !pendingDeleteIds.has(docId) && !newerLocalIds.has(docId);
              });
              if (pendingDeleteIds.size > 0) {
                console.debug(`[sync] Full pull: preserved ${pendingDeleteIds.size} locally-deleted doc(s) in ${dexieCollection}`);
              }
              if (newerLocalIds.size > 0) {
                console.debug(`[sync] Full pull: preserved ${newerLocalIds.size} newer local doc(s) in ${dexieCollection}`);
              }
            }
          } catch { /* fall through — write all docs */ }
        } else if (serverCollection === 'geoCacheFiles') {
          // Protect optimistic cache-upload rows from full-pull overwrite.
          // A full pull (initial sync or divergence recovery) brings server
          // state that may not yet know about an in-flight browser upload.
          // Without this guard, the server's stale snapshot overwrites the
          // optimistic row, causing the upload progress bar to jump backward.
          // The delta-pull path already guards this via
          // shouldAcceptReadOnlyServerUpsert; this mirrors that guard for the
          // full-pull path so both paths are consistent.
          try {
            const docIds = acceptedDocs.map((d: any) => d._id).filter(Boolean);
            const localDocs = await Promise.all(
              docIds.map((id: any) => table.get(id).catch(() => null)),
            );
            docsToWrite = acceptedDocs.filter((serverDoc: any, index: number) =>
              shouldAcceptReadOnlyServerUpsert(serverCollection, localDocs[index], serverDoc),
            );
            const preserved = acceptedDocs.length - docsToWrite.length;
            if (preserved > 0) {
              console.debug(`[sync] Full pull: preserved ${preserved} optimistic geoCacheFiles doc(s) in ${dexieCollection}`);
            }
          } catch { /* fall through — write all docs */ }
        }

        try {
          const stampedDocs = stampPulledReceipts(dexieCollection, docsToWrite);
          await table.bulkPut(stampedDocs);
          totalUpserted += stampedDocs.length;
        } catch (putErr: any) {
          // bulkPut failed — fall back to individual puts. Previously this
          // path silently dropped any doc whose individual put also failed
          // ("/* skip this doc */"). That made schema-rejected violations
          // and other Dexie validation failures invisible: the sync widget
          // showed green while the local DB was missing data.
          //
          // Policy (LocalWriteError @contract): capture the failing _ids,
          // surface them via console.error + setSyncError, but DO advance
          // the cursor so a single bad doc cannot block the entire
          // collection forever. The outer loop continues normally.
          console.warn(`[sync] bulkPut failed for ${dexieCollection} page ${pageCount}: ${putErr.message} — trying individual puts`);
          let saved = 0;
          for (const doc of stampPulledReceipts(dexieCollection, docsToWrite)) {
            try {
              await table.put(doc);
              saved++;
            } catch (perDocErr: any) {
              const docId = String(doc?._id ?? '<unknown>');
              const reason = String(perDocErr?.message ?? perDocErr ?? 'unknown Dexie error');
              localWriteFailures.push({ id: docId, reason });
            }
          }
          totalUpserted += saved;
          console.warn(`[sync] Individual puts: ${saved}/${docsToWrite.length} saved for ${dexieCollection}`);
        }
        // Server documents whose push timestamp is newer than this pull's
        // client-clock start (client clock behind the server, or updated while
        // the pull ran) would otherwise look like un-pushed local edits to the
        // post-pull pending scan and the push leg's above(checkpoint) scan, and
        // be echoed back. Record a push-ack at the server's own timestamp: the
        // server already holds this exact version, and a later local edit
        // changes the timestamp so it is still pushed. Bounded to the skew /
        // in-pull window, so _sync_meta does not grow with collection size.
        // Record a push-ack at the server's own timestamp for every server
        // document written during a full pull when no prior push checkpoint
        // exists (or when the server timestamp is newer than fullPullStartedAt).
        // Because only `docsToWrite` (verifiably received from the server) are
        // acknowledged here — and those acks persist in `_sync_meta` across
        // multi-page cursor resumes and are pruned as soon as the push
        // checkpoint advances — pre-resume server pages are never mistaken for
        // offline local edits, while records created locally before or during
        // the pull remain unacknowledged and are uploaded by the push leg.
        if (isPushableFullPull) {
          for (const d of docsToWrite) {
            if (d?._id != null) {
              pulledServerVersionEntries.push({
                _id: String(d._id),
                updatedAt: typeof d?.[fullPullPushField] === 'string' ? d[fullPullPushField] : '',
              });
            }
          }
          const docsToAck = docsToWrite
            .filter((d: any) =>
              d?._id != null &&
              typeof d?.[fullPullPushField] === 'string' &&
              Boolean(d[fullPullPushField]) &&
              (!initialPrePullPushCp || d[fullPullPushField] > fullPullStartedAt),
            )
            .map((d: any) => ({ _id: String(d._id), updatedAt: String(d[fullPullPushField]) }));
          writePushAckTimestamps(dexieCollection, docsToAck, checkpointPrefix);
        }
        console.debug(`[sync] Full pull page ${pageCount}: ${documents.length} docs into ${dexieCollection} (total: ${totalUpserted}, hasMore=${hasMore}, cursor=${cursorTs?.substring(0,24)} / ${cursorId?.substring(0,8)})`);
        if (dexieCollection === 'entities') {
          console.info(
            `[occupancy-census] Full pull ${dexieCollection} page ${pageCount}: upserted=${totalUpserted} pageDocs=${documents.length} hasMore=${hasMore}`,
          );
        }
        if (pageCount === 1 || pageCount % 10 === 0) {
          syncLog.info(`Full pull ${dexieCollection}: page ${pageCount}, ${totalUpserted} docs so far`, { collection: dexieCollection });
        }
      }

      if (!hasMore) {
        writeLocalCheckpoint(FULLPULL_CURSOR_KEY(dexieCollection, checkpointPrefix), '');
        break;
      }

      if (documents.length === 0) {
        console.warn(`[sync] Full pull stalled: page ${pageCount}, 0 docs, hasMore=${hasMore} — breaking. cursor=${cursorTs}/${cursorId}`);
        fullPullInterruptedError = new Error(`Full pull stalled for ${dexieCollection}: empty page with hasMore=true`);
        break;
      }

      // Signal forward progress for the stuck-sync watchdog: a page was
      // fetched and persisted this iteration, so a long-but-healthy full pull
      // (many pages over minutes) is NOT mistaken for a silent stall.
      onPageProgress?.();

      // Small delay between pages to let the browser breathe (IndexedDB writes, GC, proxy recovery)
      await new Promise(r => setTimeout(r, 200));
    }

    if (pageCount >= MAX_PAGES) {
      console.error(`[sync] Full pull hit MAX_PAGES (${MAX_PAGES}) for ${dexieCollection} — possible cursor bug`);
      fullPullInterruptedError = new Error(`Full pull hit MAX_PAGES for ${dexieCollection}`);
    }

    const cursorStillSaved = !!getCheckpoint(FULLPULL_CURSOR_KEY(dexieCollection, checkpointPrefix));

    // Surface per-doc Dexie write failures BEFORE potentially throwing on
    // transport/interruption errors. The local-write failures are durable
    // (cursor was already advanced past them — re-running the pull won't
    // recover them) so the user must see them even when the pull also
    // failed transport-wise.
    //
    // We log + setSyncError directly here instead of throwing because
    // these are not transport failures — throwing would (a) skip
    // reconciliation, (b) inflate the retry-backoff counter, and (c)
    // overwrite the better diagnostic info that a real transport error
    // would carry.
    reportLocalWriteFailures(dexieCollection, localWriteFailures);

    if (fullPullInterruptedError) {
      // Distinguish "no progress, real failure" from "made progress, will
      // resume" so the sync widget stops lighting up red when a 100k+
      // violation pull simply needs another page after a transient network
      // hiccup.
      //
      // Resumable when BOTH are true:
      //   - the cursor for this collection is still saved (so the next
      //     cycle's pendingFullPull fast-retry path will pick up exactly
      //     where this one stopped); AND
      //   - we wrote at least one doc this session (totalUpserted > 0),
      //     proving forward progress was made.
      //
      // The "stalled" (0 docs + hasMore=true) and MAX_PAGES branches still
      // throw because those indicate real bugs an operator must see.
      //
      // Even when we don't throw, the durable console.warn was already
      // emitted at the point of failure with the page number and reason,
      // so postmortem grep still works.
      const isResumableInterruption = cursorStillSaved && totalUpserted > 0;
      if (!isResumableInterruption) {
        throw fullPullInterruptedError;
      }
      console.info(
        `[sync] Full pull for ${dexieCollection} interrupted at page ${pageCount} ` +
        `after ${totalUpserted} doc(s); cursor saved — next cycle will resume ` +
        `without surfacing an error to the sync widget.`,
      );
      // Fall through into the "paused" branch below so the outer loop's
      // pendingFullPull fast-retry picks up the work shortly.
    }
    const canReconcileFreshFullPull = wasFreshPull && !cursorStillSaved && Array.isArray(firstPageAllIds) && !!table;

    // Only reconcile when docs were actually written in this session (totalUpserted > 0).
    // Guarding on totalUpserted prevents two failure modes:
    // (a) A resumed pull where all docs are already in Dexie (0 new writes) — running
    //     bulkDelete against allIds would wipe valid docs that weren't re-sent this page.
    // (b) A cursor mismatch that returned 0 docs — bulkDelete would empty the table.
    // Large collections (>LARGE_COLLECTION_THRESHOLD) omit allIds intentionally, so
    // pruning against a partial/absent set would corrupt local state.
    if (canReconcileFreshFullPull || (totalUpserted > 0 && pageCount > 0)) {
      if (firstPageAllIds && table) {
        const serverIdSet = new Set(firstPageAllIds);
        const localRows: any[] = await table.toArray();
        // For push-allowed collections, genuinely unpushed locally-created docs
        // will NOT appear in serverIdSet and must be preserved so the push leg
        // uploads them. However, rows that were ALREADY synchronized before
        // this pull (e.g. updatedAt <= initialPrePullPushCp, present in
        // knownServerIds/push_ack, or carrying server migration provenance) and
        // are now absent from `firstPageAllIds` were hard-deleted on the server
        // and must be pruned so a subsequent push cannot resurrect them.
        const isPushable = !!(config.pushAllowedCollections && config.pushAllowedCollections.has(serverCollection));
        const nonServerCandidateIds = isPushable
          ? localRows
              .map((r: any) => String(r?._id ?? ''))
              .filter((id: string) => id && !serverIdSet.has(id) && !pulledDocIds.has(id))
          : [];
        const reconcileAckMap = nonServerCandidateIds.length > 0
          ? await getPushAckTimestamps(dexieCollection, nonServerCandidateIds, checkpointPrefix)
          : new Map<string, string>();
        const stale = localRows
          .filter((row: any) => {
            const id = row?._id;
            if (!id || serverIdSet.has(id) || pulledDocIds.has(String(id))) return false;
            if (isPushable) {
              return isPreviouslySyncedServerRow(
                row,
                fullPullPushField,
                initialPrePullPushCp,
                knownServerIdsBeforePull,
                reconcileAckMap,
                unackedPushIdsBeforePull,
                preservedPendingDeleteIds,
                preservedNewerLocalIds,
              );
            }
            // Protect in-flight optimistic rows in server-authoritative
            // collections until their server row exists.
            if (isProtectedOptimisticRow(row)) return false;
            return true;
          })
          .map((row: any) => row._id);
        if (stale.length > 0) {
          await table.bulkDelete(stale);
        }
      }
    }

    // Check if the full pull loop actually ran to completion (last page had hasMore=false)
    if (!cursorStillSaved && (totalUpserted > 0 || totalServerDocs === 0 || !table || canReconcileFreshFullPull)) {
      // Full pull completed — mark collection as synced so next cycle enters delta mode.
      // Guard: only write PULL_KEY if we actually persisted docs (totalUpserted > 0)
      // or if the table handle is missing (empty collection on server — legitimate 0).
      // This prevents a broken table handle from poisoning the checkpoint and
      // permanently trapping the collection in delta mode with an empty Dexie table.

      // ── Post-pull stale-doc detection ────────────────────────────────────────
      // Large collections (>LARGE_COLLECTION_THRESHOLD) never receive allIds, so
      // the normal reconciliation pass is skipped. If a re-import replaced records
      // with new MongoDB _ids (hard-delete + re-insert, no soft-delete tombstones),
      // old docs stay in Dexie because bulkPut only upserts by _id — the old _ids
      // are never touched. This manifests as duplicate rows in the grid (same
      // parcelNumber, different _id: one with enriched data, one with old data).
      //
      // Guards:
      //   wasFreshPull — only run on a completely fresh pull (not resumed mid-way).
      //     Resumed pulls only write a fraction of docs in this session, so
      //     dexieCount would always exceed totalUpserted — a false positive.
      //   dexieCount > totalUpserted * 1.1 — require >10% excess to avoid
      //     false-triggering on a few pending local creates in push-allowed
      //     collections (cases, contacts). A re-import typically leaves 30-50% excess.
      //
      // Recovery: wipe the table, then set FULLPULL_CURSOR_KEY to '{}' so the
      // outer loop immediately retries (pendingFullPull is truthy) but syncCollection
      // discards the empty cursor and restarts the full pull cleanly from _id > null.
      if (table && wasFreshPull && totalUpserted > 0) {
        try {
          const dexieCount = await table.count();
          if (shouldReconcileStaleFreshPull(wasFreshPull, totalUpserted, dexieCount)) {
            // Fix 3b: Veto the stale-wipe when the excess includes un-pushed
            // local edits/creations. For push-allowed collections, distinguish
            // genuinely unpushed local rows from obsolete previously-synced
            // server rows using `isPreviouslySyncedServerRow`.
            const isPushable = !!(config.pushAllowedCollections && config.pushAllowedCollections.has(serverCollection));
            if (isPushable) {
              const pushCpVal = await getPushCheckpoint(dexieCollection, checkpointPrefix);
              const pf = PUSH_TIMESTAMP_FIELD[dexieCollection] ?? 'updatedAt';
              let pendingCount = pushCpVal
                ? await table.where(pf).above(pushCpVal).count()
                : 0;
              if (pendingCount === 0) {
                const localRows: any[] = await table.toArray();
                const excessRows = localRows.filter((r: any) => r?._id && !pulledDocIds.has(String(r._id)));
                const excessIds = excessRows.map((r: any) => String(r._id));
                const excessAckMap = excessIds.length > 0
                  ? await getPushAckTimestamps(dexieCollection, excessIds, checkpointPrefix)
                  : new Map<string, string>();
                const unpushedExcess = excessRows.filter(
                  (r: any) => !isPreviouslySyncedServerRow(
                    r,
                    pf,
                    pushCpVal || initialPrePullPushCp,
                    knownServerIdsBeforePull,
                    excessAckMap,
                    unackedPushIdsBeforePull,
                    preservedPendingDeleteIds,
                    preservedNewerLocalIds,
                  ),
                );
                const staleExcessIds = excessRows
                  .filter((r: any) => isPreviouslySyncedServerRow(
                    r,
                    pf,
                    pushCpVal || initialPrePullPushCp,
                    knownServerIdsBeforePull,
                    excessAckMap,
                    unackedPushIdsBeforePull,
                    preservedPendingDeleteIds,
                    preservedNewerLocalIds,
                  ))
                  .map((r: any) => r._id);
                pendingCount = unpushedExcess.length + preservedPendingDeleteIds.size + preservedNewerLocalIds.size;
                if (pendingCount > 0 && staleExcessIds.length > 0) {
                  await table.bulkDelete(staleExcessIds);
                }
              }
              if (pendingCount > 0) {
                console.warn(
                  `[sync] Post-pull stale detection vetoed for ${dexieCollection}: ` +
                  `${pendingCount} un-pushed local edit(s) present — preserving table`,
                );
                // Skip the wipe — fall through to normal checkpoint advancement.
              } else {
                console.warn(
                  `[sync] Post-pull stale detection: Dexie has ${dexieCount} docs but ` +
                  `server sent ${totalUpserted} for ${dexieCollection} — wiping table and restarting ` +
                  `(common causes: server re-import, account-scope change, or environment switch)`,
                );
                await table.clear();
                writePullCheckpointDurable(PULL_KEY(dexieCollection, checkpointPrefix), '');
                writePullCheckpointDurable(PULL_ID_KEY(dexieCollection, checkpointPrefix), '');
                writePullCheckpointDurable(PULL_ID_TYPE_KEY(dexieCollection, checkpointPrefix), '');
                writeLocalCheckpoint(FULLPULL_CURSOR_KEY(dexieCollection, checkpointPrefix), '{}');
                return;
              }
            } else {
              console.warn(
                `[sync] Post-pull stale detection: Dexie has ${dexieCount} docs but ` +
                `server sent ${totalUpserted} for ${dexieCollection} — wiping table and restarting ` +
                `(common causes: server re-import, account-scope change, or environment switch)`,
              );
              await table.clear();
              writePullCheckpointDurable(PULL_KEY(dexieCollection, checkpointPrefix), '');
              writePullCheckpointDurable(PULL_ID_KEY(dexieCollection, checkpointPrefix), '');
              writePullCheckpointDurable(PULL_ID_TYPE_KEY(dexieCollection, checkpointPrefix), '');
              writeLocalCheckpoint(FULLPULL_CURSOR_KEY(dexieCollection, checkpointPrefix), '{}');
              return;
            }
          }
        } catch (countErr: any) {
          console.debug(`[sync] Post-pull stale count check failed for ${dexieCollection}: ${countErr.message}`);
        }
      }

      if (isCancelled()) return;

      writePullCheckpointDurable(PULL_KEY(dexieCollection, checkpointPrefix), serverTimeAnchor || fullPullStartedAt);
      lastPull = serverTimeAnchor || fullPullStartedAt;
      writePullCheckpointDurable(PULL_ID_KEY(dexieCollection, checkpointPrefix), '');
      writePullCheckpointDurable(PULL_ID_TYPE_KEY(dexieCollection, checkpointPrefix), '');

      // Fix 3: Do NOT blindly advance the push checkpoint to NOW when there
      // are un-pushed local edits. A bulk inspection made offline (updatedAt
      // after the last push checkpoint but before this pull) would fall
      // outside `above(NOW)` and never reach the server — stranding completed
      // inspections on the device. Only advance to NOW when no pending work
      // exists; otherwise preserve the pre-pull checkpoint so the push leg
      // picks up the dirty docs.
      const prePullPushCp = await getPushCheckpoint(dexieCollection, checkpointPrefix);
      let hasPendingLocalEdits = false;
      let noCheckpointPushAnchor = fullPullStartedAt;
      if (table) {
        try {
          if (prePullPushCp) {
            hasPendingLocalEdits =
              (await table.where(fullPullPushField).above(prePullPushCp).count()) > 0;
          } else {
            // No prior push checkpoint (fresh install / reset). Local edits
            // carry client-clock timestamps, so bound the scan with the pull's
            // client-clock start — but never treat a server document as a local
            // edit by comparing its server-side timestamp with this device's
            // clock: rows this pull wrote with a newer timestamp were push-acked
            // at their server timestamp above and are excluded here exactly as
            // the push leg excludes them. Newer local docs the pull preserved
            // are pending even when they predate the pull's start.
            const candidates = await table.where(fullPullPushField).above(fullPullStartedAt).toArray();
            const olderCandidateIds = (
              await table.where(fullPullPushField).belowOrEqual(fullPullStartedAt).primaryKeys()
            )
              .map((id: any) => String(id))
              .filter(
                (id: string) => !pulledDocIds.has(id) || preservedPendingDeleteIds.has(id),
              );
            const olderCandidates = olderCandidateIds.length
              ? (await table.bulkGet(olderCandidateIds)).filter(Boolean)
              : [];
            const ackMap = await getPushAckTimestamps(
              dexieCollection,
              candidates.map((doc: any) => String(doc?._id)).concat(olderCandidateIds),
              checkpointPrefix,
            );
            const unackedCount = candidates.filter((doc: any) => isUnackedLocalDoc(doc, fullPullPushField, ackMap)).length;
            let oldestUnackedLocalTs = '';
            for (const doc of olderCandidates) {
              const docId = String(doc?._id);
              if (pulledDocIds.has(docId) && !preservedPendingDeleteIds.has(docId)) continue;
              if (
                isPreviouslySyncedServerRow(
                  doc,
                  fullPullPushField,
                  initialPrePullPushCp,
                  knownServerIdsBeforePull,
                  ackMap,
                  unackedPushIdsBeforePull,
                  preservedPendingDeleteIds,
                  preservedNewerLocalIds,
                )
              ) {
                continue;
              }
              const ts = String(doc?.[fullPullPushField] ?? '');
              if (ts && (!oldestUnackedLocalTs || ts < oldestUnackedLocalTs)) oldestUnackedLocalTs = ts;
            }
            hasPendingLocalEdits = unackedCount > 0 || !!oldestUnackedLocalTs || !!minPreservedLocalPushTs;
            if (oldestUnackedLocalTs) {
              const beforeOldest = isoJustBefore(oldestUnackedLocalTs);
              if (beforeOldest < noCheckpointPushAnchor) noCheckpointPushAnchor = beforeOldest;
            }
            if (minPreservedLocalPushTs) {
              const beforePreserved = isoJustBefore(minPreservedLocalPushTs);
              if (beforePreserved < noCheckpointPushAnchor) noCheckpointPushAnchor = beforePreserved;
            }
          }
        } catch { /* conservatively assume no pending */ }
      }
      if (!hasPendingLocalEdits) {
        const completedAt = new Date().toISOString();
        writePushCheckpoint(dexieCollection, completedAt, checkpointPrefix);
        await pruneSupersededPushAcks(dexieCollection, completedAt, checkpointPrefix);
      } else if (!prePullPushCp) {
        // Anchor at the pull's start (or just before the oldest preserved
        // offline edit) so the push leg's above(cp) scan picks up the pending
        // edits instead of the entire table; pulled server rows were push-acked
        // per page above and are filtered out by `isUnackedLocalDoc` in the
        // push leg.
        writePushCheckpoint(dexieCollection, noCheckpointPushAnchor, checkpointPrefix);
      }
      // else: preserve prePullPushCp so the push section finds the pending edits.
      if (isPushableFullPull && !isCancelled()) {
        recordKnownServerVersions(dexieCollection, pulledServerVersionEntries, checkpointPrefix, wasFreshPull);
      }
      syncLog.success(`Full pull complete for ${dexieCollection}: ${totalUpserted} doc(s) synced in ${pageCount} page(s)`, { collection: dexieCollection });
      // A full pull that persisted docs proves the table is populated again —
      // re-arm the empty-delta recovery so a LATER genuine IndexedDB wipe
      // (localStorage surviving) can recover once more.
      if (totalUpserted > 0) {
        emptyDeltaRecovered.delete(pullStateKey(config.departmentId, checkpointPrefix, dexieCollection));
      }
      
    } else if (!cursorStillSaved && totalUpserted === 0 && table && localWriteFailures.length === 0) {
      // Server sent documents but nothing was written to Dexie — table handle may be
      // broken. Do NOT set PULL_KEY so next cycle retries with a fresh table handle.
      //
      // Skip this throw when localWriteFailures > 0: in that case we already
      // know exactly why nothing was written (per-doc schema rejects, etc.)
      // and have surfaced a LocalWriteError to the UI. Re-throwing here would
      // (a) overwrite that diagnostic, (b) trigger transport-style retry+
      // backoff for a Dexie validation issue retry can't fix, and (c) leave
      // the collection trapped in the full-pull retry loop forever.
      throw new Error(`Full pull wrote 0/${totalServerDocs} server document(s) for ${dexieCollection}`);
    } else if (!cursorStillSaved && totalUpserted === 0 && table && localWriteFailures.length > 0) {
      if (isCancelled()) return;
      // All server docs rejected locally. The LocalWriteError was already
      // surfaced above. Set the pull checkpoint so we don't keep refetching
      // docs we know won't land — the next cycle enters delta mode and the
      // operator (with the console.error trail) can investigate the schema
      // mismatch that caused the rejections.
      writePullCheckpointDurable(PULL_KEY(dexieCollection, checkpointPrefix), serverTimeAnchor || fullPullStartedAt);
      lastPull = serverTimeAnchor || fullPullStartedAt;
      writePullCheckpointDurable(PULL_ID_KEY(dexieCollection, checkpointPrefix), '');
      writePullCheckpointDurable(PULL_ID_TYPE_KEY(dexieCollection, checkpointPrefix), '');
      const completedAt = new Date().toISOString();
      writePushCheckpoint(dexieCollection, completedAt, checkpointPrefix);
    } else {
      // Full pull interrupted — cursor is saved for resume on next cycle.
      // PULL_KEY intentionally NOT written so next cycle re-enters full pull path.
      // Return immediately so we do not enter the push check mid-full-pull.
      if (isPushableFullPull && pulledServerVersionEntries.length > 0 && !isCancelled()) {
        recordKnownServerVersions(dexieCollection, pulledServerVersionEntries, checkpointPrefix, false);
      }
      console.debug(`[sync] Full pull paused after ${totalUpserted} docs (${pageCount} pages) for ${dexieCollection} — will resume`);
      return;
    }
    // After full pull: push is normally skipped because data came FROM server.
    // But if there are locally-deleted docs that were preserved during the pull
    // (pending deletes that haven't reached the server), push must still run.
    // The push checkpoint was set to maxUpdatedAt of pulled docs, so only
    // locally-modified docs (newer than the pull) will be sent.
    if (config.pushAllowedCollections && config.pushAllowedCollections.has(serverCollection) && table) {
      try {
        const pf = PUSH_TIMESTAMP_FIELD[dexieCollection] ?? 'updatedAt';
        const pushCp = await getPushCheckpoint(dexieCollection, checkpointPrefix);
        let pendingCount = pushCp
          ? await table.where(pf).above(pushCp).count()
          : 0; // no checkpoint = no pending changes (checkpoint was just set above)

        // Also count genuinely unpushed locally-created docs that exist in
        // Dexie but were NOT on the server (excluding previously synced rows
        // that were deleted on the server).
        if (pendingCount === 0 && !initialPrePullPushCp && firstPageAllIds && firstPageAllIds.length > 0) {
          const serverIdSet = new Set(firstPageAllIds);
          const allLocalIds: string[] = await table.toCollection().primaryKeys() as any;
          const candidateIds = allLocalIds
            .map((id: any) => String(id ?? ''))
            .filter((id: string) => id && !serverIdSet.has(id) && !pulledDocIds.has(id));
          if (candidateIds.length > 0) {
            const candidateDocs = (await table.bulkGet(candidateIds)).filter(Boolean);
            const candidateAckMap = await getPushAckTimestamps(dexieCollection, candidateIds, checkpointPrefix);
            const unpushedDocs = candidateDocs.filter(
              (doc: any) => !isPreviouslySyncedServerRow(
                doc,
                pf,
                initialPrePullPushCp,
                knownServerIdsBeforePull,
                candidateAckMap,
                unackedPushIdsBeforePull,
                preservedPendingDeleteIds,
                preservedNewerLocalIds,
              ),
            );
            if (unpushedDocs.length > 0) {
              pendingCount = unpushedDocs.length;
              writePushCheckpoint(dexieCollection, '', checkpointPrefix);
              console.debug(`[sync] Full pull: ${unpushedDocs.length} local-only doc(s) in ${dexieCollection} — resetting push checkpoint to flush them`);
            }
          }
        }

        if (pendingCount > 0) {
          console.debug(`[sync] Full pull complete but ${pendingCount} pending local change(s) for ${dexieCollection} — proceeding to push`);
          // Fall through to push section below instead of returning
        } else {
          return; // No pending changes — safe to skip push
        }
      } catch {
        return; // Can't check — safe to skip push
      }
    } else {
      return; // Not pushable — skip push
    }
  }

  // ── DELTA PULL ─────────────────────────────────────────────────────────
  if (isFirstSync || isWriteOnlyPush) {
    // Skip delta download (full pull already ran above, or collection is
    // write-only); fall through directly to push.
  } else {
  let deltaSince = lastPull;
  let deltaSinceId: string | undefined = await getPullCheckpointDurable(PULL_ID_KEY(dexieCollection, checkpointPrefix)) || undefined;
  const savedDeltaSinceIdType = await getPullCheckpointDurable(PULL_ID_TYPE_KEY(dexieCollection, checkpointPrefix));
  let deltaSinceIdType: 'string' | 'objectId' | undefined = parseCursorIdType(savedDeltaSinceIdType, deltaSinceId);
  let totalDeltaDocs = 0;
  let lastDeltaDocuments: any[] = [];
  let deltaPageCount = 0;
  const MAX_DELTA_PAGES = 10_000;

  if (!deltaSince) {
    writePullCheckpointDurable(PULL_KEY(dexieCollection, checkpointPrefix), '');
    writePullCheckpointDurable(PULL_ID_KEY(dexieCollection, checkpointPrefix), '');
    writePullCheckpointDurable(PULL_ID_TYPE_KEY(dexieCollection, checkpointPrefix), '');
    writeLocalCheckpoint(FULLPULL_CURSOR_KEY(dexieCollection, checkpointPrefix), '');
    return;
  }

  while (deltaPageCount < MAX_DELTA_PAGES) {
    deltaPageCount++;
    const prevDeltaSince = deltaSince;
    const prevDeltaSinceId = deltaSinceId;
    const prevDeltaSinceIdType = deltaSinceIdType;

    const pullBodyObj: Record<string, any> = {
      collection: serverCollection,
      since: deltaSince,
      sinceId: deltaSinceId,
      ...(deltaSinceIdType ? { sinceIdType: deltaSinceIdType } : {}),
      limit: 500,
    };
    // Account-scoped filtering (mobile-api only)
    if (config.inspectionAccountIds && config.inspectionAccountIds.length > 0) {
      pullBodyObj.inspectionAccountIds = config.inspectionAccountIds;
    }
    const pullBody = JSON.stringify(pullBodyObj);
    let documents: any[];
    let checkpoint: CheckpointType & { id?: string; idType?: 'string' | 'objectId' };
    let hasMore: boolean | undefined;

    const wsClient = getWsClient();
    if (wsClient?.getStatus().state === 'connected') {
      const wsPull = await pullViaWebSocket(
        wsClient,
        serverCollection,
        { updatedAt: deltaSince, id: deltaSinceId, idType: deltaSinceIdType },
        {
          limit: 500,
          sinceIdType: deltaSinceIdType,
          inspectionAccountIds: config.inspectionAccountIds,
        },
      );
      if (!wsPull) {
        throw new Error(`Pull ${dexieCollection} websocket unavailable`);
      }
      documents = wsPull.documents;
      checkpoint = wsPull.checkpoint;
      hasMore = wsPull.hasMore;
    } else {
      // HTTP delta-pull fallback (WebSocket disconnected). Gated by the
      // pull semaphore for the same reason as the full-pull page fetch
      // above — see PullSemaphore @contract.
      const data = await pullSemaphore.withSlot(async () => {
        let headers = await buildHeaders();
        let pullRes = await timedFetch(`${base}${routePrefix}/pull`, { method: 'POST', headers, body: pullBody });

        if (pullRes.status === 401 || pullRes.status === 403) {
          console.log(`[sync] ${dexieCollection} pull got ${pullRes.status} — refreshing token and retrying`);
          await new Promise(r => setTimeout(r, 1_000));
          headers = await buildHeaders();
          pullRes = await timedFetch(`${base}${routePrefix}/pull`, { method: 'POST', headers, body: pullBody });
        }

        if (!pullRes.ok) {
          throw await httpSyncFailure(pullRes, `Pull ${dexieCollection} `);
        }

        return await pullRes.json() as {
          documents: any[];
          checkpoint: CheckpointType & { id?: string; idType?: 'string' | 'objectId' };
          hasMore?: boolean;
        };
      });
      documents = data.documents;
      checkpoint = data.checkpoint;
      hasMore = data.hasMore;
    }

    if (hasMore && documents.length > 0 && (!checkpoint?.updatedAt || !checkpoint?.id)) {
      throw new Error(
        `Pull ${dexieCollection} returned hasMore=true without a complete compound checkpoint; cannot continue delta pagination safely`,
      );
    }

    lastDeltaDocuments = documents;
    totalDeltaDocs += documents.length;

    if (documents.length > 0 && table) {
      // Delta pull: upsert updates, delete tombstones. Only upserts persist
      // server content, so only they pass the inbound gate — a tombstone just
      // deletes by _id, and deleting a quarantined doc on the server must
      // still remove it locally.
      const tombstones = coerceDocIds(documents.filter((d: any) => d._deleted === true));
      const inbound = partitionInboundPulledDocuments(
        serverCollection,
        documents.filter((d: any) => d._deleted !== true),
      );
      reportLocalWriteFailures(dexieCollection, inbound.rejected);
      const upserts = coerceDocIds(inbound.accepted);
      if (upserts.length > 0) {
        // Bug 4 fix: For pushable collections, only overwrite Dexie if the server
        // version is newer-or-equal to what is locally stored. This prevents the
        // SSE self-echo race: user edits → push → SSE fires back → delta pull
        // overwrites a second edit made between push and echo.
        // For read-only (server-authoritative) collections, always overwrite.
        //
        // Bug 5 fix: Additionally, if the local doc was already pushed (its
        // updatedAt <= pushCheckpoint), it has no pending local changes and the
        // server version should be authoritative. Without this, a stale local
        // doc with a bumped timestamp permanently blocks server updates from
        // other clients and can push stale data back to the server.
        if (config.pushAllowedCollections?.has(serverCollection)) {
          const docIds = upserts.map((d: any) => d._id).filter(Boolean);
          const localDocs = await table.bulkGet(docIds);
          const localMap = new Map(
            localDocs
              .filter((d: any) => d != null)
              .map((d: any) => [String(d._id), d])
          );
          const pushCp = await getPushCheckpoint(dexieCollection, checkpointPrefix);
          const toWrite = upserts.filter((serverDoc: any): boolean => {
            const local = localMap.get(String(serverDoc._id)) as any;
            if (!local) return true; // new doc — always write
            if (local._deleted === true) return false; // never overwrite a pending tombstone
            const serverTs = (serverDoc.updatedAt as string) || '';
            const localTs = (local.updatedAt as string) || '';
            // A fresh legacy field-response apply must replace answers a
            // conductor previously saved from template defaults.
            const migrationAppliedAt = String((serverDoc as { legacyMigrationAppliedAt?: unknown }).legacyMigrationAppliedAt ?? '');
            const acceptedMigrationAt = String((local as { legacyMigrationAppliedAt?: unknown }).legacyMigrationAppliedAt ?? '');
            if (migrationAppliedAt && migrationAppliedAt !== acceptedMigrationAt) return true;
            // Server is newer-or-equal — always accept
            if (serverTs >= localTs) return true;
            // Local is newer: only protect if it has PENDING local changes
            // (updatedAt > push checkpoint). If already pushed, the server
            // version from another client should win.
            if (pushCp && localTs <= pushCp) return true;
            return false;
          });
          if (toWrite.length > 0) {
            // Defense-in-depth: strip any __optimistic/fetchedAt the server
            // may still echo back (e.g. a pre-fix push polluted the server
            // doc and the worker hasn't run its $unset yet). bulkPut is a
            // full replace, so removing the field here clears it locally.
            for (const doc of toWrite) {
              if (doc && typeof doc === 'object') {
                delete doc.__optimistic;
                delete doc.fetchedAt;
              }
            }
            if (isCancelled()) return;
            await table.bulkPut(stampPulledReceipts(dexieCollection, toWrite));
            const deltaPushField = PUSH_TIMESTAMP_FIELD[dexieCollection] ?? 'updatedAt';
            recordKnownServerVersions(
              dexieCollection,
              toWrite
                .filter((d: any) => d?._id != null)
                .map((d: any) => ({
                  _id: String(d._id),
                  updatedAt: typeof d?.[deltaPushField] === 'string' ? d[deltaPushField] : '',
                })),
              checkpointPrefix,
            );
            const maxWrittenAt = toWrite.reduce((max: string, doc: any) => {
              const docTs = (doc.updatedAt as string) || '';
              return docTs > max ? docTs : max;
            }, '');
            await writeAdvancePushCheckpoint(dexieCollection, maxWrittenAt, checkpointPrefix);
          }
        } else {
          let toWrite = upserts;
          if (serverCollection === 'geoCacheFiles') {
            const localDocs = await Promise.all(
              upserts.map((doc: any) => table.get(doc._id).catch(() => null)),
            );
            toWrite = upserts.filter((serverDoc: any, index: number) =>
              shouldAcceptReadOnlyServerUpsert(serverCollection, localDocs[index], serverDoc),
            );
          }
          if (isCancelled()) return;
          if (toWrite.length > 0) await table.bulkPut(stampPulledReceipts(dexieCollection, toWrite));
        }
      }
      if (tombstones.length > 0) {
        // Preserve optimistic rows from tombstone deletion. An optimistic row
        // is a locally-created doc (e.g. a geoCacheFiles upload placeholder)
        // that hasn't been fully acknowledged by the server yet. A tombstone
        // for the same _id is likely a stale soft-delete that the push hasn't
        // yet overwritten (the server-side executeSyncPush clears _deleted on
        // overwrite, but the delta pull may race ahead of the push). Deleting
        // the optimistic row here makes the grid row vanish — the exact bug
        // this guard prevents.
        const tombIds = tombstones.map((t: any) => t._id).filter(Boolean);
        // Use individual get() calls instead of bulkGet() — not all TableLike
        // implementations (e.g. SecureTable) expose bulkGet. Tombstone counts
        // are typically small (1-5 per delta page), so the overhead is minimal.
        const localTombDocs = await Promise.all(
          tombIds.map((id: any) => table.get(id).catch(() => null)),
        );
        // Only NON-TERMINAL optimistic rows are protected. A failed optimistic
        // row must accept the tombstone — otherwise a county deleted by another
        // admin stays in Dexie forever (the local row never gets `_deleted`, so
        // listCacheFiles keeps rendering it). See isProtectedOptimisticRow.
        const optimisticIds = new Set(
          localTombDocs
            .filter((d: any) => d != null && isProtectedOptimisticRow(d))
            .map((d: any) => String((d as any)._id)),
        );
        const idsToDelete = tombIds.filter((id: any) => !optimisticIds.has(String(id)));
        if (idsToDelete.length > 0) {
          await table.bulkDelete(idsToDelete);
        }
        const preserved = tombIds.length - idsToDelete.length;
        debugLog(
          `[sync] Delta reconcile: deleted ${idsToDelete.length} tombstone(s) from ${dexieCollection}` +
          (preserved > 0 ? ` (preserved ${preserved} optimistic)` : ''),
        );
      }
    }

    if (isCancelled()) return;

    const nextIdType = parseCursorIdType(checkpoint?.idType, checkpoint?.id);
    if (checkpoint?.updatedAt) {
      writePullCheckpointDurable(PULL_KEY(dexieCollection, checkpointPrefix), checkpoint.updatedAt);
      writePullCheckpointDurable(PULL_ID_KEY(dexieCollection, checkpointPrefix), checkpoint.id || '');
      writePullCheckpointDurable(PULL_ID_TYPE_KEY(dexieCollection, checkpointPrefix), nextIdType || '');
      deltaSince = checkpoint.updatedAt;
      deltaSinceId = checkpoint.id;
      deltaSinceIdType = nextIdType;
    }

    if (!hasMore) {
      // If a terminal delta page returned checkpoint.id without idType (e.g.
      // custom route or legacy response without BSON type metadata), do not
      // persist an untyped sinceId across cycles — an untyped sinceId forces
      // the next delta pull into STRING_THEN_OBJECTID_CURSOR and re-pulls
      // every ObjectId document at that timestamp on every poll.
      if (deltaSinceId && !deltaSinceIdType) {
        writePullCheckpointDurable(PULL_ID_KEY(dexieCollection, checkpointPrefix), '');
        deltaSinceId = undefined;
      }
      break;
    }

    if (documents.length === 0) {
      console.warn(`[sync] Delta pull stalled: page ${deltaPageCount}, 0 docs, hasMore=${hasMore} — breaking. cursor=${deltaSince}/${deltaSinceId}`);
      break;
    }

    const cursorDidNotAdvance =
      deltaSince === prevDeltaSince &&
      (deltaSinceId || '') === (prevDeltaSinceId || '') &&
      (deltaSinceIdType || '') === (prevDeltaSinceIdType || '');
    if (cursorDidNotAdvance) {
      console.warn(
        `[sync] Delta pull cursor did not advance on page ${deltaPageCount} for ${dexieCollection} ` +
        `(since=${deltaSince}, sinceId=${deltaSinceId ?? ''}, sinceIdType=${deltaSinceIdType ?? ''}) — breaking to prevent infinite loop`,
      );
      writePullCheckpointDurable(PULL_ID_KEY(dexieCollection, checkpointPrefix), '');
      writePullCheckpointDurable(PULL_ID_TYPE_KEY(dexieCollection, checkpointPrefix), '');
      break;
    }

    onPageProgress?.();
    await new Promise(r => setTimeout(r, 50));
  }

  if (deltaPageCount >= MAX_DELTA_PAGES) {
    console.error(`[sync] Delta pull hit MAX_DELTA_PAGES (${MAX_DELTA_PAGES}) for ${dexieCollection} — possible cursor bug`);
  }

  if (totalDeltaDocs > 0) {
    console.debug(`[sync] Pulled ${totalDeltaDocs} docs into ${dexieCollection}`);
    syncLog.info(`Pulled ${totalDeltaDocs} updated doc(s) for ${dexieCollection}`, { collection: dexieCollection });
  }

  // ── DIVERGENCE CHECK ───────────────────────────────────────────
  // Guard against checkpoint/data desync: if the delta pull returned 0 new
  // docs but the local Dexie table is completely empty, the checkpoint is
  // stale (IndexedDB was likely cleared while localStorage survived).
  // Clear the pull checkpoint so the NEXT sync cycle triggers a full pull
  // and repopulates Dexie from the server.
  //
  // IMPORTANT: Skip the divergence check entirely if there are pending local
  // changes waiting to be pushed. A local write (e.g. soft-delete) changes
  // the local count before the server learns about it. Without this guard,
  // the count mismatch triggers a false divergence → full pull → overwrites
  // the un-pushed local change, causing deleted items to reappear.
  if (table) {
    try {
      const localCount = await table.count();
      // The `emptyDeltaRecovered` key bounds this recovery to once per
      // session per collection. A real IndexedDB wipe still self-heals, but
      // a legitimately-empty server collection no longer churns full pull →
      // empty delta → checkpoint clear → full pull forever — the checkpoint
      // written by the recovery full pull is retained.
      const emptyDeltaKey = pullStateKey(config.departmentId, checkpointPrefix, dexieCollection);
      if (
        shouldRecoverFromEmptyDelta(lastDeltaDocuments.length, localCount) &&
        !emptyDeltaRecovered.has(emptyDeltaKey)
      ) {
        if (isCancelled()) return;
        emptyDeltaRecovered.add(emptyDeltaKey);
        writePullCheckpointDurable(PULL_KEY(dexieCollection, checkpointPrefix), '');
        writePullCheckpointDurable(PULL_ID_KEY(dexieCollection, checkpointPrefix), '');
        writePullCheckpointDurable(PULL_ID_TYPE_KEY(dexieCollection, checkpointPrefix), '');
        writePushCheckpoint(dexieCollection, '', checkpointPrefix);
        writeLocalCheckpoint(FULLPULL_CURSOR_KEY(dexieCollection, checkpointPrefix), '');
        // Wake this collection's loop immediately so it doesn't sleep —
        // the next iteration will see no checkpoint and run a full pull.
        setTimeout(() => wakeLocalWrite(dexieCollection), 50);
        return;
      }
    } catch {
      // count() failed — table handle may be broken; skip empty-table recovery
    }
  }

  } // write-only audit_logs skip delta pull

  } catch (pullErr: any) {
    // Surface EVERY pull failure in the Activity Log — including read-only
    // collections that re-throw below — so a failing delta pull is visible
    // to operators instead of only appearing as console noise. Full-pull
    // page-fetch failures already logged themselves (marked __syncLogged);
    // skip those to avoid a duplicate entry for the same failure.
    // Never log headers, tokens, or payloads — collection + message only.
    if (!(pullErr as any)?.__syncLogged) {
      syncLog.error(`Pull failed for ${dexieCollection}: ${pullErr.message}`, { collection: dexieCollection, detail: pullErr.message });
    }
    // For non-pushable collections, re-throw immediately (original behaviour).
    if (!isPushableCollection) {
      throw pullErr;
    }
    // For pushable collections, stash the error and proceed to push.
    pullError = pullErr;
    console.warn(
      `[sync] ${dexieCollection} pull failed (${pullErr.message}) — proceeding to push`,
    );
  } // ── end pull try-block ──────────────────────────────────────────

  // ── PUSH: send local changes to server ──────────────────────────
  if (!table) {
    if (pullError) throw pullError;
    return;
  }

  // Skip push for server-authoritative (read-only) collections.
  // If pushAllowedCollections is provided and this server collection is not in it,
  // skip push to avoid infinite 400 retry loops for reference data.
  if (config.pushAllowedCollections && !config.pushAllowedCollections.has(serverCollection)) {
    return;
  }

  const lastPush = await getPushCheckpoint(dexieCollection, checkpointPrefix);
  const pushField = PUSH_TIMESTAMP_FIELD[dexieCollection] ?? 'updatedAt';
  let localDocs: any[];
  try {
    localDocs = lastPush
      ? await table.where(pushField).above(lastPush).toArray()
      : await table.toArray();
  } catch {
    localDocs = await table.toArray();
  }

  if (localDocs.length > 0) {
    if (dexieCollection === 'import_runs') {
      localDocs = localDocs.filter((doc: any) => doc?._provisionalImportRun !== true);
    }
    const docIds = localDocs.map((doc: any) => String(doc._id));
    const pushAckMap = await getPushAckTimestamps(dexieCollection, docIds, checkpointPrefix);
    if (pushAckMap.size > 0) {
      // Keep (push) the doc when it has never been acked, has no local timestamp,
      // or its timestamp differs from the ack (precision-insensitive compare).
      localDocs = localDocs.filter((doc: any) => isUnackedLocalDoc(doc, pushField, pushAckMap));
    }
  }

  // Self-heal a stale pending-push counter. `localDocs` now holds the true set
  // of un-acked dirty docs for this collection. If it is empty, any pending
  // count left over from a prior interrupted push is stale — e.g. a transient
  // network drop threw mid-push, and the catch handler intentionally preserves
  // the pending count so queued work isn't forgotten. Once those docs are
  // satisfied on a later cycle, nothing else resets the counter, so it would
  // sit at a phantom value (e.g. "1,500 pending") until a full page reload
  // rebuilds the in-memory store. Clearing here makes the widget converge to
  // reality on the next sync cycle without a reload.
  if (localDocs.length === 0) {
    clearCollectionPushState(dexieCollection);
  }

  if (localDocs.length > 0) {
    const seen = new Set<string>();
    const uniqueDocs = localDocs.filter((doc: any) => {
      const id = String(doc._id);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    // Surface pending push count + pushing flag PER COLLECTION so the
    // global aggregator can sum across all collections in flight. Earlier
    // versions used scalar setters that overwrote each other across the
    // N parallel `syncCollection()` invocations. See `setCollectionPushState`
    // contract in syncStatusStore.ts.
    setCollectionPushState(dexieCollection, { pending: uniqueDocs.length, pushing: true });
    debugLog(`[sync] ⬆ Pushing ${uniqueDocs.length} doc(s) for ${dexieCollection}`);
    syncLog.info(`Pushing ${uniqueDocs.length} doc(s) for ${dexieCollection} to server`, { collection: dexieCollection });

    // Batched push to prevent 413 Payload Too Large
    const allConflicts: any[] = [];
    let batchSize = PUSH_BATCH_SIZE;
    let batchIndex = 0;
    const totalBatches = Math.ceil(uniqueDocs.length / batchSize);

    try {
      while (batchIndex < uniqueDocs.length) {
        const batch = uniqueDocs.slice(batchIndex, batchIndex + batchSize);
        const changes = batch.map((doc: any) => ({
          assumedMasterState: null,
          // Strip client-only fields (__optimistic, fetchedAt) so they never
          // pollute MongoDB. The server has no counterpart for these markers;
          // pushing them would make the optimistic-row tombstone guard protect
          // a row forever (the worker's completion write does not $unset them).
          newDocumentState: stripClientOnlyFields(doc),
        }));
        let conflicts: any[] = [];
        // Fix 2: server-confirmed acked list. When the server returns `acked`
        // (an array of { _id, updatedAt }), only those docs are confirmed
        // written. Legacy servers omit `acked` — fall back to all non-
        // conflicted docs in the batch (previous behavior).
        let ackedList: Array<{ _id: string; updatedAt?: string }> | null = null;
        let pushedViaWs = false;
        const wsClient = getWsClient();
        if (wsClient?.getStatus().state === 'connected') {
          // WebSocket push. `pushViaWebSocket` rethrows the underlying
          // transport error; wrap it as PushSyncError so the retry loop
          // recognises this as a push failure (and not, e.g., a pull failure).
          // A mid-push WS disconnect is NOT a graceful account-change/logout —
          // it is a transport failure. Fall back to HTTP push for this batch
          // so the local write reaches the server instead of being silently
          // abandoned by the outer "WebSocket disconnected" graceful-break.
          try {
            const wsPush = await pushViaWebSocket(wsClient, serverCollection, changes);
            conflicts = wsPush?.conflicts || [];
            // Fix 2: honor the server's `acked` membership list. Legacy
            // servers (no `acked` field) fall back to everything-not-conflicted.
            if (Array.isArray(wsPush?.acked)) {
              ackedList = wsPush.acked;
            }
            pushedViaWs = true;
          } catch (err: any) {
            if (err?.message === 'WebSocket disconnected') {
              console.warn(`[sync] ${dexieCollection} WS push dropped (transport disconnect) — falling back to HTTP`);
            } else {
              throw new PushSyncError(
                dexieCollection,
                `Push ${dexieCollection} (ws) failed: ${err?.message ?? String(err)}`,
                err,
              );
            }
          }
        }
        if (!pushedViaWs) {
          const pushBody = JSON.stringify({ collection: serverCollection, changes });

          let headers = await buildHeaders();
          let pushRes: Response;
          try {
            pushRes = await timedFetch(`${base}${routePrefix}/push`, { method: 'POST', headers, body: pushBody });
          } catch (err: any) {
            // Network-level fetch failure (offline, DNS, TLS, etc.).
            throw new PushSyncError(
              dexieCollection,
              `Push ${dexieCollection} (http) network error: ${err?.message ?? String(err)}`,
              err,
            );
          }

          if (pushRes.status === 401 || pushRes.status === 403) {
            console.log(`[sync] ${dexieCollection} push batch got ${pushRes.status} — refreshing token and retrying`);
            await new Promise(r => setTimeout(r, 1_000));
            headers = await buildHeaders();
            try {
              pushRes = await timedFetch(`${base}${routePrefix}/push`, { method: 'POST', headers, body: pushBody });
            } catch (err: any) {
              throw new PushSyncError(
                dexieCollection,
                `Push ${dexieCollection} (http) network error after auth retry: ${err?.message ?? String(err)}`,
                err,
              );
            }
          }

          if (pushRes.status === 413) {
            if (batchSize > 1) {
              batchSize = Math.max(1, Math.floor(batchSize / 2));
              console.warn(`[sync] Push 413 for ${dexieCollection} — reducing batch size to ${batchSize} and retrying`);
              continue;
            } else {
              // Single doc too large for HTTP — try to fall back to WebSocket
              // which has no body-size limit. Wait briefly for WS to connect.
              const wsRetry = getWsClient();
              if (wsRetry && wsRetry.getStatus().state !== 'connected') {
                console.warn(`[sync] Single doc 413 for ${dexieCollection}/${String(batch[0]._id)} via HTTP — waiting for WebSocket...`);
                await new Promise(r => setTimeout(r, 3_000));
              }
              const wsFallback = getWsClient();
              if (wsFallback?.getStatus().state === 'connected') {
                debugLog(`[sync] Retrying oversized doc ${dexieCollection}/${String(batch[0]._id)} via WebSocket`);
                try {
                  const wsPush = await pushViaWebSocket(wsFallback, serverCollection, changes);
                  conflicts = wsPush?.conflicts || [];
                  if (Array.isArray(wsPush?.acked)) {
                    ackedList = wsPush.acked;
                  }
                } catch (wsErr: any) {
                  console.error(`[sync] WS fallback also failed for ${dexieCollection}/${String(batch[0]._id)} — skipping`, wsErr);
                  batchIndex += 1;
                  continue;
                }
              } else {
                console.error(`[sync] Single doc too large for ${dexieCollection}/${String(batch[0]._id)} — no WS available, skipping`);
                batchIndex += 1;
                continue;
              }
            }
          } else if (!pushRes.ok) {
            throw new PushSyncError(
              dexieCollection,
              (await httpSyncFailure(pushRes, `Push ${dexieCollection} batch `)).message,
            );
          } else {
            const data = await pushRes.json() as { conflicts: any[]; acked?: Array<{ _id: string; updatedAt?: string }> };
            conflicts = data.conflicts;
            if (Array.isArray(data.acked)) {
              ackedList = data.acked;
            }
          }
        }
        allConflicts.push(...conflicts);
        const conflictIds = new Set(conflicts.map((conflict: any) => String(conflict._id)));
        // Fix 2: use the server's `acked` list when available. Only docs the
        // server explicitly confirmed are recorded as acked. Legacy servers
        // (no `acked` field) fall back to all non-conflicted docs in the batch.
        let ackableDocs: Array<{ _id: string; updatedAt?: string }>;
        if (ackedList) {
          // Build a set of acked _ids for filtering
          const ackedIds = new Set(ackedList.map((a) => String(a._id)));
          ackableDocs = batch.filter((doc: any) => ackedIds.has(String(doc._id)));
        } else {
          ackableDocs = batch.filter((doc: any) => !conflictIds.has(String(doc._id)));
        }
        const ackableIds = new Set(ackableDocs.map((doc) => String(doc._id)));
        recordUnackedPushDocs(
          dexieCollection,
          batch.filter((doc: any) => !ackableIds.has(String(doc._id))),
          checkpointPrefix,
        );
        clearUnackedPushDocs(
          dexieCollection,
          [...ackableDocs, ...batch.filter((doc: any) => doc._deleted === true)],
          checkpointPrefix,
        );
        if (isCancelled()) return;
        writePushAckTimestamps(dexieCollection, ackableDocs, checkpointPrefix);
        recordKnownServerVersions(
          dexieCollection,
          ackableDocs.map((d) => ({ _id: String(d._id), updatedAt: String(d.updatedAt ?? '') })),
          checkpointPrefix,
        );

        batchIndex += batchSize;

        // Update remaining pending count as batches succeed (still pushing).
        const remaining = Math.max(0, uniqueDocs.length - batchIndex);
        setCollectionPushState(dexieCollection, { pending: remaining, pushing: remaining > 0 });

        // Small delay between batches to let server breathe
        if (batchIndex < uniqueDocs.length) {
          await new Promise(r => setTimeout(r, 100));
        }
      }
    } catch (err) {
      // Mark this collection as no-longer-pushing so the global aggregator
      // doesn't keep `isPushing` true forever, but preserve the pending
      // count (un-pushed docs are still queued) and let the outer catch
      // tally `failed` via PushSyncError detection.
      setCollectionPushState(dexieCollection, { pushing: false });
      syncLog.error(`Push failed for ${dexieCollection}: ${(err as any)?.message ?? String(err)}`, { collection: dexieCollection, detail: (err as any)?.message });
      throw err;
    }

    // Push complete — drop this collection from the aggregator entirely.
    // (clearCollectionPushState GC's the map entry.)
    clearCollectionPushState(dexieCollection);
    const pushedCount = uniqueDocs.length - allConflicts.length;
    if (pushedCount > 0) {
      debugLog(`[sync] ✅ Pushed ${pushedCount} doc(s) for ${dexieCollection} to server`);
      syncLog.success(`Pushed ${pushedCount} doc(s) for ${dexieCollection} to MongoDB`, { collection: dexieCollection });
    }

    // Timestamps of conflict-resolved docs that must re-push once. A bumped
    // updatedAt alone lands below the cycle-end checkpoint (set to NOW at
    // the end of this leg) and would be skipped by the next above() scan, so
    // the checkpoint is anchored just before the earliest requeued doc.
    const pushRequeueTimestamps: string[] = [];

    // Process all accumulated conflicts
    for (const conflict of allConflicts) {
      const localDoc = uniqueDocs.find((d: any) => String(d._id) === String(conflict._id));
      if (!localDoc) continue;

      const serverTs = conflict.updatedAt || '';
      const localTs  = localDoc.updatedAt || '';

      // ── Rule 1: Local delete intent always wins ──────────────────────────────
      // If the user deleted this doc locally, honor that intent unconditionally.
      // Without this, the conflict resolver would find no diffs (canAutoMerge([])
      // = true) and silently revert the delete by writing the server doc to Dexie.
      if (localDoc._deleted === true) {
        try {
          await table.put(localDoc);
          wakeLocalWrite(dexieCollection);
          console.log(`[sync] Delete intent preserved for ${dexieCollection}/${String(conflict._id)}`);
        } catch (err: any) {
          console.warn(`[sync] Delete intent put failed for ${dexieCollection}/${String(conflict._id)}:`, err.message);
        }
        continue;
      }

      const fieldDiffs = detectFieldDiffs(localDoc, conflict, dexieCollection);
      const meaningfulDiffs = fieldDiffs.filter((d: any) => d.field !== 'updatedAt');

      // ── Rule 2: Idempotent server echo ──────────────────────────────────────
      // A conflict that differs only by updatedAt is already converged. Accept
      // the server timestamp but keep the local document body so that fields
      // excluded from conflict detection (entities.inspectionAccountName,
      // entities.zoning, vendorData.rawHash/rawPayload) are NOT silently
      // dropped — the server projection never returns them, so table.put(conflict)
      // would wipe them from Dexie. Do not wake the loop; re-pushing the local
      // doc and waking here creates an endless push → conflict → wake cycle.
      if (meaningfulDiffs.length === 0) {
        try {
          await table.put({ ...localDoc, updatedAt: conflict.updatedAt });
          console.log(`[sync] Idempotent same-change for ${dexieCollection}/${String(conflict._id)}`);
        } catch (err: any) {
          console.warn(`[sync] Idempotent put failed for ${dexieCollection}/${String(conflict._id)}:`, err.message);
        }
        continue;
      }

      const mergedDiffs = applyAutoMergeRules(fieldDiffs);
      if (requiresManualConflictResolution(fieldDiffs)) {
        emitManualConflict(dexieCollection, conflict, localDoc, mergedDiffs, config.onConflict);
        continue;
      }

      // ── Rule 3: Local is newer-or-equal → client-wins silently ──────────────
      // Single-user scenario: user just pushed, server echo came back, or user
      // edited faster than the server processed. Protected complex topology was
      // already surfaced above and can never enter this timestamp shortcut.
      // resolveClientNewerConflictDoc applies only registered collision merges
      // (monotonic — a completed case event can never downgrade) so the local
      // doc converges onto state the server's stale-write guard will accept;
      // scalar/server-wins diffs keep local values. When a merge applied, the
      // bumped updatedAt is anchored into the cycle-end checkpoint via
      // pushRequeueTimestamps so the converged doc re-pushes once — delivering
      // unrelated local edits the rejected push never landed — instead of
      // being skipped by above(checkpoint).
      if (localTs >= serverTs) {
        const { docToStore, requeued } = resolveClientNewerConflictDoc(
          localDoc,
          conflict,
          mergedDiffs,
          localTs,
          serverTs,
        );
        try {
          await table.put(docToStore);
          if (requeued) {
            pushRequeueTimestamps.push(String(docToStore.updatedAt));
          }
          wakeLocalWrite(dexieCollection);
          console.log(`[sync] Client-wins (local newer/equal) for ${dexieCollection}/${String(conflict._id)}`);
        } catch (err: any) {
          console.warn(`[sync] Client-wins put failed for ${dexieCollection}/${String(conflict._id)}:`, err.message);
        }
        continue;
      }

      // ── Server is strictly newer: attempt field-level merge ──────────────────
      const autoMergeable = canAutoMerge(fieldDiffs);

      if (autoMergeable) {
        // ── Rule 4: Auto-mergeable diffs (array union, timestamp latest, etc.) ─
        // Merge local + server, bump updatedAt, re-push the merged state.
        // Use localDoc as merge base so local-only fields excluded from conflict
        // detection (e.g. inspectionAccountName, zoning) are preserved rather than
        // silently dropped because they don't appear in the server projection.
        const mergedDoc = buildMergedDocument(localDoc, mergedDiffs);
        const bumped = { ...mergedDoc, updatedAt: isoAfterBoth(localTs, serverTs) };
        try {
          await table.put(bumped);
          // The bumped timestamp lands below this cycle's end checkpoint, so
          // the merged doc would never be re-pushed without the requeue anchor.
          pushRequeueTimestamps.push(String(bumped.updatedAt));
          wakeLocalWrite(dexieCollection);
          console.log(`[sync] Auto-merged conflict for ${dexieCollection}/${String(conflict._id)}`);
          config.onConflict?.({
            id: `${dexieCollection}-${String(conflict._id)}-${Date.now()}`,
            collection: dexieCollection,
            documentId: String(conflict._id),
            localState: localDoc,
            serverState: conflict,
            detectedAt: new Date().toISOString(),
            resolved: true,
            resolution: 'auto-merged',
            autoMerged: true,
            fieldDiffs: mergedDiffs,
          });
        } catch (err: any) {
          console.warn(`[sync] Auto-merge failed for ${dexieCollection}/${String(conflict._id)}:`, err.message);
        }
      } else {
        // ── Rule 5: Genuine scalar conflict — surface to UI ──────────────────
        // Two users set the same field to genuinely different values.
        // This is the ONLY scenario that should ever reach the end user.
        emitManualConflict(dexieCollection, conflict, localDoc, mergedDiffs, config.onConflict);
      }
    }

    if (allConflicts.length === 0) {
      debugLog(`[sync] Pushed ${uniqueDocs.length} docs from ${dexieCollection} in ${totalBatches} batch(es)`);
    }

    // Set push checkpoint to NOW after a successful push cycle.
    // Using maxUpdatedAt of the pushed docs would cause the same race condition
    // as the full-pull checkpoint bug: a subsequent edit landing at the same
    // millisecond as the max pushed timestamp would be skipped by `above()`.
    // Wall-clock time at push completion is always strictly after all pushed docs.
    if (isCancelled()) return;
    // When conflict resolution requeued docs (bumped updatedAt above), anchor
    // the checkpoint just before the earliest requeue instead — the widened
    // scan only re-selects docs in that narrow window, and isUnackedLocalDoc
    // filters out anything already acked, so only the requeued docs re-push.
    const requeueAnchor = pushRequeueTimestamps.length > 0
      ? isoJustBefore(pushRequeueTimestamps.reduce((earliest, ts) => (ts < earliest ? ts : earliest)))
      : '';
    const newPushCheckpoint = requeueAnchor || new Date().toISOString();
    setPushCheckpoint(dexieCollection, newPushCheckpoint, checkpointPrefix);
    // Prune superseded push-ack records: every ack at or below the new
    // checkpoint is unreachable by future `above(checkpoint)` scans, so
    // keeping them grows _sync_meta unbounded. The ack just written for
    // this cycle's docs is also at or below the checkpoint and is pruned
    // — it is no longer needed because the checkpoint itself suppresses
    // re-pushes of those docs.
    await pruneSupersededPushAcks(dexieCollection, newPushCheckpoint, checkpointPrefix);
    if (pushRequeueTimestamps.length > 0) {
      // Defer the wake past this cycle's end so it lands on the loop's sleep
      // listener — a wake raised mid-cycle resolves no active sleep and is
      // lost, which would leave the requeued doc waiting for the next poll.
      setTimeout(() => wakeLocalWrite(dexieCollection), 50);
    }
  } else if (lastPush) {
    // No docs to push — checkpoint stays at current value
    // (Don't update checkpoint if no push occurred)
  } else {
    // First push cycle with no local changes — set checkpoint to now
    writePushCheckpoint(dexieCollection, new Date().toISOString(), checkpointPrefix);
  }

  if (_dexieInstance) {
    try {
      await reconcileDirtyLedger(_dexieInstance, dexieCollection);
    } catch (error: any) {
      console.warn(`[sync] Failed to reconcile dirty ledger for ${dexieCollection}:`, error);
    }
  }

  // If pull failed earlier but push was allowed to proceed, re-throw so the
  // outer loop triggers its normal backoff / retry for the pull leg.
  if (pullError) {
    throw pullError;
  }
}

// ---------------------------------------------------------------------------
// Direct Dexie delete by ID — used when SSE delivers a delete event
// No pull needed: the document is already gone from MongoDB
// ---------------------------------------------------------------------------
export async function applyRemoteDelete(
  dexieCollection: string,
  documentId: string,
  db: any,
): Promise<void> {
  try {
    const _rawDel = db[dexieCollection];
    const table = _rawDel?.table ?? (typeof _rawDel?.bulkPut === 'function' ? _rawDel : undefined);
    if (!table) return;
    // Preserve optimistic rows from remote delete events. An optimistic row
    // is a locally-created doc that hasn't been fully acknowledged by the
    // server yet. A hard-delete event for the same _id is likely a stale
    // cleanup that predates the optimistic write — deleting the local row
    // would make the grid row vanish. The push cycle will reconcile the
    // server state; if the server truly intends to delete this doc, the
    // next delta pull will return a tombstone and the optimistic guard in
    // the delta pull handler will preserve it until the push completes.
    // Only NON-TERMINAL optimistic rows are preserved — a failed optimistic row
    // is terminal and must accept the remote delete, or it lingers in Dexie
    // forever. See isProtectedOptimisticRow.
    const existing = await table.get(documentId);
    if (existing && isProtectedOptimisticRow(existing)) {
      debugLog(`[sync/sse] Preserved optimistic ${dexieCollection}/${documentId} from remote delete event`);
      return;
    }
    await table.delete(documentId);
    debugLog(`[sync/sse] Deleted ${dexieCollection}/${documentId} via SSE delete event`);
  } catch (err: any) {
    console.warn(`[sync/sse] Failed to delete ${dexieCollection}/${documentId}:`, err.message);
  }
}

// ---------------------------------------------------------------------------
// Cancellation-aware sleep — resolves early if cancelled OR if woken externally
// ---------------------------------------------------------------------------
function sleep(
  ms: number,
  isCancelled: () => boolean,
  wakeSignal?: { addListener: (fn: () => void) => void },
): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => { if (!done) { done = true; resolve(); } };

    const timer = setTimeout(finish, ms);
    const check = setInterval(() => {
      if (isCancelled()) { clearTimeout(timer); clearInterval(check); finish(); }
    }, 500);
    setTimeout(() => clearInterval(check), ms + 600);

    wakeSignal?.addListener(() => { clearTimeout(timer); clearInterval(check); finish(); });
  });
}

// ---------------------------------------------------------------------------
// Module-level registry: dexieCollection → wake fn
// Populated by setupReplication(). Allows service layer to wake a collection's
// push loop immediately after a local Dexie write without importing React or
// passing callbacks through every service function.
// ---------------------------------------------------------------------------
const _wakeRegistry = new Map<string, () => void>();

/**
 * Wake the push loop for a specific Dexie collection immediately.
 * Call this after any local write (insert/patch/remove) so the entity
 * reaches MongoDB without waiting for the next SSE event.
 *
 * Safe to call before setupReplication() is started — it is a no-op then.
 */
export function wakeLocalWrite(dexieCollection: string): void {
  _wakeRegistry.get(dexieCollection)?.();
}

/**
 * Wake ALL collection sync loops immediately — manual "Sync Now" trigger.
 * Each loop will run a delta pull + push cycle on its next iteration.
 * Safe to call before setupReplication() is started — it is a no-op then.
 */
export function triggerFullSync(): void {
  _wakeRegistry.forEach((wakeFn) => wakeFn());
}

export function combineReplicationStates(
  statesList: NamedReplicationStates[],
): NamedReplicationStates {
  const combined = new Map<string, ReplicationStateStub>();
  for (const states of statesList) {
    for (const [name, state] of states) {
      combined.set(name, state);
    }
  }
  replicationTeardowns.set(combined, { nestedStates: statesList });
  return combined;
}

// ---------------------------------------------------------------------------
// Online/offline helpers
// ---------------------------------------------------------------------------
function isOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

function waitForOnline(): Promise<void> {
  if (isOnline()) return Promise.resolve();
  return new Promise((resolve) => {
    const handler = () => { window.removeEventListener('online', handler); resolve(); };
    window.addEventListener('online', handler);
  });
}

// ---------------------------------------------------------------------------
// Phased initial sync — readiness gate
//
// Heavy, geometry-fat collections (e.g. `entities`, `violation_index`) must
// NOT fire their initial full pull at a freshly-(re)connected — possibly cold
// or just-scaled-out — replica. They wait until the light "readiness set"
// (every collection that is neither deferred nor account-scoped) has completed
// a clean pull over the CURRENT socket, which proves the exact replica we are
// pinned to is alive and serving sync (a far stronger signal than a blind
// timer or a load-balanced HTTP health probe). The gate re-arms on every
// disconnect so a reconnect, or a new replica grabbed during scale-out, gets
// the same handshake-first treatment. An absolute timeout guarantees the gate
// can never deadlock initial sync.
// ---------------------------------------------------------------------------
export interface ReadinessGate {
  /** True when deferred collections are free to pull. */
  readonly isOpen: boolean;
  /** Record that a light-set collection finished a clean pull. */
  markComplete(dexieKey: string): void;
  /** Awaited by deferred collections before their pull. No-op once open. */
  wait(): Promise<void>;
  /** Re-close the gate (call on disconnect) so a fresh replica is re-paced. */
  arm(): void;
  /** Open unconditionally (teardown) so no deferred loop can hang. */
  forceOpen(): void;
  /** Arm the absolute-timeout backstop. Call once after construction. */
  start(): void;
}

export function createReadinessGate(opts: {
  enabled: boolean;
  lightKeys: ReadonlySet<string>;
  deferredCount: number;
  absoluteTimeoutMs: number;
  instanceId?: string;
  /** Injected for tests; defaults to the real release-side logging. */
  onRelease?: (reason: string) => void;
}): ReadinessGate {
  const { enabled, lightKeys, deferredCount, absoluteTimeoutMs, instanceId = 'sync', onRelease } = opts;
  // Disabled, or nothing to wait on ⇒ permanently open (zero behavior change).
  let open = !enabled || lightKeys.size === 0;
  const completed = new Set<string>();
  let waiters: Array<() => void> = [];
  let absTimer: ReturnType<typeof setTimeout> | null = null;

  const clearAbsTimer = () => {
    if (absTimer !== null) { clearTimeout(absTimer); absTimer = null; }
  };
  const release = (reason: string) => {
    if (open) return;
    open = true;
    clearAbsTimer();
    debugLog(`[sync:${instanceId}] Readiness gate OPEN (${reason}) — releasing ${deferredCount} deferred collection(s)`);
    onRelease?.(reason);
    const toResolve = waiters;
    waiters = [];
    toResolve.forEach((fn) => fn());
  };
  const armAbsTimer = () => {
    if (!enabled || open || absTimer !== null || typeof setTimeout === 'undefined') return;
    absTimer = setTimeout(() => release('absolute-timeout'), absoluteTimeoutMs);
  };

  return {
    get isOpen() { return open; },
    markComplete(dexieKey: string) {
      if (open || !lightKeys.has(dexieKey)) return;
      completed.add(dexieKey);
      if (completed.size >= lightKeys.size) release('light-phase-complete');
    },
    wait(): Promise<void> {
      if (open) return Promise.resolve();
      return new Promise<void>((resolve) => waiters.push(resolve));
    },
    arm() {
      if (!enabled) return;
      open = false;
      completed.clear();
      armAbsTimer();
    },
    forceOpen() { release('forced-open'); },
    start() { armAbsTimer(); },
  };
}

// ---------------------------------------------------------------------------
// Stuck-sync watchdog
//
// Field reports describe a sync that "looked like it was syncing but never
// finished" — the cold-replica death-spiral where heavy pulls keep failing on
// a churning replica while the engine reports itself active. This watchdog
// detects that silent stall: while online, with a resumable full-pull cursor
// still outstanding, if NO page progress is observed for `stuckMs`, it forces
// a clean WebSocket reconnect so the engine resumes on a fresh connection
// (which also re-runs the readiness handshake). Dependencies are injected so
// the trigger logic is unit-testable without a live socket or DB.
// ---------------------------------------------------------------------------
export interface StuckSyncWatchdog {
  start(): void;
  stop(): void;
  /** Run a single check now (exposed for tests / manual triggering). */
  check(): void;
}

export function createStuckSyncWatchdog(opts: {
  enabled: boolean;
  stuckMs: number;
  isOnline: () => boolean;
  getLastProgressAt: () => number;
  hasOutstandingWork: () => boolean;
  forceReconnect: (reason: string) => void;
  markChecked: () => void;
  now?: () => number;
}): StuckSyncWatchdog {
  const {
    enabled, stuckMs, isOnline, getLastProgressAt,
    hasOutstandingWork, forceReconnect, markChecked,
  } = opts;
  const now = opts.now ?? (() => Date.now());
  let timer: ReturnType<typeof setInterval> | null = null;

  const check = () => {
    if (!enabled) return;
    // Offline is an expected idle state, not a stall — keep resetting the
    // baseline so the device does not "self-heal" the moment it returns.
    if (!isOnline()) { markChecked(); return; }
    const idleMs = now() - getLastProgressAt();
    if (idleMs < stuckMs) return;
    // Only intervene when there is genuinely outstanding pull work. A device
    // that is fully synced and simply waiting on change events is healthy.
    if (!hasOutstandingWork()) return;
    // Force first, then reset the baseline so the reconnect callback can
    // still read the true idle duration. Resetting before forceReconnect
    // made the watchdog log "no progress for ~0s".
    forceReconnect('stuck-sync watchdog');
    markChecked();
  };

  return {
    check,
    start() {
      if (!enabled || timer !== null || typeof setInterval === 'undefined') return;
      const interval = Math.max(15_000, Math.floor(stuckMs / 2));
      timer = setInterval(check, interval);
    },
    stop() {
      if (timer !== null) { clearInterval(timer); timer = null; }
    },
  };
}

// ---------------------------------------------------------------------------
// setupReplication — starts a sync loop per syncable collection + SSE client
// ---------------------------------------------------------------------------
export function setupReplication(
  db: unknown,
  config: ReplicationConfig,
): NamedReplicationStates {
  const instanceId = config.checkpointPrefix || 'tenant';

  // One pull semaphore per replication instance. Shared by every
  // collection loop spawned below so a single 100k-row pull releases its
  // slot between pages, letting smaller collections interleave instead of
  // sitting behind it. See PullSemaphore @contract.
  const pullSemaphore = new PullSemaphore(config.maxConcurrentPulls);

  bindDexieMetaInstance(db);

  const states = new Map<string, ReplicationStateStub>();
  const wakeRegistryKeys: string[] = [];
  const wakeListeners: Array<() => void> = [];
  const collectionWakeMap = new Map<string, () => void>();
  const pendingWakeMap = new Map<string, number>();

  // Spread wake-ups across collections instead of firing all ~20 in the same
  // tick. On a (re)connect or polling tick every collection loop would
  // otherwise wake simultaneously and each fire a pull at once — a thundering
  // herd that floods the single WebSocket and stacks divergence/checkpoint and
  // IndexedDB work on the mobile main thread, which is what collapses the
  // socket (1006) on the first full sync. The socket-level pull concurrency cap
  // bounds in-flight pulls; this staggers when each loop wakes so the burst is
  // smoothed rather than instantaneous. The first collection still wakes
  // synchronously so a single-collection wake is unaffected.
  const WAKE_ALL_STAGGER_MS = 100;
  const wakeAll = () => {
    wakeListeners.forEach((fn, i) => {
      if (i === 0) {
        fn();
      } else {
        setTimeout(fn, i * WAKE_ALL_STAGGER_MS);
      }
    });
  };
  const wakeCollection = (dexieKey: string) => {
    queueWakeRequest(pendingWakeMap, dexieKey, collectionWakeMap.get(dexieKey), instanceId);
  };

  // Wake all sync loops immediately when the device comes back online.
  // Without this, inspectors returning from a dead-zone wait up to MAX_BACKOFF_MS
  // (2 min) before their pending edits are pushed to the server.
  let _onlineHandler: (() => void) | undefined;
  if (typeof window !== 'undefined') {
    _onlineHandler = () => {
      console.log(`[sync:${instanceId}] Network restored — waking all sync loops`);
      syncLog.success('Network restored — waking all sync loops');
      wakeAll();
    };
    window.addEventListener('online', _onlineHandler as EventListener);
  }

  // Cooldown prevents the divergence check from firing on every 1-second
  // WebSocket reconnect during instability. Without this, each reconnect
  // triggers checkSyncDivergence + wakeAll, which for large collections
  // (19k entities) can clear pull checkpoints and restart multi-minute
  // full pulls in a tight loop — eventually OOMing mobile Safari.
  const DIVERGENCE_CHECK_COOLDOWN_MS = 30_000;
  let lastDivergenceCheckAt = 0;

  const runDivergenceCheckAndWake = () => {
    const now = Date.now();
    if (now - lastDivergenceCheckAt < DIVERGENCE_CHECK_COOLDOWN_MS) {
      // Cooldown active — still wake all collections so delta pulls run,
      // but skip the expensive divergence status check + checkpoint clear.
      debugLog(`[sync:${instanceId}] Divergence check skipped (cooldown, ${Math.round((DIVERGENCE_CHECK_COOLDOWN_MS - (now - lastDivergenceCheckAt)) / 1000)}s remaining)`);
      wakeAll();
      return;
    }
    lastDivergenceCheckAt = now;

    void checkSyncDivergence(db, config)
      .then((divergedDexieKeys) => {
        if (divergedDexieKeys.length > 0) {
          for (const dexieKey of divergedDexieKeys) {
            wakeCollection(dexieKey);
          }
        }
        // Always wake ALL collections on reconnect — not just diverged ones.
        // Collections outside of divergenceCheckCollections (e.g. entities)
        // would otherwise sleep indefinitely if the Change Stream missed an
        // event during the disconnect window.
        wakeAll();
      })
      .catch((error: any) => {
        console.warn(`[sync:${instanceId}] Divergence wake failed:`, error?.message ?? error);
        wakeAll();
      });
  };

  const collections = Object.keys(config.collectionMap);

  // Stagger startup by 200ms per collection so all 17 don't hit the
  // MongoDB Atlas M0 connection pool simultaneously on first full sync.
  const STARTUP_STAGGER_MS = 200;

  // §issue-64 Phase 1 diagnostics: the mobile account gate blocks until the
  // gate-critical collections write their first pull checkpoint. Logging the
  // time-since-replication-start for each one makes a slow cold-start (the
  // RC#1 timeout) directly observable in the existing SyncStatusWidget log
  // instead of having to infer it. Reuses `syncLog` — no new logger.
  const replicationStartedAt = Date.now();
  const GATE_CRITICAL_DEXIE_KEYS = new Set([
    'inspection_accounts',
    'inspection_account_groups',
    'users',
  ]);

  // ── Phased initial sync — readiness gate ─────────────────────────────────
  // The "light set" = every collection that is neither deferred (heavy) nor
  // account-scoped (cannot complete before account selection). When all of
  // them have completed a clean pull over the live socket, the gate opens and
  // the deferred heavy collections are released. See createReadinessGate.
  const deferredKeys = new Set(config.deferredFullSyncCollections ?? []);
  const accountScopedKeys = new Set(config.accountScopedCollections ?? []);
  const readinessEnabled = !!config.useWebSocket && deferredKeys.size > 0;
  const readinessLightKeys = new Set(
    collections.filter((k) => !deferredKeys.has(k) && !accountScopedKeys.has(k)),
  );
  const readinessGate = createReadinessGate({
    enabled: readinessEnabled,
    lightKeys: readinessLightKeys,
    deferredCount: deferredKeys.size,
    absoluteTimeoutMs: config.readinessGateTimeoutMs ?? 45_000,
    instanceId,
    onRelease: (reason) =>
      syncLog.info(`Readiness gate released deferred collections (${reason})`),
  });
  if (readinessEnabled) {
    readinessGate.start();
    syncLog.info(
      `Phased initial sync enabled — ${deferredKeys.size} deferred collection(s) gated on ${readinessLightKeys.size} light collection(s)`,
    );
  }

  // ── Stuck-sync watchdog ──────────────────────────────────────────────────
  // `lastProgressAt` advances whenever ANY collection completes a clean sync
  // cycle OR a full-pull page lands (threaded into syncCollection via
  // onPageProgress), so a long-but-healthy 100k-row pull is never mistaken for
  // a stall. The watchdog forces a clean reconnect only when work is still
  // outstanding but no progress has happened for `stuckSyncTimeoutMs`.
  let lastProgressAt = Date.now();
  const markSyncProgress = () => { lastProgressAt = Date.now(); };
  const wsRoutePrefix = config.syncRoutePrefix ?? '/sync';
  const stuckWatchdog = createStuckSyncWatchdog({
    enabled: (config.stuckSyncTimeoutMs ?? 0) > 0 && !!config.useWebSocket,
    stuckMs: config.stuckSyncTimeoutMs ?? 0,
    isOnline: _rfIsOnline,
    getLastProgressAt: () => lastProgressAt,
    hasOutstandingWork: () =>
      collections.some((k) => !!getCheckpoint(FULLPULL_CURSOR_KEY(k, config.checkpointPrefix))),
    markChecked: markSyncProgress,
    forceReconnect: (reason) => {
      const client = getActiveWebSocketSyncClient(config.apiBaseUrl, config.departmentId, wsRoutePrefix);
      const idleSec = Math.round((Date.now() - lastProgressAt) / 1_000);
      console.warn(`[sync:${instanceId}] Stuck-sync watchdog — no progress for ~${idleSec}s with a full pull pending; forcing reconnect`);
      syncLog.warn('Sync stalled with work pending — forcing reconnect to self-heal');
      if (client) {
        client.forceReconnect(reason);
        // Re-pace the reconnected (possibly fresh) replica from the light set.
        readinessGate.arm();
      } else {
        // No WS client (e.g. SSE/polling) — just re-wake every loop.
        wakeAll();
      }
    },
  });

  // ── Multi-tab leader election (Web Locks) ─────────────────────────────────
  // When multiple tabs of the same app are open, only the leader tab should
  // run the sync engine. Non-leader tabs park until the leader releases the
  // lock (tab close, navigation, etc.). This prevents N tabs from each
  // running full pulls and pushing duplicate changes simultaneously.
  //
  // The lock is requested via navigator.locks.request. The callback resolves
  // a promise that gates every collection loop — no network work happens
  // until the lock is acquired. If Web Locks is unavailable (older browsers),
  // the gate is immediately open (single-tab assumption).
  let leaderLockAcquired: Promise<void> = Promise.resolve();
  let releaseLeaderLock: (() => void) | undefined;
  if (typeof navigator !== 'undefined' && (navigator as any).locks?.request) {
    const lockName = `firesquirrel-sync-${instanceId}`;
    leaderLockAcquired = new Promise<void>((resolveLock) => {
      (navigator as any).locks.request(lockName, async () => {
        resolveLock();
        // Hold the lock until teardown releases it.
        await new Promise<void>((resolveHold) => {
          releaseLeaderLock = resolveHold;
        });
      });
    });
    debugLog(`[sync:${instanceId}] Leader election: waiting for Web Lock "${lockName}"`);
  }

  collections.forEach((dexieCollection, index) => {
    const serverCollection = config.collectionMap[dexieCollection];
    if (!serverCollection) return;

    let cancelled = false;
    const error$ = makeObservable<any>();
    const active$ = makeObservable<boolean>();
    const canceled$ = makeObservable<boolean>();
    // See ReplicationStateStub.firstCycleSettled — resolved (once, idempotent
    // on further calls) right after this collection's first syncCollection()
    // attempt settles, whether it found dirty docs to push or not.
    let resolveFirstCycleSettled: () => void = () => {};
    const firstCycleSettled = new Promise<void>((resolve) => {
      resolveFirstCycleSettled = resolve;
    });
    // Tracks whether this collection has EVER completed a real
    // syncCollection() attempt (success or a genuine — non-graceful-
    // disconnect — error). See hadUnconfirmedFirstCycleDisconnect below.
    let hasCompletedARealCycle = false;
    // True only if a graceful WebSocket disconnect terminated this
    // collection's loop (via the `isGracefulDisconnect` break) before it
    // ever completed a single real cycle. In that case syncCollection()
    // never got a chance to discover or push this collection's locally-
    // dirty docs — its push-state entry is cleared (nothing to report) and
    // the loop then exits permanently, so isDrainedFor()/getPendingCountFor()
    // alone would report "nothing pending" even though we genuinely never
    // checked. Callers that need an honest "was this collection actually
    // confirmed clean" signal (e.g. the mobile pre-wipe flush) should treat
    // this as "unconfirmed", not "drained".
    let firstCycleWasUnconfirmedDisconnect = false;
    // Resolvers registered via registerNextCycleWaiter(), flushed the next
    // time this collection's loop iteration settles (success, normal error,
    // OR the graceful-disconnect break — see all three flush sites below).
    // See registerNextCycleWaiter's own doc for why this exists separately
    // from firstCycleSettled.
    let nextCycleWaiters: Array<() => void> = [];
    function flushNextCycleWaiters(): void {
      const waiters = nextCycleWaiters;
      nextCycleWaiters = [];
      waiters.forEach((resolve) => resolve());
    }
    let inFlightCyclePromise: Promise<void> | null = null;

    const state: ReplicationStateStub = {
      error$: { subscribe: error$.subscribe },
      active$: { subscribe: active$.subscribe },
      canceled$: { subscribe: canceled$.subscribe },
      firstCycleSettled,
      hadUnconfirmedFirstCycleDisconnect: () => firstCycleWasUnconfirmedDisconnect,
      /**
       * Returns a promise that resolves once this collection's CURRENT (if
       * mid-flight) or NEXT loop iteration settles from the moment this is
       * called — unlike `firstCycleSettled` (which only ever covers the
       * FIRST cycle, from setupReplication() time), this can be called
       * again after the first cycle to observe a LATER cycle, e.g. one
       * triggered by `triggerFullSync()` waking a sleeping loop. Used by
       * `waitForNextSyncAttempt` to close the race where a write lands
       * between the first cycle finding nothing dirty and a wake-triggered
       * re-check actually running — see that function's doc for why a
       * synchronous isDrainedFor() check right after triggerFullSync()
       * cannot see a re-check that hasn't run yet.
       */
      registerNextCycleWaiter: () => new Promise<void>((resolve) => {
        nextCycleWaiters.push(resolve);
      }),
      cancel: async () => {
        cancelled = true;
        canceled$.emit(true);
        collectionWakeMap.get(dexieCollection)?.();
        // Unconditionally clear this collection's push-state entry. The sync
        // loop's own cleanup only runs on its next iteration and only clears
        // state via the `isGracefulDisconnect` catch branch (WebSocket
        // disconnect) or its normal push-complete path — neither of which
        // fires if this collection is cancelled mid-cycle without an error.
        // Without this, a stale non-zero `pending`/`pushing` entry can
        // survive in the global syncStatusStore after cancelAllReplication()
        // resolves, until the next setupReplication() for this collection
        // overwrites it.
        clearCollectionPushState(dexieCollection);
        // If this collection is cancelled before its loop ever reaches the
        // try/catch (e.g. during the `index * STARTUP_STAGGER_MS` startup
        // delay, or while parked on `leaderLockAcquired`), the `if
        // (cancelled) return;` early-outs skip resolveFirstCycleSettled()
        // entirely — that promise would otherwise never resolve, and a
        // caller awaiting waitForInitialSyncAttempt() would sit out its
        // FULL timeout for this collection even though we already know,
        // right now, that no discovery/push cycle is coming. Resolving here
        // is idempotent with the loop's own (skipped) resolve call.
        resolveFirstCycleSettled();
        // Same reasoning for any registerNextCycleWaiter() callers — a
        // cancelled collection will never run the cycle they're waiting on.
        flushNextCycleWaiters();
        if (inFlightCyclePromise) {
          let cancelWaitTimer: ReturnType<typeof setTimeout> | undefined;
          await Promise.race([
            inFlightCyclePromise.catch(() => {}),
            new Promise<void>((resolve) => {
              cancelWaitTimer = setTimeout(resolve, 100);
            }),
          ]);
          clearTimeout(cancelWaitTimer);
        }
        await flushPendingMetaWrites();
      },
    };

    const collectionWake = {
      addListener: (fn: () => void) => {
        wakeListeners.push(fn);
        collectionWakeMap.set(dexieCollection, fn);
        // Also register in the module-level registry so wakeLocalWrite() works
        _wakeRegistry.set(dexieCollection, fn);
        wakeRegistryKeys.push(dexieCollection);

        flushQueuedWakeRequests(pendingWakeMap, dexieCollection, fn);
      },
    };

    (async () => {
      // Stagger startup — wait index * STARTUP_STAGGER_MS before first sync
      if (index > 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, index * STARTUP_STAGGER_MS));
      }
      if (cancelled) return;

      // Park until this tab acquires the leader lock (Web Locks). No network
      // work happens until leadership is granted — prevents N open tabs from
      // each running full pulls and duplicate pushes simultaneously.
      await leaderLockAcquired;
      if (cancelled) return;

      let consecutiveErrors = 0;
      // §issue-64 Phase 1: emit a single syncLog line the first time a
      // gate-critical collection has a pull checkpoint, with the elapsed time
      // since replication start so the mobile gate's wait window is observable.
      let gateCriticalCheckpointLogged = false;
      while (!cancelled) {
        if (!_rfIsOnline()) {
          console.log(`[sync] Offline — waiting for reconnect: ${dexieCollection}`);
          await _rfWaitForOnline();
          if (cancelled) break;
        }

        // Phased initial sync: heavy/deferred collections hold their pull until
        // the light readiness set has completed cleanly over this socket. No-op
        // for non-deferred collections and once the gate is open (steady state /
        // disabled). The gate re-arms on disconnect so a reconnect re-paces.
        if (readinessEnabled && deferredKeys.has(dexieCollection) && !readinessGate.isOpen) {
          syncLog.info(`Holding ${dexieCollection} until light-phase sync completes`, { collection: dexieCollection });
          await readinessGate.wait();
          if (cancelled) break;
        }

        active$.emit(true);
        
        // Phase 4: Mark sync as active
        setSyncing(true);
        
        let nextSleep = INDEFINITE_SLEEP_MS; // sleep until SSE wakes us
        try {
          inFlightCyclePromise = syncCollection(dexieCollection, serverCollection, db, config, pullSemaphore, markSyncProgress, () => cancelled);
          await inFlightCyclePromise;
          inFlightCyclePromise = null;
          if (cancelled) break;

          // §issue-64 Phase 1 — record when a gate-critical collection lands its
          // first pull checkpoint (the moment the mobile account gate can stop
          // waiting on it). Logged once per collection per session.
          if (!gateCriticalCheckpointLogged && GATE_CRITICAL_DEXIE_KEYS.has(dexieCollection)) {
            const checkpoint = getCheckpoint(PULL_KEY(dexieCollection, config.checkpointPrefix));
            if (checkpoint) {
              gateCriticalCheckpointLogged = true;
              syncLog.info(
                `Gate-critical collection ${dexieCollection} checkpointed`,
                {
                  collection: dexieCollection,
                  detail: `+${Date.now() - replicationStartedAt}ms since sync start`,
                },
              );
            }
          }

          consecutiveErrors = 0;

          // A clean sync cycle = forward progress (resets the stuck-sync
          // watchdog baseline) and, for a light-set collection, a vote toward
          // opening the readiness gate so the deferred heavy collections can
          // start pulling.
          markSyncProgress();
          // Record the collection's first clean cycle and release any
          // waitForCollectionFirstSync waiters — writers that must not act
          // on a pre-pull local snapshot gate on this signal.
          cleanCycleCompleted.add(dexieCollection);
          const firstSyncWaiters = cleanCycleWaiters.get(dexieCollection);
          if (firstSyncWaiters?.length) {
            cleanCycleWaiters.delete(dexieCollection);
            for (const w of firstSyncWaiters) w();
          }
          readinessGate.markComplete(dexieCollection);
          firstCycleWasUnconfirmedDisconnect = false;

          // Clear THIS collection's failed-push count on success. The
          // aggregator only zeroes the global value if every collection
          // is also clean, so one collection succeeding cannot mask
          // another collection's failures (the prior bug with
          // `setFailedPushCount(0)`).
          setCollectionPushState(dexieCollection, { failed: 0 });

          // Phase 4: Mark sync success
          setSyncSuccess();
        } catch (err: any) {
          inFlightCyclePromise = null;
          // WebSocket disconnect (either intentional teardown or a transient
          // socket drop that aborted an in-flight WS pull/waiter).
          const isGracefulDisconnect = err.message === 'WebSocket disconnected';
          if (isGracefulDisconnect) {
            console.debug(`[sync] ${dexieCollection} interrupted (WebSocket disconnected)`);
            // Drop this collection's push state from the aggregator so the
            // global `isPushing` flag doesn't stick on after a graceful cancel.
            clearCollectionPushState(dexieCollection);
            if (!hasCompletedARealCycle) {
              // This collection's first cycle was interrupted by a disconnect
              // before completing a real pull+push attempt — syncCollection()
              // never got a chance to discover or push whatever is locally
              // dirty for it. Record this as "unconfirmed" so a caller relying
              // on isDrainedFor()/getPendingCountFor() (which now both see a
              // cleared, "nothing pending" entry) doesn't mistake a
              // never-checked collection for a confirmed-clean one.
              firstCycleWasUnconfirmedDisconnect = true;
            }
            // Settle any first-cycle / next-cycle waiters promptly so callers
            // awaiting waitForInitialSyncAttempt do not sit out their full
            // timeout when a disconnect interrupts the first cycle.
            resolveFirstCycleSettled();
            flushNextCycleWaiters();
            active$.emit(false);
            setSyncing(false);
            if (cancelled) {
              break;
            }
            // Transient WebSocket drop while replication is still active: keep
            // the collection loop alive and sleep until onReconnect / wakeAll()
            // or the §POLL-1 fallback wakes it. Breaking here when !cancelled
            // permanently killed the collection's sync loop for the rest of the
            // session and stranded collectionStatuses[dexieCollection] at
            // 'active'.
            await sleep(nextSleep, () => cancelled, collectionWake);
            continue;
          }

          consecutiveErrors++;
          // Detect push failures by typed error class (not substring match
          // on the message). This covers BOTH the HTTP push path and the
          // WebSocket push path — `pushViaWebSocket` errors used to slip
          // through the previous `err.message?.includes('Push ')` check.
          if (isPushSyncError(err)) {
            setCollectionPushState(dexieCollection, {
              failed: consecutiveErrors,
              pushing: false,
            });
          } else {
            // Non-push error (e.g. pull failure). Just clear the pushing
            // flag for this collection so the widget doesn't stick.
            setCollectionPushState(dexieCollection, { pushing: false });
          }
          const isAuthError = err.message?.includes('HTTP 401') || err.message?.includes('HTTP 403');
          // A 404 whose body names the tenant means this department no longer
          // resolves on the server (stale slug / wrong department). Retrying is
          // pointless and the cause must be visible, so it is not a generic network error.
          const isTenantNotFound = err.message?.includes('HTTP 404') && /tenant not found/i.test(err.message);
          if (isTenantNotFound) {
            console.error(`[sync] ${dexieCollection}: department not found on server - sync is paused for this department. Sign out and re-select the department. ${err.message}`);
            config.onTenantNotFound?.(config.departmentId);
          }
          if (isAuthError) {
            console.warn(`[sync] ${dexieCollection} auth error — backing off ${AUTH_BACKOFF_MS / 1000}s`);
            nextSleep = AUTH_BACKOFF_MS;
          } else {
            const backoff = Math.min(AUTH_BACKOFF_MS * Math.pow(2, consecutiveErrors - 1), MAX_BACKOFF_MS);
            nextSleep = backoff;
            if (consecutiveErrors === 1 || consecutiveErrors % 5 === 0) {
              console.error(`[sync] ${dexieCollection} error (attempt ${consecutiveErrors}, next retry ${Math.round(backoff / 1000)}s):`, err.message);
            }
          }
          error$.emit(err);
          
          // Phase 4: Set error in sync status
          const errorType = isAuthError ? 'auth' : isTenantNotFound ? 'tenant-not-found' : 'network';
          setSyncError(err.message || 'Sync error', errorType);
        }
        // Signal that at least one full pull+push attempt has settled for
        // this collection (idempotent — later iterations calling resolve()
        // again are a no-op). Fires on both the success and catch paths.
        resolveFirstCycleSettled();
        // Every iteration's completion (not just the first) flushes any
        // registerNextCycleWaiter() callers registered before this point —
        // e.g. the mobile flush's post-triggerFullSync() re-check wait.
        flushNextCycleWaiters();
        // Reached by the success path AND the normal (non-graceful-
        // disconnect) catch path — both represent a REAL attempt at this
        // collection's sync cycle, as opposed to the graceful-disconnect
        // branch above, which terminates the loop without ever reaching
        // here. See hadUnconfirmedFirstCycleDisconnect.
        hasCompletedARealCycle = true;
        active$.emit(false);
        
        // Phase 4: Mark sync as inactive
        setSyncing(false);

        // If the full pull was interrupted mid-way (fetch error on page 2+, etc.),
        // the FULLPULL_CURSOR_KEY is still set. Do NOT sleep — immediately re-enter
        // syncCollection so paging continues from the saved cursor in the same session.
        // Without this, the outer loop sleeps 24h waiting for an SSE wake that never
        // comes (nothing changed on server), leaving Dexie permanently at 500 records.
        // Use a short 2s pause on error paths to avoid hammering on persistent failures.
        const pendingFullPull = getCheckpoint(FULLPULL_CURSOR_KEY(dexieCollection, config.checkpointPrefix));
        if (pendingFullPull) {
          const retryDelay = consecutiveErrors > 0 ? 2_000 : 500;
          await new Promise(r => setTimeout(r, retryDelay));
          continue;
        }
        
        // Sleep indefinitely — SSE wake or online reconnect will interrupt this
        await sleep(nextSleep, () => cancelled, collectionWake);
      }
    })();

    states.set(dexieCollection, state);
  });

  // ---------------------------------------------------------------------------
  // §POLL-1 / §POLL-2 — Safety-net polling timers
  //
  // A single shared timer covers both states. The interval switches between
  // the disconnected rate (§POLL-1, 30 s) and the connected rate (§POLL-2,
  // 90 s) depending on the WebSocket status reported by onStatusChange.
  //
  // Call _startPolling(connected) to arm or re-arm the timer with the
  // appropriate interval. Call _stopPolling() on teardown or intentional
  // disconnect.
  // ---------------------------------------------------------------------------
  let _pollingTimer: ReturnType<typeof setInterval> | null = null;

  const _startPolling = (connected: boolean): void => {
    if (typeof window === 'undefined') return;
    // Always clear the existing timer before re-arming so the interval
    // switches cleanly when the WebSocket transitions between states.
    if (_pollingTimer !== null) {
      clearInterval(_pollingTimer);
      _pollingTimer = null;
    }
    const ms = connected ? POLLING_FALLBACK_CONNECTED_MS : POLLING_FALLBACK_DISCONNECTED_MS;
    const label = connected ? '§POLL-2 connected safety-net' : '§POLL-1 disconnected fallback';
    debugLog(`[sync:${instanceId}] ${label} started (${ms / 1_000}s)`);
    _pollingTimer = setInterval(() => {
      debugLog(`[sync:${instanceId}] ${label} wake — delta-pulling all collections`);
      wakeAll();
    }, ms);
  };

  const _stopPolling = (): void => {
    if (_pollingTimer !== null) {
      clearInterval(_pollingTimer);
      _pollingTimer = null;
      debugLog(`[sync:${instanceId}] Safety-net polling stopped`);
    }
  };

  // Start real-time sync client — WebSocket (preferred) or SSE (legacy fallback)
  let stopSyncClient: (() => void) | undefined;

  if (typeof window !== 'undefined') {
    if (config.useWebSocket) {
      // WebSocket bidirectional sync
      stopSyncClient = startWebSocketSyncClient({
        apiBaseUrl: config.apiBaseUrl,
        departmentId: config.departmentId,
        getAuthHeaders: config.getAuthHeaders,
        collectionMap: config.collectionMap,
        syncRoutePrefix: wsRoutePrefix,
        enableVisibilityReconnect: config.enableVisibilityReconnect,
        onSyncEvent: (event, dexieKey) => {
          if (event.type === 'delete' && event.documentId) {
            applyRemoteDelete(dexieKey, event.documentId, db);
          } else if (event.type === 'upsert') {
            wakeCollection(dexieKey);
          }
        },
        onAuthFailure: config.onAuthFailure,
        onReconnect: runDivergenceCheckAndWake,
        onStatusChange: (status) => {
          if (status === 'live') {
            // WebSocket connected — switch to the lighter 90-second safety net.
            // runDivergenceCheckAndWake (fired by onReconnect) already wakes all
            // collections on this same transition, so the safety-net timer's
            // first fire is deferred a full interval.
            _startPolling(true);
          } else if (status === 'polling') {
            // WebSocket reconnecting — accelerate to 30-second fallback so
            // the device does not go stale on a spotty network. Re-arm the
            // readiness gate so the heavy collections re-prove the (possibly
            // freshly-scaled-out) replica before backfilling it again.
            readinessGate.arm();
            _startPolling(false);
          } else {
            // Intentional disconnect (logout, account change) — stop all polling.
            _stopPolling();
          }
        },
      });
      // Arm the stuck-sync watchdog once the real-time client exists. It is a
      // no-op unless config.stuckSyncTimeoutMs > 0.
      stuckWatchdog.start();
    }
  }

  if ((config.divergenceCheckCollections?.length ?? 0) > 0) {
    setTimeout(runDivergenceCheckAndWake, 0);
  }

  const removeOnlineListenerFn = _onlineHandler
    ? () => { if (typeof window !== 'undefined') window.removeEventListener('online', _onlineHandler!); }
    : undefined;
  replicationTeardowns.set(states, {
    stopSse: stopSyncClient,
    removeOnlineListener: removeOnlineListenerFn,
    stopPolling: _stopPolling,
    stopWatchdog: () => stuckWatchdog.stop(),
    releaseReadinessGate: () => readinessGate.forceOpen(),
    releaseLeaderLock: () => releaseLeaderLock?.(),
    wakeRegistryKeys,
  });


  debugLog(`[sync] Replication started for ${states.size} collections`);
  return states;
}


/**
 * @process waitForInitialSyncAttempt
 *
 * Steps:
 * 1. Collect each collection's `firstCycleSettled` promise from `states`.
 * 2. Wait for all of them to resolve (each resolves once its collection's
 *    first pull+push cycle has settled — see ReplicationStateStub), OR for
 *    `timeoutMs` to elapse, whichever comes first.
 *
 * Inputs:
 * - states: The NamedReplicationStates returned by setupReplication() for the
 *   session being awaited.
 * - timeoutMs: Safety ceiling in case a collection's first cycle never
 *   settles (e.g. genuinely offline) — default 5000ms.
 *
 * Outputs:
 * - Promise<void>: always resolves (never rejects); callers that need to
 *   know whether every collection actually settled vs. timed out should
 *   inspect their own downstream state (e.g. syncStatusStore) afterward.
 *
 * Failure Handling:
 * - Never throws. Used to replace fixed-delay heuristics (e.g. "sleep 1s and
 *   hope discovery finished") with a real per-collection completion signal,
 *   while still bounding the wait so a stalled collection cannot hang the
 *   caller forever.
 *
 * Idempotency:
 * - REQUIRED — safe to call multiple times against the same `states`.
 */
export async function waitForInitialSyncAttempt(
  states: NamedReplicationStates,
  timeoutMs = 5_000,
): Promise<void> {
  const settlePromises = Array.from(states.values())
    .map((s) => s.firstCycleSettled)
    .filter((p): p is Promise<void> => !!p);
  if (settlePromises.length === 0) return;

  // The timeout timer is explicitly cleared once the race settles either
  // way, so every collection settling before the ceiling doesn't leave a
  // dangling timer alive for the remainder of timeoutMs.
  let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    Promise.all(settlePromises),
    new Promise<void>((resolve) => {
      timeoutTimer = setTimeout(resolve, timeoutMs);
    }),
  ]);
  clearTimeout(timeoutTimer);
}

/**
 * True if ANY of the given collections (or any collection in `states` when
 * `collections` is omitted) had its loop terminated by a graceful WebSocket
 * disconnect before ever completing a real pull+push attempt — see
 * `ReplicationStateStub.hadUnconfirmedFirstCycleDisconnect`.
 *
 * Used by callers that need to distinguish "confirmed nothing was pending"
 * from "never actually checked" — a bounded, short-lived session (e.g. the
 * mobile pre-wipe flush) whose scoped `isDrainedFor`/`getPendingCountFor`
 * both read "drained"/"0" for a collection can still be looking at a
 * collection that was never given a chance to discover its locally-dirty
 * docs, rather than one that was confirmed to have none.
 */
export function hasUnconfirmedFirstCycleDisconnect(
  states: NamedReplicationStates,
  collections?: readonly string[],
): boolean {
  const keys = collections ?? Array.from(states.keys());
  for (const key of keys) {
    if (states.get(key)?.hadUnconfirmedFirstCycleDisconnect?.()) {
      return true;
    }
  }
  return false;
}

/**
 * @process waitForNextSyncAttempt
 *
 * Steps:
 * 1. Register a "next cycle" waiter on every collection in `states` (or the
 *    given `collections` subset) via `registerNextCycleWaiter()`.
 * 2. Wait for all of them to resolve (each resolves once its collection's
 *    current-or-next loop iteration settles from THIS call's perspective —
 *    unlike `waitForInitialSyncAttempt`, which only ever covers the very
 *    first cycle), or for `timeoutMs` to elapse, whichever comes first.
 *
 * Inputs:
 * - states: The NamedReplicationStates session to wait on.
 * - timeoutMs: Safety ceiling — default 3000ms.
 *
 * Outputs:
 * - Promise<void>: always resolves (never rejects).
 *
 * Failure Handling:
 * - Never throws. Intended specifically to close the race between
 *   `triggerFullSync()` waking a sleeping loop and a caller immediately
 *   checking `isDrainedFor()`/`waitForPendingPushDrain()` synchronously
 *   right after — the woken loop's re-check has not run yet at that point
 *   (it resumes on a LATER tick), so an immediate drain check can observe
 *   stale (pre-wake) push state and miss a write that landed just before
 *   the wake. Call this AFTER `triggerFullSync()` and BEFORE checking
 *   drain, so the drain check reflects the woken loops' actual re-check.
 *
 * Idempotency:
 * - REQUIRED — safe to call multiple times against the same `states`; each
 *   call registers fresh waiters independent of any prior call.
 */
export async function waitForNextSyncAttempt(
  states: NamedReplicationStates,
  timeoutMs = 3_000,
): Promise<void> {
  const nextCyclePromises = Array.from(states.values())
    .map((s) => s.registerNextCycleWaiter?.())
    .filter((p): p is Promise<void> => !!p);
  if (nextCyclePromises.length === 0) return;

  let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    Promise.all(nextCyclePromises),
    new Promise<void>((resolve) => {
      timeoutTimer = setTimeout(resolve, timeoutMs);
    }),
  ]);
  clearTimeout(timeoutTimer);
}

// ---------------------------------------------------------------------------
// cancelAllReplication
// ---------------------------------------------------------------------------
export async function cancelAllReplication(
  states: NamedReplicationStates,
): Promise<void> {
  const teardown = replicationTeardowns.get(states);
  if (teardown?.nestedStates?.length) {
    for (const nested of teardown.nestedStates) {
      await cancelAllReplication(nested);
    }
    replicationTeardowns.delete(states);
    return;
  }
  teardown?.stopSse?.();
  teardown?.removeOnlineListener?.();
  teardown?.stopPolling?.();
  teardown?.stopWatchdog?.();
  // Open the readiness gate first so any deferred collection loop currently
  // parked in `await readinessGate.wait()` unblocks and can observe `cancelled`.
  teardown?.releaseReadinessGate?.();
  // Release the multi-tab leader lock so another tab can acquire it.
  teardown?.releaseLeaderLock?.();
  teardown?.wakeRegistryKeys?.forEach((key) => {
    _wakeRegistry.delete(key);
  });
  for (const state of states.values()) {
    await state.cancel();
  }
  await flushPendingMetaWrites();
  // Drop the first-clean-cycle marks for this instance's collections so a
  // department switch / sync restart requires a fresh pull before
  // waitForCollectionFirstSync resolves true again.
  for (const key of states.keys()) {
    cleanCycleCompleted.delete(key);
  }
  replicationTeardowns.delete(states);
}
